import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { okJson } from '../services/httpError';
import VisitDestinationList from './visitors/VisitDestinationList';
import {
  addDestination,
  applyDestinationsChange,
  destinationsOfVisit,
  destinationsPayload,
  primaryDestinationOf,
} from '../utils/visitDestinations';
import type { VisitDestinationChoice } from '../utils/visitDestinations';
import { createVehiclesForPerson, describeVehicleFailures } from '../services/personVehicles';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Select, { SingleValue } from 'react-select';
import {
  X,
  Search,
  Plus,
  User,
  UserCheck,
  Users,
  Clock,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Upload,
  Video,
  VideoOff,
  Camera,
  RotateCcw,
  RefreshCw,
  Loader2,
  Loader,
  UserCircle,
  CreditCard,
  Info,
  Car,
  Footprints,
  FileText,
  AlertTriangle,
  Globe,
  ShieldAlert,
  MessageSquare,
  MessageCircle,
  Edit2,
  Briefcase,
  Calendar,
  LogIn,
  Phone,
  ExternalLink,
} from 'lucide-react';
import { api, condoApi, fetchWithAuth } from '../services/api';
import { submitPreRegistration } from '../services/preRegistrationApi';
import { usePersonFieldConfig } from '../hooks/usePersonFieldConfig';
import { resolveFieldConfigType, configurableFieldsFor, EXISTING_PERSON_HARDCODED_FIELDS } from '../utils/fieldConfigUtils';
import { missingIdentityRequirements } from '../utils/identityRequirements';
import { offersOnFootChoice, arrivalModeFor, visitVehicleOptions } from '../utils/visitArrivalMode';
import { VEHICLE_PHOTO_ASPECT, VEHICLE_PHOTO_ASPECT_CLASS } from '../utils/vehiclePhoto';
import { VisitorEntranceType } from '../types/customer/base';
import { DeviceInfo } from '../services/deviceCommunication';
import { DeviceIntegration, DeviceOperations, CommunicationMode } from '../integrations/devices';
import toast from 'react-hot-toast';
import { normalizeCPF, isCPFFormat as checkCPFFormat, isValidCPF } from '../utils/cpfUtils';
import { formatCPF, maskCPF, canRegisterWithoutCPF } from '../utils/cpfFormatter';
import { effectivePersonRoles } from './PersonDetailModal/personModalRules';
import PhotoUploadModal, { PhotoResult } from './PhotoUploadModal';
import PhotoWebcamModal from './PhotoWebcamModal';
import { sanitizePersonName, validateFullName } from '../utils/fullName';
import PhotoIPCameraModal from './PhotoIPCameraModal';
import PhotoUploadArea from './PhotoUploadArea';
import { AllPhotosEditModal } from './AllPhotosEditModal';
import { MjpegLivePreview } from './MjpegLivePreview';
import { logger } from '../services/logger';
import { useOCR } from '../hooks/useOCR';
import { useStreamKeepalive } from '../hooks/useStreamKeepalive';
import { getAbsoluteImageUrl } from '../utils/imageUrl';
import { resolvePhotoUrl, resolveDeviceSyncTimes } from '../domain/personSyncData';
import { DateInput } from './ui/DateInput';
import { LiberacoesBanner } from './visitors/LiberacoesBanner';
import { PersonNotesAlert } from './notes/PersonNotesAlert';
import { summarizeLiberacoes, LiberacaoSummary } from '../utils/liberacoes';
import { maskPhoneInput } from '../utils/phone';
import {
  isCustomPeriodValidationRequired,
  buildPersonIdentityUpdate,
  buildPreRegistrationRolePayload,
  resolveUnitAfterResidentSelection,
  shouldAutoSelectSingleOption,
  collectResidentUnits,
  type PersonIdentityUpdate,
} from './VisitorRegistrationModal.helpers';
import { ScheduleInput, ScheduleRestrictions } from './ScheduleInput';
import VisitGradeInput, { VisitDaySchedule } from './VisitGradeInput';
import { useKeycloak } from '../contexts/KeycloakContext';
import { useDebounce } from '../hooks/useDebounce';
import { cn } from '../utils/cn';
import { LicensePlate } from './ui/LicensePlate';
import { FormLoadingOverlay } from './common/FormLoadingOverlay';
import { usePersonNotes } from '../hooks/usePersonNotes';
import { usePersonDetailContext } from '../contexts/PersonDetailContext';
import { useCustomerNotes } from '../hooks/useCustomerNotes';
import { useForceUppercase } from '../hooks/useForceUppercase';
import { startAdaptiveSnapshotPolling, AdaptiveSnapshotPollerHandle } from '../utils/adaptiveSnapshotPoller';
import { ReentryConfirmationModal } from './OperationalView/components/ReentryConfirmationModal';
import { LucideIcon } from 'lucide-react';
import { appendIdentityPhoto } from '../services/identityPhotoUpload';
import { groupDeviceFailures, formatSyncSuccess } from '../utils/deviceSyncError';
import { deviceFailureToast } from './DeviceFailureToast';
import { useSyncReporter } from '../hooks/useSyncReporter';
import { CnhValidadeHint } from './registration/CnhValidadeHint';
import { isCnhValidadeField } from '../utils/cnhValidade';
import { formatMissingRequirements } from '../utils/identityRequirements';
import { syncDeviceAndReport } from '../utils/syncDeviceAndReport';
import { maskSearchValue } from './searchInputMask';
import { foreignerPayload, isCpfRequired, identityComplete, normalizePassport, countryOptions, formatForeignPhone } from '../utils/foreigner';
import {
  User as _IconUser, Car as _IconCar, Truck as _IconTruck, Package as _IconPackage,
  Bike as _IconBike, Bus as _IconBus, PersonStanding as _IconPersonStanding,
  ShoppingBag as _IconShoppingBag, Wrench as _IconWrench, Shield as _IconShield,
  Zap as _IconZap, Forklift as _IconForklift, HelpingHand as _IconHelpingHand, DoorOpen as _IconDoorOpen,
} from 'lucide-react';

const ENTRANCE_ICON_MAP_MODAL: Record<string, LucideIcon> = {
  User: _IconUser, Car: _IconCar, Truck: _IconTruck, Package: _IconPackage,
  Bike: _IconBike, Bus: _IconBus, PersonStanding: _IconPersonStanding,
  ShoppingBag: _IconShoppingBag, Wrench: _IconWrench, Shield: _IconShield,
  Zap: _IconZap, Forklift: _IconForklift, HelpingHand: _IconHelpingHand, DoorOpen: _IconDoorOpen,
};

interface Person {
  id: string;
  first_name: string;
  last_name: string;
  cpf?: string;
  phone?: string;
  profile_picture_url?: string;
  identity_photo_url?: string;
  photos?: Array<{
    id: string;
    photo_url: string;
    photo_type: string;
  }>;
}

interface VisitorSearchResult {
  id: string;
  first_name: string;
  last_name: string;
  cpf?: string;
  phone?: string;
  profile_picture_url?: string;
  identity_photo_url?: string;
}

interface PreviousVisitInfo {
  authorizedByName: string;
  authorizedByResidentId: string;
  unit: string;
  block: string;
  unitId?: string; // UUID of unit (condo) or department (empresa)
  vehiclePlate?: string;
  purpose?: string;
  companyName?: string;
  contractorId?: string;
  lastVisitDate: string; // ISO8601
  lastExitDate?: string; // ISO8601
  roleType?: 'visitor' | 'service_provider';
  serviceType?: string;
  requiresIntercom?: boolean;
  // Validity period from the previous role — restored by handleRepeatVisit so repeating a
  // visit doesn't silently drop a previously-configured "Sempre Habilitado"/"Período
  // Personalizado" window back to the no-expiration default.
  validUntil?: string;
  alwaysEnabled?: boolean;
  customPeriodAccess?: boolean;
  indefinite?: boolean;
}

// Some historical role_metadata records stored "Name - Unit - Street (Dep.)" as
// authorized_by_name instead of just the name — keep only the name portion.
function cleanAuthorizedByName(name?: string): string {
  return (name || '').split(' - ')[0].trim();
}

function mapRoleToVisitInfo(role: any): PreviousVisitInfo {
  const metadata = role.role_metadata || {};
  return {
    authorizedByName: cleanAuthorizedByName(metadata.authorized_by_name),
    authorizedByResidentId: metadata.authorized_by_resident_id || '',
    unit: metadata.unit || '',
    block: metadata.block || '',
    unitId: metadata.unit_id || '',
    vehiclePlate: metadata.vehicle_plate,
    purpose: metadata.purpose,
    companyName: metadata.company_name,
    contractorId: metadata.contractor_id,
    lastVisitDate: metadata.last_entry_at || metadata.first_entry_at || role.created_at,
    lastExitDate: metadata.last_exit_at,
    roleType: role.role_type,
    serviceType: metadata.service_type,
    // Same "default true unless explicitly false" convention already used elsewhere (Visitors.tsx)
    requiresIntercom: metadata.requires_intercom !== false,
    validUntil: role.valid_until,
    alwaysEnabled: metadata.always_enabled === true,
    customPeriodAccess: metadata.custom_period_access === true,
    indefinite: metadata.indefinite === true,
  };
}

// Custom scrollbar styles for form fields area — colors flip per theme so the scrollbar
// doesn't render dark-gray on the light-theme modal (and vice versa).
const getScrollbarStyles = (darkMode: boolean) => darkMode ? `
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: #111827;
    border-radius: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #374151;
    border-radius: 4px;
    border: 1px solid #1f2937;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #4b5563;
  }

  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #374151 #111827;
  }
` : `
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
    border: 1px solid #e5e7eb;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }

  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #d1d5db #f3f4f6;
  }
`;

interface VisitorRegistrationModalProps {
  open: boolean;
  onClose: () => void;
  customerId: string;
  onSuccess?: () => void;
  darkMode?: boolean;
  selectedCustomer?: { type?: string; name?: string; condoType?: string; mobileAppEnabled?: boolean } | null;
  initialStep?: RegistrationStep;
  prefilledPerson?: {
    id: string;
    first_name: string;
    last_name: string;
    cpf?: string;
    phone?: string;
    identity_photo_url?: string;
    profile_picture_url?: string;
    document_front_url?: string;
    document_back_url?: string;
    vehicles?: any[];
  };
  initialFormData?: {
    cpf?: string;
    firstName?: string;
    lastName?: string;
  };
  initialBlockId?: string;
  initialUnitId?: string;
  initialDepartmentId?: string;
  compactMode?: boolean; // Default true: single name field, Nome+CPF on same line
  prefilledPersonId?: string; // Just the person ID - modal will fetch full data
  embedded?: boolean; // When true, renders without modal overlay for main frame display
  preRegistrationComment?: string; // Comment from pre-registration to display as banner
  personType?: string; // 'visitor' (default) or 'service_provider'
  preRegistrationToken?: string; // QR token — when set, "Pré-Registro" button is enabled
  onPreRegistrationCreated?: () => void; // When provided, always shows the "Pré-Registro" button
  loadingPreRegistrationToken?: boolean; // Shows loading state on pre-reg button while QR is being fetched
  initialResidentId?: string; // Pre-selects a resident — for "Autorizar Visita" from morador row
}

type RegistrationStep = 'search' | 'form';

// WhatsApp helper functions
const applyTemplate = (template: string, vars: Record<string, string>): string => {
  let result = template;
  for (const [key, value] of Object.entries(vars)) {
    if (!value) {
      // Remove entire line containing this empty placeholder
      result = result.replace(new RegExp(`[^\n]*\\{\\{${key}\\}\\}[^\n]*\n?`, 'g'), '');
    } else {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    }
  }
  // Collapse more than 2 consecutive newlines
  return result.replace(/\n{3,}/g, '\n\n').trim();
};

const formatPhoneForWhatsApp = (phone: string): string => {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Add Brazil country code if not present
  if (digits.length === 11) {
    return `55${digits}`; // 55 + DDD + number
  } else if (digits.length === 10) {
    return `55${digits}`; // 55 + DDD + number (landline)
  }

  return digits; // Already has country code or invalid
};

const createWhatsAppMessage = (
  responsiblePersonName: string,
  visitorName: string,
  visitorType: 'visitor' | 'service_provider',
  destination: string,
  condoName: string,
  template?: string
): string => {
  const now = new Date();
  const dateTime = now.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo'
  });

  const typeLabel = visitorType === 'visitor' ? 'visitante' : 'prestador de serviço';

  if (template) {
    return applyTemplate(template, {
      nome_responsavel: responsiblePersonName,
      nome_visitante: visitorName,
      tipo: typeLabel,
      condominio: condoName,
      destino: destination,
      data_hora: dateTime,
    });
  }

  return `Olá ${responsiblePersonName}!

Um ${typeLabel} deseja entrar no condomínio ${condoName}.

Nome: ${visitorName}
Data/Hora: ${dateTime}${destination ? `\nDestino: ${destination}` : ''}

Você autoriza a entrada?
Responda SIM para permitir ou NÃO para negar.`;
};

const openWhatsApp = (phone: string, message: string): void => {
  const formattedPhone = formatPhoneForWhatsApp(phone);
  const encodedMessage = encodeURIComponent(message);
  const url = `https://wa.me/${formattedPhone}?text=${encodedMessage}`;

  // Open in new tab
  window.open(url, '_blank');
};

const VisitorRegistrationModal: React.FC<VisitorRegistrationModalProps> = ({
  open,
  onClose,
  customerId,
  onSuccess,
  darkMode = false,
  selectedCustomer = null,
  initialStep = 'search',
  prefilledPerson,
  initialFormData,
  initialBlockId,
  initialUnitId,
  initialDepartmentId,
  compactMode = true,
  prefilledPersonId,
  embedded = false,
  preRegistrationComment,
  personType,
  preRegistrationToken,
  onPreRegistrationCreated,
  loadingPreRegistrationToken = false,
  initialResidentId,
}) => {
  const [currentStep, setCurrentStep] = useState<RegistrationStep>(initialStep);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<VisitorSearchResult[]>([]);
  const [selectedResultIndex, setSelectedResultIndex] = useState<number>(0); // Track highlighted result
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [existingPerson, setExistingPerson] = useState<Person | null>(null);
  const [accessType, setAccessType] = useState<'single_visit' | 'temporary'>('single_visit');
  const [isAlwaysEnabled, setIsAlwaysEnabled] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [alwaysEnabledUntil, setAlwaysEnabledUntil] = useState('');
  const [isIndefinite, setIsIndefinite] = useState(false);
  const [customPeriodUntil, setCustomPeriodUntil] = useState('');
  const [vehicleList, setVehicleList] = useState<Array<{ plate: string; model: string; color: string }>>([]);
  const [personVehicles, setPersonVehicles] = useState<Array<{ id: string; plate: string; brand?: string; model?: string; color?: string; year?: number; is_active: boolean }>>([]);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [newVehiclePlate, setNewVehiclePlate] = useState('');
  const [newVehicleModel, setNewVehicleModel] = useState('');
  const [newVehicleColor, setNewVehicleColor] = useState('');
  // Keep vehiclePlate for backwards compat (role_metadata)
  const vehiclePlate = vehicleList[0]?.plate || '';
  // "Entrando a pé" — offered when there is a vehicle to opt OUT of, and only while the visitor is
  // still arriving. See utils/visitArrivalMode.
  //
  // Plates this visit picked up that are NOT on the person's record — carried across by "Repetir
  // Visita", or typed at the desk. Held separately from the SELECTION (`vehicleList`) because the
  // two were once the same list, so clearing the selection destroyed the plate itself: choosing
  // "Entrando a pé" left the operator with "Nenhum veículo adicionado" and no way back to the car.
  const [offeredPlates, setOfferedPlates] = useState<Array<{ plate: string; model?: string; color?: string }>>([]);
  useEffect(() => {
    if (vehicleList.length === 0) return;
    setOfferedPlates(prev => {
      const key = (p: string) => p.replace(/\s+/g, '').toUpperCase();
      const known = new Set([
        ...prev.map(p => key(p.plate)),
        ...personVehicles.map(p => key(p.plate || '')),
      ]);
      const additions = vehicleList.filter(v => v.plate && !known.has(key(v.plate)));
      return additions.length === 0 ? prev : [...prev, ...additions];
    });
  }, [vehicleList, personVehicles]);
  useEffect(() => {
    setOfferedPlates([]);
  }, [existingPerson?.id, open]);

  const visitVehicles = visitVehicleOptions({
    personVehicles,
    offeredPlates,
    selected: vehicleList,
  });
  const showOnFootChoice = offersOnFootChoice({
    registeredVehicleCount: personVehicles.length,
    attachedVehicleCount: offeredPlates.length,
    roles: (existingPerson as any)?.roles,
  });
  const arrivedOnFoot = vehicleList.length === 0;
  const [companyName, setCompanyName] = useState<string>('');
  const [contractorId, setContractorId] = useState<string>('');
  const [contractors, setContractors] = useState<{ id: string; name: string }[]>([]);
  const [showNewContractor, setShowNewContractor] = useState(false);
  const [newContractorName, setNewContractorName] = useState('');
  const [creatingContractor, setCreatingContractor] = useState(false);
  const [contractorSearch, setContractorSearch] = useState('');
  const [contractorDropdownOpen, setContractorDropdownOpen] = useState(false);
  // Rendered via portal (position: fixed, viewport coordinates) so it can never be clipped by
  // the modal's own overflow-y-auto body — the field sits low in the form, right above the
  // footer, so a normal absolute-positioned dropdown gets cut off with "Criar nova empresa"
  // invisible below the fold. `openUpward` flips it above the field when there's no room below.
  const [contractorDropdownPos, setContractorDropdownPos] = useState<{ top?: number; bottom?: number; left: number; width: number; openUpward: boolean } | null>(null);
  // Gates the "Criar nova empresa" option below — only relevant when the current user is a
  // porteiro; other roles can always create contractors (mirrors backend checkPorteiroCanManageContractors).
  const [porteiroCanManageContractors, setPorteiroCanManageContractors] = useState(false);
  const contractorComboRef = useRef<HTMLDivElement>(null);
  const contractorDropdownRef = useRef<HTMLDivElement>(null);
  const [schedule, setSchedule] = useState<ScheduleRestrictions>({ type: 'always' });
  const [providerSubType, setProviderSubType] = useState<'single_visit' | 'custom' | 'always'>('single_visit');
  const [requiresIntercom, setRequiresIntercom] = useState<boolean>(true); // Default to true
  const [autoGiveEntrada, setAutoGiveEntrada] = useState<boolean>(true); // Auto-give entrada on registration (default enabled)
  const [fullName, setFullName] = useState<string>(''); // For compact mode: single name field

  // Entrance types
  const [visitorEntranceTypes, setVisitorEntranceTypes] = useState<VisitorEntranceType[]>([]);
  const [selectedEntranceTypeId, setSelectedEntranceTypeId] = useState<string | null>(null);

  // Photo requirements from customer settings
  const [photoRequirements, setPhotoRequirements] = useState<{ identity_photo?: boolean; document_front?: boolean; document_back?: boolean }>({});
  const [allPersonTypeFieldConfigs, setAllPersonTypeFieldConfigs] = useState<Record<string, any>>({});
  const [whatsappTemplate, setWhatsappTemplate] = useState<string | undefined>(undefined);
  const [visitorCustomPeriodEnabled, setVisitorCustomPeriodEnabled] = useState(true);
  const [visitorCustomPeriodMaxDays, setVisitorCustomPeriodMaxDays] = useState(365);
  const [spCustomPeriodEnabled, setSPCustomPeriodEnabled] = useState(false);
  const [spCustomPeriodMaxDays, setSPCustomPeriodMaxDays] = useState(365);
  const [useCustomPeriodAccess, setUseCustomPeriodAccess] = useState(false);
  // Per-weekday "grade de horário" for the custom period (visitor + prestador). Empty = date-only
  // period (liberado o período inteiro). Sent as role_metadata.visit_schedule.
  const [visitSchedule, setVisitSchedule] = useState<VisitDaySchedule[]>([]);

  // Post-registration: entrance type picker (shown after visitor is created)
  const [entranceTypePickerData, setEntranceTypePickerData] = useState<{
    personName: string;
    unitLabel: string;
    personId: string;
    roleId: string;
  } | null>(null);
  const PICKER_COUNTDOWN_SECONDS = 15;
  const [pickerCountdown, setPickerCountdown] = useState(PICKER_COUNTDOWN_SECONDS);

  const [residents, setResidents] = useState<any[]>([]);
  const [selectedResponsible, setSelectedResponsible] = useState<{ value: string; label: string } | null>(null);
  // Additive: when the customer has the mobile app enabled and a responsible resident/staff
  // is selected, staff can defer "Liberar Acesso" to that person's approval via their app
  // instead of granting access immediately — does not change the default (unchecked) behavior.
  const [waitForAppApproval, setWaitForAppApproval] = useState(false);

  // Block/Unit filtering for resident selection
  const [blocks, setBlocks] = useState<any[]>([]);
  // A condomínio with a single quadra/bloco makes that filter a formality — one option, one
  // click, every time. Latched with a ref because the select is clearable: without it the effect
  // would re-select the instant the operator emptied the field. See shouldAutoSelectSingleOption.
  const autoSelectedSingleBlockRef = useRef(false);
  const [units, setUnits] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [isLoadingBlocks, setIsLoadingBlocks] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState<{ value: string; label: string } | null>(null);

  // Pick the only quadra/bloco for the operator (see the ref above for why it fires once).
  useEffect(() => {
    const options = blocks.map((b: any) => ({ value: b.id, label: b.name }));
    if (shouldAutoSelectSingleOption(options, selectedBlock, autoSelectedSingleBlockRef.current)) {
      autoSelectedSingleBlockRef.current = true;
      setSelectedBlock(options[0]);
    }
  }, [blocks, selectedBlock]);

  // A different condomínio means a different set of quadras, so the latch resets with it.
  useEffect(() => {
    autoSelectedSingleBlockRef.current = false;
  }, [selectedCustomer?.name]);
  const [selectedUnit, setSelectedUnit] = useState<{ value: string; label: string; blockId?: string; blockName?: string } | null>(null);
  const [personAvailableUnits, setPersonAvailableUnits] = useState<{ value: string; label: string; blockId?: string; blockName?: string }[]>([]);
  const [streets, setStreets] = useState<Array<{id: string; name: string}>>([]);
  const [selectedStreet, setSelectedStreet] = useState<{ value: string; label: string } | null>(null);
  const [streetNumberSearch, setStreetNumberSearch] = useState('');
  const [allCustomerUnits, setAllCustomerUnits] = useState<any[]>([]);

  // Visita a múltiplos destinos — one registration covering several unidades or departamentos.
  //
  // The visit's destinations live in TWO controls: the unidade/departamento picker this form has
  // always had (the PRIMARY destination, which carries the liberação and the responsible person)
  // and these extras. utils/visitDestinations owns the rules that keep them in agreement; the
  // backend applies the matching ones and drops everything past the first when the customer has
  // the feature off.
  const [multiDestinationEnabled, setMultiDestinationEnabled] = useState(false);
  const [extraDestinations, setExtraDestinations] = useState<VisitDestinationChoice[]>([]);
  const [pickingDestination, setPickingDestination] = useState(false);

  // Resolves a unit id against whatever units are loaded, so a resident's unit assignments can be
  // turned into labelled options with their block. allCustomerUnits is the complete set when it
  // has loaded; `units` is the block/street-filtered subset.
  const lookupLoadedUnit = useCallback(
    (unitId: string) => {
      const source = allCustomerUnits.length > 0 ? allCustomerUnits : units;
      return source.find((u: any) => u.id === unitId);
    },
    [allCustomerUnits, units]
  );

  const isEmpresaCustomer = selectedCustomer?.type === 'empresa' || selectedCustomer?.type === 'fabrica';

  // Where the visit is going, as one list: the picker's own destination first, then the extras.
  const primaryDestination = useMemo(
    () => primaryDestinationOf(selectedUnit, selectedCustomer?.type),
    [selectedUnit, selectedCustomer?.type]
  );
  const visitDestinations = useMemo(
    () => destinationsOfVisit(primaryDestination, extraDestinations),
    [primaryDestination, extraDestinations]
  );

  // An edit on the list can touch the picker: removing the destination it is showing promotes the
  // next one, because both describe the same visit.
  const handleDestinationsChange = useCallback((next: VisitDestinationChoice[]) => {
    const applied = applyDestinationsChange(next, primaryDestinationOf(selectedUnit, selectedCustomer?.type));
    setExtraDestinations(applied.extras);
    if (!applied.primary) {
      setSelectedUnit(null);
      return;
    }
    if (applied.primary.id !== selectedUnit?.value) {
      const promoted = lookupLoadedUnit(applied.primary.id);
      setSelectedUnit({
        value: applied.primary.id,
        label: applied.primary.label,
        blockId: promoted?.block_id,
        blockName: promoted?.block?.name,
      });
      if (promoted?.block_id) {
        setSelectedBlock({ value: promoted.block_id, label: promoted.block?.name || '' });
      }
      // The responsible person answered for the destination that just went away.
      setSelectedResponsible(null);
    }
  }, [selectedUnit, selectedCustomer?.type, lookupLoadedUnit]);

  // Everywhere the visit could also go, minus where it already goes. A condomínio needs every
  // unidade here, not the block-filtered subset the primary picker works from: "and also 303" is
  // usually another block.
  const destinationOptions = useMemo(() => {
    if (isEmpresaCustomer) {
      return departments.map((dept: any) => ({ value: dept.id, label: dept.name, kind: 'department' as const }));
    }
    const source = allCustomerUnits.length > 0 ? allCustomerUnits : units;
    return source.map((unit: any) => {
      const parts: string[] = [];
      if (unit.block?.name) parts.push(unit.block.name);
      parts.push(unit.number || unit.name);
      return { value: unit.id, label: parts.join(' – '), kind: 'unit' as const };
    });
  }, [isEmpresaCustomer, departments, allCustomerUnits, units]);

  const availableDestinationOptions = useMemo(
    () => destinationOptions.filter(option => !visitDestinations.some(chosen => chosen.id === option.value)),
    [destinationOptions, visitDestinations]
  );

  // Opening the picker is what loads the full unidade list, rather than every modal open paying
  // for a request only this feature needs.
  const handlePickDestination = useCallback(() => {
    setPickingDestination(true);
    if (isEmpresaCustomer || allCustomerUnits.length > 0 || !customerId) return;
    fetchWithAuth(`/api/v1/customers/${customerId}/units`)
      .then(r => okJson<any>(r, 'Erro ao carregar as unidades'))
      .then((data: any[]) => setAllCustomerUnits(data || []))
      .catch((err: any) => toast.error(err?.message || 'Erro ao carregar as unidades'));
  }, [isEmpresaCustomer, allCustomerUnits.length, customerId]);

  // Server-side search for residents
  const [residentSearchTerm, setResidentSearchTerm] = useState('');
  const debouncedResidentSearch = useDebounce(residentSearchTerm, 300);
  const [isLoadingResidents, setIsLoadingResidents] = useState(false);

  // ESC key double-press state
  const [escPressCount, setEscPressCount] = useState(0);
  const escTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSyncRef = useRef<((willCheckIn: boolean) => void) | null>(null);

  // Photo capture state - individual modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showWebcamModal, setShowWebcamModal] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<PhotoResult | null>(null);

  // Inline IP camera state (visitante category for monitoring - auto-starts when modal opens)
  const [ipCameras, setIPCameras] = useState<any[]>([]);
  const [selectedIPCamera, setSelectedIPCamera] = useState<string>('');
  const [isLoadingCameras, setIsLoadingCameras] = useState(false);
  const [livePreviewUrl, setLivePreviewUrl] = useState<string | null>(null);
  const [rtspPreviewUrl, setRtspPreviewUrl] = useState<string | null>(null);
  const [httpSnapshotUrl, setHttpSnapshotUrl] = useState<string | null>(null); // Electron snapshot polling (Digest auth)
  const [isCapturingFrame, setIsCapturingFrame] = useState(false);
  const [isStreamReconnecting, setIsStreamReconnecting] = useState(false);
  const [reconnectStatus, setReconnectStatus] = useState<string | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  const httpPollerRef = useRef<AdaptiveSnapshotPollerHandle | null>(null);
  const rtspIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCapturingRef = useRef(false);
  const currentStreamIdRef = useRef<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  // Ref mirrors for values used inside useCallback without causing dep churn
  const selectedIPCameraRef = useRef<string>('');
  const isStartingStreamRef = useRef(false);
  const lastFrameTimeRef = useRef<number>(0);
  const isInElectron = typeof window !== 'undefined' && window.electronAPI !== undefined;
  const firstResultRef = useRef<HTMLButtonElement | null>(null);

  // CPF auto-search state
  const [cpfCheckStatus, setCpfCheckStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid' | 'taken'>('idle');
  const [cpfExistingPerson, setCpfExistingPerson] = useState<{
    id: string;
    name: string;
    photo_url?: string;
  } | null>(null);

  // Notes state
  const [showNotesBanner, setShowNotesBanner] = useState(true);
  const [newVisitorNote, setNewVisitorNote] = useState('');
  const [concludingNoteId, setConcludingNoteId] = useState<string | null>(null);
  const [addingResidentPhone, setAddingResidentPhone] = useState(false);
  const [residentPhoneInput, setResidentPhoneInput] = useState('');
  const [savingResidentPhone, setSavingResidentPhone] = useState(false);

  // Previous visit state for repeat visit feature
  const [previousVisit, setPreviousVisit] = useState<PreviousVisitInfo | null>(null);
  const [recentVisits, setRecentVisits] = useState<PreviousVisitInfo[]>([]);
  const [showPreviousVisitCard, setShowPreviousVisitCard] = useState(false);
  const [isRepeatingVisit, setIsRepeatingVisit] = useState(false);
  const [isRepeatingVisitLoading, setIsRepeatingVisitLoading] = useState(false);

  // Birth date and under 18 state
  const [birthDate, setBirthDate] = useState<string>('');
  const [isUnder18, setIsUnder18] = useState<boolean>(false);

  // Reset to initial step when modal opens or initialStep changes
  useEffect(() => {
    if (open) {
      setCurrentStep(initialStep);
    }
  }, [open, initialStep]);

  // Close contractor combobox on outside click — the dropdown itself renders in a portal
  // (outside contractorComboRef's DOM subtree), so it needs its own ref checked too.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideField = contractorComboRef.current?.contains(target);
      const insideDropdown = contractorDropdownRef.current?.contains(target);
      if (!insideField && !insideDropdown) {
        setContractorDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close the (fixed-position) dropdown on scroll/resize — its coordinates are computed once
  // at open time, so they'd otherwise go stale as soon as the modal scrolls.
  useEffect(() => {
    if (!contractorDropdownOpen) return;
    const close = () => setContractorDropdownOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [contractorDropdownOpen]);

  // Opens the contractor dropdown, computing its position from the field's current viewport
  // rect — flips to open upward when there isn't enough room below (field sits right above
  // the modal footer). Rendered via portal + position:fixed so it can't be clipped by the
  // modal's overflow-y-auto body.
  const CONTRACTOR_DROPDOWN_EST_HEIGHT = 260; // list (max-h-52) + "Criar nova empresa" row
  const openContractorDropdown = () => {
    const rect = contractorComboRef.current?.getBoundingClientRect();
    if (rect) {
      const openUpward = window.innerHeight - rect.bottom < CONTRACTOR_DROPDOWN_EST_HEIGHT;
      setContractorDropdownPos({
        left: rect.left,
        width: rect.width,
        top: openUpward ? undefined : rect.bottom + 4,
        bottom: openUpward ? window.innerHeight - rect.top + 4 : undefined,
        openUpward,
      });
    }
    setContractorDropdownOpen(true);
  };

  // Auto-focus first required empty field when modal opens
  useEffect(() => {
    if (!open) return;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      // Find all required input fields
      const requiredFields = document.querySelectorAll('input[required], input[aria-required="true"]');

      // Find first empty required field
      for (const field of Array.from(requiredFields)) {
        const input = field as HTMLInputElement;
        if (!input.value || input.value.trim() === '') {
          input.focus();
          break;
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [open, currentStep]);

  // ID Photo capture state - IP Camera modal (captura_facial category)
  const [showIDPhotoModal, setShowIDPhotoModal] = useState(false);

  // Document photo capture state - IP Camera modals
  const [showDocumentFrontModal, setShowDocumentFrontModal] = useState(false);
  const [showDocumentBackModal, setShowDocumentBackModal] = useState(false);
  const [documentFrontPhoto, setDocumentFrontPhoto] = useState<PhotoResult | null>(null);
  const [documentBackPhoto, setDocumentBackPhoto] = useState<PhotoResult | null>(null);

  // Document photo capture state - Upload and Webcam modals
  const [showDocFrontUploadModal, setShowDocFrontUploadModal] = useState(false);
  const [showDocFrontWebcamModal, setShowDocFrontWebcamModal] = useState(false);
  const [showDocBackUploadModal, setShowDocBackUploadModal] = useState(false);
  const [showDocBackWebcamModal, setShowDocBackWebcamModal] = useState(false);

  // Vehicle photo capture state
  const [showVehiclePhotoModal, setShowVehiclePhotoModal] = useState(false);
  const [showVehicleUploadModal, setShowVehicleUploadModal] = useState(false);
  const [showVehicleWebcamModal, setShowVehicleWebcamModal] = useState(false);
  const [vehiclePhoto, setVehiclePhoto] = useState<PhotoResult | null>(null);

  // All-photos-at-once editor
  const [showAllPhotosModal, setShowAllPhotosModal] = useState(false);

  // Photo zoom modal state
  const [zoomPhotoUrl, setZoomPhotoUrl] = useState<string | null>(null);
  const [zoomPhotoTitle, setZoomPhotoTitle] = useState<string>('');
  const [zoomPhotoActions, setZoomPhotoActions] = useState<{ onReplace: () => void; onRemove: () => void } | null>(null);

  const forceUpper = useForceUppercase();

  // OCR state
  const { state: ocrState, isAvailable: ocrAvailable, processImage: processOCRImage, reset: resetOCR } = useOCR();
  const [ocrMismatches, setOcrMismatches] = useState<{
    first_name?: string;
    last_name?: string;
    cpf?: string;
  }>({});
  const [ocrProcessing, setOcrProcessing] = useState(false);

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const [isActuallySubmitting, setIsActuallySubmitting] = useState(false);
  const [disclaimerAcknowledged, setDisclaimerAcknowledged] = useState(false);

  // Re-entry confirmation state
  const [showReentryConfirmation, setShowReentryConfirmation] = useState(false);
  const [reentryVariant, setReentryVariant] = useState<'inside' | 'awaiting_entry' | 'active_role'>('inside');

  /**
   * What the re-entry dialog should SAY about the role we found.
   *
   * Five different lookups used to each hardcode 'awaiting_entry', so the caption described which
   * query had matched rather than what the person's access actually is. A prestador holding an
   * open liberação was announced as "AGUARDANDO ENTRADA" while their own profile said "Sem visita".
   *
   * visitor_state alone cannot answer it: the backend parks a liberação at awaiting_entry BETWEEN
   * visits, so that value means "cleared and not inside", not "standing at the gate".
   *
   * Never returns 'inside' — these call sites all take the remove-the-old-role path on confirm,
   * and 'inside' selects the check-out path instead. Being inside is handled by its own branch.
   */
  /**
   * A standing clearance — "pode vir até 22/10" — is NOT a visit in progress.
   *
   * Registering an arrival for someone on a unit's whitelist used to pop "Registrar uma nova
   * visita? A visita atual será finalizada automaticamente", and confirming it called
   * deletePersonRole — which DELETED the clearance. The porteiro doing their normal job destroyed
   * the morador's whitelist. There is nothing to resolve here: the person simply has permission,
   * and the new visit is recorded alongside it.
   *
   * A pré-cadastro (visita única) is excluded on purpose: that IS the planned visit being
   * registered, so replacing it is the right behaviour.
   */
  const isOpenClearance = (role: any): boolean => {
    const meta = role?.role_metadata || role?.metadata || {};
    if (meta.visitor_state === 'inside') return false; // genuinely mid-visit

    const server = role?.liberacao;
    if (server) {
      return (
        (server.status === 'liberado' || server.status === 'aguardando') &&
        ['periodo', 'grade', 'sempre'].includes(server.kind)
      );
    }
    // Fallback for a backend that predates models/liberacao.go.
    return (
      meta.always_enabled === true ||
      meta.custom_period_access === true ||
      (Array.isArray(meta.visit_schedule) && meta.visit_schedule.length > 0)
    );
  };

  const reentryCaptionForRole = (role: any): 'awaiting_entry' | 'active_role' => {
    // PHASE 2: the server says whether this is a clearance (models/liberacao.go). Only a pending
    // one-off visit is genuinely "aguardando entrada"; anything else open is a liberação.
    const server = role?.liberacao;
    if (server) {
      return server.status === 'aguardando' && server.kind === 'visita_unica'
        ? 'awaiting_entry'
        : 'active_role';
    }
    const meta = role?.role_metadata || role?.metadata || {};
    const isLiberacao =
      meta.always_enabled === true ||
      meta.custom_period_access === true ||
      meta.visitor_state === 'pre_registered' ||
      (Array.isArray(meta.visit_schedule) && meta.visit_schedule.length > 0);
    if (isLiberacao) return 'active_role';
    // A one-off visit that has already ended once isn't waiting at the gate either.
    if (meta.last_exit_at) return 'active_role';
    return meta.visitor_state === 'awaiting_entry' ? 'awaiting_entry' : 'active_role';
  };
  const [isCheckingVisitorState, setIsCheckingVisitorState] = useState(false);
  const [activeVisitInfo, setActiveVisitInfo] = useState<{
    personId: string;
    roleId: string;
    personName: string;
    unit: string;
    entryTime: string;
    person: any; // Store the full person object for form population
    visitor: any; // Store the visitor object for photo URL
  } | null>(null);
  const [pendingReentry, setPendingReentry] = useState(false);
  const [isPreRegSubmitting, setIsPreRegSubmitting] = useState(false);

  const { register, handleSubmit, reset, watch, setValue, getValues, trigger, unregister, formState: { errors, isSubmitting, isValid } } = useForm<any>({
    mode: 'onChange', // Enable validation on change
    defaultValues: {
      role_type: ''
    }
  });

  // Which person type is being registered RIGHT NOW. The operator switches between visitante and
  // prestador with a toggle inside this form, and each type has its own field configuration — so
  // the config has to follow the toggle, not the prop. Pinning it to `personType` (which neither
  // Operacional call site passes) meant a prestador registration rendered the visitor config and
  // every custom field configured for prestador was invisible.
  const roleType = watch('role_type');
  const effectiveFieldConfigType = resolveFieldConfigType(roleType, personType);

  // `allPersonTypeFieldConfigs` already holds every type's config, so the toggle switches instantly
  // off that map; the hook is the fallback for the first paint and for when the bulk load failed.
  const fetchedFieldConfig = usePersonFieldConfig(customerId, effectiveFieldConfigType);
  const fieldConfig = allPersonTypeFieldConfigs[effectiveFieldConfigType] ?? fetchedFieldConfig;

  // When CPF is disabled in field config, unregister it so it doesn't block form submission
  const cpfEnabled = fieldConfig?.standard_fields?.cpf?.enabled ?? true;
  useEffect(() => {
    if (!cpfEnabled) {
      unregister('cpf');
    }
  }, [cpfEnabled, unregister]);

  // Fills the configurable fields from the record when registering access for someone already
  // known. They are saved with "blank means not filled in", so an un-prefilled field loses
  // nothing — but showing an empty RG for a visitor who has one reads as if we lost it, and the
  // operator retypes data we already hold. Only ever fills what is still empty, so it cannot
  // overwrite something the operator is in the middle of typing.
  useEffect(() => {
    if (!existingPerson) return;
    const person = existingPerson as any;

    if (person.rg && !getValues('rg')) {
      setValue('rg', person.rg);
    }

    if (person.birth_date && !birthDate) {
      const [yyyy, mm, dd] = String(person.birth_date).slice(0, 10).split('-');
      if (yyyy && mm && dd) {
        setBirthDate(`${dd}/${mm}/${yyyy}`);
      }
    }

    if (person.is_minor) {
      setIsUnder18(true);
    }

    if (person.extra_data && typeof person.extra_data === 'object') {
      Object.entries(person.extra_data).forEach(([key, value]) => {
        if (value !== null && value !== undefined && !getValues(`extra_data.${key}`)) {
          setValue(`extra_data.${key}`, value);
        }
      });
    }
  }, [existingPerson, birthDate, getValues, setValue]);

  // Who may waive the CPF requirement — see canRegisterWithoutCPF, shared with
  // PersonalInfoSection and ResidentModal. This copy was admins-only, so the porteiro who meets
  // the visitor with no document was the one person who could not register them.
  // Both role stores: a síndico's role is in user_customer_assignments, not in the token.
  const { isSuperAdmin, roles, user, getRolesForCustomer } = useKeycloak();
  const isAdmin = canRegisterWithoutCPF(
    effectivePersonRoles(customerId ? getRolesForCustomer(customerId) : [], roles),
    isSuperAdmin,
  );
  const operatorName = user?.name || user?.username || 'Operador';
  // Progress presentation: the sync card in the toast corner (zoomable to the modal).
  const makeSyncReporter = useSyncReporter();

  // Person detail context for opening person modal
  const { openPersonDetail } = usePersonDetailContext();
  const navigate = useNavigate();

  // Fetch notes when CPF matches existing person OR when adding access to existing person
  const { notes: existingPersonNotes, loading: notesLoading } = usePersonNotes(
    (cpfCheckStatus === 'taken' || existingPerson) ? customerId : undefined,
    cpfCheckStatus === 'taken' ? cpfExistingPerson?.id : existingPerson?.id
  );

  // Get concludeNote function for closing notes
  const { concludeNote } = useCustomerNotes(
    customerId,
    existingPerson ? { person_id: existingPerson.id } : undefined
  );

  // Liberações: access a resident granted this person in advance from the app, per unit, each with
  // its own period and its own intercom rule. None of it used to reach the portaria, so a visitor
  // the resident had explicitly cleared still got the full stop-and-call treatment.
  const [liberacaoRoles, setLiberacaoRoles] = useState<any[]>([]);
  useEffect(() => {
    if (!existingPerson?.id || !customerId) {
      setLiberacaoRoles([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await condoApi.getPersonRoles(customerId, existingPerson.id);
        if (cancelled) return;
        setLiberacaoRoles(response?.data?.data || []);
      } catch (error) {
        // Informational only — a failed lookup must never block registering the visit.
        if (!cancelled) setLiberacaoRoles([]);
        logger.warn('visitor.modal', 'Failed to load liberações', { personId: existingPerson?.id });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingPerson?.id, customerId]);

  // Summarised here rather than at fetch time so the rua can be filled in from the units this
  // screen loads for its own RUA/NÚMERO filters. GET /people/:id/roles returns role_metadata
  // exactly as stored — none of the unit enrichment the people list performs — so a liberação
  // created from the resident's app carries only a frozen "18 - 5", and the banner showed that
  // while the filters two inches below read "Rua Cazuza, 42". The units arrive after the roles,
  // which is why this re-derives instead of being computed once.
  const liberacoes: LiberacaoSummary[] = useMemo(
    () => summarizeLiberacoes(liberacaoRoles, new Date(), { resolveUnit: lookupLoadedUnit }),
    [liberacaoRoles, lookupLoadedUnit],
  );

  // "Cadastrar Acesso" edits the name of someone already registered, so the single "Nome Completo"
  // field has to start from what is stored. Keyed on the person's id, never on the object, so a
  // re-render does not throw away what the operator is typing.
  useEffect(() => {
    if (!existingPerson) return;
    setFullName(`${existingPerson.first_name || ''} ${existingPerson.last_name || ''}`.trim());
    // canSubmit still gates on first_name/last_name, so keep them in step with the name field.
    setValue('first_name', existingPerson.first_name || '', { shouldValidate: false });
    setValue('last_name', existingPerson.last_name || '', { shouldValidate: false });
    // Masked, like the header shows it. Only when the person HAS a CPF — overwriting with '' would
    // wipe a CPF the operator typed into the search box before picking this person by name.
    if (existingPerson.cpf) {
      setValue('cpf', formatCPF(existingPerson.cpf), { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingPerson?.id]);

  // Watch required fields
  const firstName = watch('first_name');
  const lastName = watch('last_name');
  const cpf = watch('cpf');
  const phone = watch('phone');
  // Watched so the Pré-Cadastro gate re-evaluates as the operator types, rather than only on
  // submit — these are configurable fields a condomínio can mark obrigatório.
  const watchedRG = watch('rg');
  const watchedEmail = watch('email');
  const watchedExtraData = watch('extra_data');

  // Sync currentStep with initialStep prop and clear search state when needed
  useEffect(() => {
    if (initialStep === 'form') {
      setCurrentStep('form');
      // Clear search state to ensure search UI doesn't show
      setHasSearched(false);
      setSearchResults([]);
      setSearchInput('');
    } else if (initialStep === 'search') {
      setCurrentStep('search');
    }
  }, [initialStep, open]); // Re-run when initialStep or open changes

  // Reset confirmation modal when main modal opens/closes
  useEffect(() => {
    if (!open) {
      setShowConfirmModal(false);
      setDisclaimerAcknowledged(false);
      setPendingFormData(null);
      // Reset notes state
      setNewVisitorNote('');
      setShowNotesBanner(true);
      // Reset birth date and under 18
      setBirthDate('');
      setIsUnder18(false);
      // Reset ESC counter
      setEscPressCount(0);
      if (escTimeoutRef.current) {
        clearTimeout(escTimeoutRef.current);
        escTimeoutRef.current = null;
      }
    }
  }, [open]);

  // Handle ESC key - requires double-press to close
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();

        if (escPressCount === 0) {
          // First ESC press
          setEscPressCount(1);
          toast('Pressione ESC novamente para fechar', {
            duration: 2000,
          });

          // Reset counter after 3 seconds
          if (escTimeoutRef.current) clearTimeout(escTimeoutRef.current);
          escTimeoutRef.current = setTimeout(() => {
            setEscPressCount(0);
          }, 3000);
        } else {
          // Second ESC press - close modal
          setEscPressCount(0);
          if (escTimeoutRef.current) {
            clearTimeout(escTimeoutRef.current);
            escTimeoutRef.current = null;
          }
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (escTimeoutRef.current) {
        clearTimeout(escTimeoutRef.current);
      }
    };
  }, [open, onClose, escPressCount]);

  // Handle initial form data for new visitors (prefill without locking fields)
  useEffect(() => {
    if (initialFormData && open) {
      console.log(' Pre-filling new visitor form with data:', initialFormData);

      // Set form values without setting existingPerson (keeps fields editable)
      if (initialFormData.cpf) {
        setValue('cpf', initialFormData.cpf);
      }
      if (initialFormData.firstName || initialFormData.lastName) {
        const firstName = initialFormData.firstName || '';
        const lastName = initialFormData.lastName || '';
        setValue('first_name', firstName);
        setValue('last_name', lastName);

        // In compact mode, also set the visible fullName field
        if (compactMode) {
          const fullNameValue = `${firstName} ${lastName}`.trim();
          setFullName(fullNameValue);
          console.log(' Set fullName in compact mode:', fullNameValue);
        }
      }

      // Check for pre-registration photo and phone from window object
      const preRegPhoto = (window as any).__preRegPhoto;
      const preRegPhone = (window as any).__preRegPhone;

      if (preRegPhoto) {
        console.log(' Setting pre-registration photo');
        setCapturedPhoto({
          base64: preRegPhoto,
          blob: null as any,
          width: 0,
          height: 0,
          faceDetected: false,
        });
        // Clean up
        delete (window as any).__preRegPhoto;
      }

      if (preRegPhone) {
        console.log(' Setting pre-registration phone');
        setValue('phone', preRegPhone);
        // Clean up
        delete (window as any).__preRegPhone;
      }

      // Go directly to form step
      setCurrentStep('form');
    }
  }, [initialFormData, open, setValue, compactMode]);

  // Handle prefilled person data when modal opens
  useEffect(() => {
    if (prefilledPerson && open) {
      setIsCheckingVisitorState(true);
      console.log(' Pre-filling visitor modal with person:', prefilledPerson);
      console.log(' Photo URLs from prefilledPerson:', {
        identity_photo_url: prefilledPerson.identity_photo_url,
        profile_picture_url: prefilledPerson.profile_picture_url,
        document_front_url: prefilledPerson.document_front_url,
        document_back_url: prefilledPerson.document_back_url,
        vehicles: prefilledPerson.vehicles
      });
      console.log(' Photo URLs received:', {
        identity_photo_url: prefilledPerson.identity_photo_url,
        profile_picture_url: prefilledPerson.profile_picture_url,
        document_front_url: prefilledPerson.document_front_url,
        document_back_url: prefilledPerson.document_back_url,
        vehicles: prefilledPerson.vehicles
      });

      // CRITICAL: Set existingPerson to indicate we're adding a visit to an existing person, not creating new
      setExistingPerson({
        id: prefilledPerson.id,
        first_name: prefilledPerson.first_name,
        last_name: prefilledPerson.last_name,
        cpf: prefilledPerson.cpf,
        phone: prefilledPerson.phone,
        identity_photo_url: prefilledPerson.identity_photo_url,
        profile_picture_url: prefilledPerson.profile_picture_url,
      } as Person);

      // Set basic form values (these may not be needed since existingPerson is set, but keep for safety)
      setValue('first_name', prefilledPerson.first_name);
      setValue('last_name', prefilledPerson.last_name);
      setValue('cpf', prefilledPerson.cpf || '');
      setValue('phone', prefilledPerson.phone || '');

      // Set photos from prefilledPerson IMMEDIATELY (don't wait for fetch)
      const _prePhoto = resolvePhotoUrl(prefilledPerson);
      if (_prePhoto) {
        const photoUrl = getAbsoluteImageUrl(_prePhoto);
        console.log(' [Prefilled] Setting main photo from prefilledPerson:', photoUrl);
        setCapturedPhoto({
          base64: photoUrl,
          blob: null as any,
          width: 0,
          height: 0,
          faceDetected: false,
        });
      }

      if (prefilledPerson.document_front_url) {
        const frontUrl = getAbsoluteImageUrl(prefilledPerson.document_front_url);
        console.log(' [Prefilled] Setting document FRONT photo:', frontUrl);
        setDocumentFrontPhoto({
          base64: frontUrl,
          blob: null as any,
          width: 0,
          height: 0,
          faceDetected: false,
        });
      } else {
        console.log(' [Prefilled] No document_front_url in prefilledPerson');
      }

      if (prefilledPerson.document_back_url) {
        const backUrl = getAbsoluteImageUrl(prefilledPerson.document_back_url);
        console.log(' [Prefilled] Setting document BACK photo:', backUrl);
        setDocumentBackPhoto({
          base64: backUrl,
          blob: null as any,
          width: 0,
          height: 0,
          faceDetected: false,
        });
      } else {
        console.log(' [Prefilled] No document_back_url in prefilledPerson');
      }

      if (prefilledPerson.vehicles && Array.isArray(prefilledPerson.vehicles) && prefilledPerson.vehicles.length > 0) {
        setPersonVehicles(prefilledPerson.vehicles);
        console.log(' [Prefilled] Vehicles array:', prefilledPerson.vehicles);
        const vehicle = prefilledPerson.vehicles[0];
        const _vPhotoUrl0 = vehicle.vehicle_photo_url || vehicle.photo_url;
        if (_vPhotoUrl0) {
          const vehicleUrl = getAbsoluteImageUrl(_vPhotoUrl0);
          console.log(' [Prefilled] Setting vehicle photo:', vehicleUrl);
          setVehiclePhoto({
            base64: vehicleUrl,
            blob: null as any,
            width: 0,
            height: 0,
            faceDetected: false,
          });
        } else {
          console.log(' [Prefilled] Vehicle exists but no vehicle_photo_url');
        }
      } else {
        console.log(' [Prefilled] No vehicles in prefilledPerson');
      }

      // Go directly to form
      setCurrentStep('form');

      // Fetch full person data to get roles for previous visit check (photos already set from prefilledPerson)
      fetchWithAuth(`/api/v1/customers/${customerId}/people/${prefilledPerson.id}?include_photos=true&include_roles=true`)
        .then(async (response) => {
          if (response.ok) {
            const data = await response.json();
            const person = data.data || data;

            console.log(' [Prefilled] Person data with roles:', {
              id: person.id,
              name: `${person.first_name} ${person.last_name}`,
              hasRoles: !!person.roles,
              rolesCount: person.roles?.length || 0,
              roles: person.roles
            });

            console.log(' [Prefilled] Photo fields from second fetch:', {
              identity_photo_url: person.identity_photo_url,
              profile_picture_url: person.profile_picture_url,
              document_front_url: person.document_front_url,
              document_back_url: person.document_back_url,
              vehicles: person.vehicles,
              ALL_PERSON_KEYS: Object.keys(person)
            });

            // Update existingPerson with full data including roles
            setExistingPerson(person);
            if (person.vehicles && Array.isArray(person.vehicles)) setPersonVehicles(person.vehicles);

            // Load main identity/profile photo from person data
            const _personPhoto = resolvePhotoUrl(person);
            if (_personPhoto) {
              const photoUrl = getAbsoluteImageUrl(_personPhoto);
              console.log(' [Prefilled] Setting main photo from second fetch:', photoUrl);
              setCapturedPhoto({
                base64: photoUrl,
                blob: null as any,
                width: 0,
                height: 0,
                faceDetected: false,
              });
            } else {
              console.log(' [Prefilled] No main photo URL in person data');
            }

            // Load document photos from person data
            if (person.document_front_url) {
              const frontUrl = getAbsoluteImageUrl(person.document_front_url);
              console.log(' [Prefilled] Setting document front:', frontUrl);
              setDocumentFrontPhoto({
                base64: frontUrl,
                blob: null as any,
                width: 0,
                height: 0,
                faceDetected: false,
              });
            } else {
              console.log(' [Prefilled] No document_front_url');
            }

            if (person.document_back_url) {
              const backUrl = getAbsoluteImageUrl(person.document_back_url);
              console.log(' [Prefilled] Setting document back:', backUrl);
              setDocumentBackPhoto({
                base64: backUrl,
                blob: null as any,
                width: 0,
                height: 0,
                faceDetected: false,
              });
            } else {
              console.log(' [Prefilled] No document_back_url');
            }

            // Load vehicle photo from vehicles array
            if (person.vehicles && Array.isArray(person.vehicles) && person.vehicles.length > 0) {
              const vehicle = person.vehicles[0];
              const _vPhotoUrl1 = vehicle.vehicle_photo_url || vehicle.photo_url;
              if (_vPhotoUrl1) {
                const vehicleUrl = getAbsoluteImageUrl(_vPhotoUrl1);
                console.log(' [Prefilled] Setting vehicle photo:', vehicleUrl);
                setVehiclePhoto({
                  base64: vehicleUrl,
                  blob: null as any,
                  width: 0,
                  height: 0,
                  faceDetected: false,
                });
              } else {
                console.log(' [Prefilled] Vehicle has no photo URL');
              }
            } else {
              console.log(' [Prefilled] No vehicles in person data');
            }

            // Check if visitor is awaiting entry — show confirmation before opening form
            const awaitingRole = (person.roles || []).find((role: any) =>
              (role.role_type === 'visitor' || role.role_type === 'service_provider') &&
              role.role_metadata?.visitor_state === 'awaiting_entry'
            );
            if (awaitingRole && !isOpenClearance(awaitingRole)) {
              const meta = awaitingRole.role_metadata || {};
              setActiveVisitInfo({
                personId: person.id,
                roleId: awaitingRole.id,
                personName: `${person.first_name} ${person.last_name}`,
                unit: meta.unit_display || meta.unit || 'Não especificada',
                entryTime: awaitingRole.created_at || awaitingRole.valid_from,
                person,
                visitor: { id: person.id, ...person },
              });
              setReentryVariant(reentryCaptionForRole(awaitingRole));
              setShowReentryConfirmation(true);
              setIsCheckingVisitorState(false);
              return;
            }

            // Catch any remaining active role (e.g. always_enabled in "exited" state)
            // Exclude pre_registered — those are future visits, not active conflicts
            const anyActiveRolePrefilled = (person.roles || []).find((role: any) =>
              (role.role_type === 'visitor' || role.role_type === 'service_provider') && role.is_active === true &&
              role.role_metadata?.visitor_state !== 'pre_registered'
            );
            if (anyActiveRolePrefilled && !isOpenClearance(anyActiveRolePrefilled)) {
              const meta = anyActiveRolePrefilled.role_metadata || {};
              setActiveVisitInfo({
                personId: person.id,
                roleId: anyActiveRolePrefilled.id,
                personName: `${person.first_name} ${person.last_name}`,
                unit: meta.unit_display || meta.unit || 'Não especificada',
                entryTime: meta.last_entry_at || anyActiveRolePrefilled.created_at || '',
                person,
                visitor: { id: person.id, ...person },
              });
              setReentryVariant(reentryCaptionForRole(anyActiveRolePrefilled));
              setShowReentryConfirmation(true);
              setIsCheckingVisitorState(false);
              return;
            }
            setIsCheckingVisitorState(false);

            // Extract previous visit information for repeat visit feature
            console.log(' [Prefilled] Checking for previous visits...');
            if (person.roles && Array.isArray(person.roles) && person.roles.length > 0) {
              console.log(` [Prefilled] Found ${person.roles.length} roles:`, person.roles.map((r: any) => ({
                role_type: r.role_type,
                created_at: r.created_at,
                has_metadata: !!r.role_metadata,
                authorized_by: r.role_metadata?.authorized_by_resident_id
              })));

              // Filter visitor roles with metadata
              const visitorRoles = person.roles.filter(
                (role: any) => (role.role_type === 'visitor' || role.role_type === 'service_provider') && role.role_metadata
              );

              console.log(` [Prefilled] Found ${visitorRoles.length} visitor roles with metadata`);

              if (visitorRoles.length > 0) {
                // Sort by created_at descending to get most recent
                const mostRecentRole = visitorRoles.sort(
                  (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                )[0];

                const metadata = mostRecentRole.role_metadata;
                console.log(' [Prefilled] Most recent role metadata:', metadata);

                // Show previous visit card if we have ANY data to repeat
                if (metadata) {
                  console.log(' [Prefilled] Previous visit found with data!');
                  setPreviousVisit(mapRoleToVisitInfo(mostRecentRole));
                  setRecentVisits(visitorRoles.slice(0, 5).map(mapRoleToVisitInfo));
                  setShowPreviousVisitCard(true);
                } else {
                  console.log(' [Prefilled] Most recent role has no metadata');
                }
              } else {
                console.log(' [Prefilled] No visitor roles with metadata found');
              }
            } else {
              console.log(' [Prefilled] No roles found in person data');
            }
          }
        })
        .catch(console.error);
    }
  }, [prefilledPerson, open, setValue, customerId]);

  // Handle prefilledPersonId - fetch person and set as existing (same as prefilledPerson but fetch first)
  useEffect(() => {
    if (prefilledPersonId && open && customerId) {
      setIsCheckingVisitorState(true);
      console.log(' Fetching person by ID:', prefilledPersonId);

      // Fetch full person data to get document, vehicle photos, AND roles for previous visit check
      fetchWithAuth(`/api/v1/customers/${customerId}/people/${prefilledPersonId}?include_photos=true&include_roles=true`)
        .then(async (response) => {
          if (response.ok) {
            const data = await response.json();
            const person = data.data || data;
            console.log(' Person fetched by ID');
            console.log(' [PrefilledID] Photo fields:', {
              identity_photo_url: person.identity_photo_url,
              profile_picture_url: person.profile_picture_url,
              document_front_url: person.document_front_url,
              document_back_url: person.document_back_url,
              vehicles: person.vehicles
            });

            // Update existingPerson with full data including roles
            setExistingPerson(person);
            if (person.vehicles && Array.isArray(person.vehicles)) setPersonVehicles(person.vehicles);

            // Set basic form values
            setValue('first_name', person.first_name);
            setValue('last_name', person.last_name);
            setValue('cpf', person.cpf || '');
            setValue('phone', person.phone || '');

            // Load main identity/profile photo from person data
            const _idPhoto = resolvePhotoUrl(person);
            if (_idPhoto) {
              const photoUrl = getAbsoluteImageUrl(_idPhoto);
              console.log(' [PrefilledID] Setting main photo:', photoUrl);
              setCapturedPhoto({
                base64: photoUrl,
                blob: null as any,
                width: 0,
                height: 0,
                faceDetected: false,
              });
            }

            // Load document photos
            if (person.document_front_url) {
              const frontUrl = getAbsoluteImageUrl(person.document_front_url);
              console.log(' [PrefilledID] Setting document front:', frontUrl);
              setDocumentFrontPhoto({
                base64: frontUrl,
                blob: null as any,
                width: 0,
                height: 0,
                faceDetected: false,
              });
            }

            if (person.document_back_url) {
              const backUrl = getAbsoluteImageUrl(person.document_back_url);
              console.log(' [PrefilledID] Setting document back:', backUrl);
              setDocumentBackPhoto({
                base64: backUrl,
                blob: null as any,
                width: 0,
                height: 0,
                faceDetected: false,
              });
            }

            // Load vehicle photo
            if (person.vehicles && Array.isArray(person.vehicles) && person.vehicles.length > 0) {
              const vehicle = person.vehicles[0];
              const _vPhotoUrl2 = vehicle.vehicle_photo_url || vehicle.photo_url;
              if (_vPhotoUrl2) {
                const vehicleUrl = getAbsoluteImageUrl(_vPhotoUrl2);
                console.log(' [PrefilledID] Setting vehicle photo:', vehicleUrl);
                setVehiclePhoto({
                  base64: vehicleUrl,
                  blob: null as any,
                  width: 0,
                  height: 0,
                  faceDetected: false,
                });
              }
            }

            // Check if visitor is awaiting entry — show confirmation before opening form
            const awaitingRoleById = (person.roles || []).find((role: any) =>
              (role.role_type === 'visitor' || role.role_type === 'service_provider') &&
              role.role_metadata?.visitor_state === 'awaiting_entry'
            );
            if (awaitingRoleById && !isOpenClearance(awaitingRoleById)) {
              const meta = awaitingRoleById.role_metadata || {};
              setActiveVisitInfo({
                personId: person.id,
                roleId: awaitingRoleById.id,
                personName: `${person.first_name} ${person.last_name}`,
                unit: meta.unit_display || meta.unit || 'Não especificada',
                entryTime: awaitingRoleById.created_at || awaitingRoleById.valid_from,
                person,
                visitor: { id: person.id, ...person },
              });
              setReentryVariant(reentryCaptionForRole(awaitingRoleById));
              setShowReentryConfirmation(true);
              setIsCheckingVisitorState(false);
              return;
            }

            // Catch any remaining active role (e.g. always_enabled in "exited" state)
            // Exclude pre_registered — those are future visits, not active conflicts
            const anyActiveRoleById = (person.roles || []).find((role: any) =>
              (role.role_type === 'visitor' || role.role_type === 'service_provider') && role.is_active === true &&
              role.role_metadata?.visitor_state !== 'pre_registered'
            );
            if (anyActiveRoleById && !isOpenClearance(anyActiveRoleById)) {
              const meta = anyActiveRoleById.role_metadata || {};
              setActiveVisitInfo({
                personId: person.id,
                roleId: anyActiveRoleById.id,
                personName: `${person.first_name} ${person.last_name}`,
                unit: meta.unit_display || meta.unit || 'Não especificada',
                entryTime: meta.last_entry_at || anyActiveRoleById.created_at || '',
                person,
                visitor: { id: person.id, ...person },
              });
              setReentryVariant(reentryCaptionForRole(anyActiveRoleById));
              setShowReentryConfirmation(true);
              setIsCheckingVisitorState(false);
              return;
            }

            // Not awaiting — set up person and go to form
            setExistingPerson(person);
            if (person.vehicles && Array.isArray(person.vehicles)) setPersonVehicles(person.vehicles);
            setValue('first_name', person.first_name);
            setValue('last_name', person.last_name);
            setValue('cpf', person.cpf || '');
            setValue('phone', person.phone || '');
            setCurrentStep('form');
            setIsCheckingVisitorState(false);

            // Extract previous visit information
            if (person.roles && Array.isArray(person.roles) && person.roles.length > 0) {
              const visitorRoles = person.roles.filter(
                (role: any) => (role.role_type === 'visitor' || role.role_type === 'service_provider') && role.role_metadata
              );

              if (visitorRoles.length > 0) {
                const mostRecentRole = visitorRoles.sort(
                  (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                )[0];

                const metadata = mostRecentRole.role_metadata;
                if (metadata) {
                  setPreviousVisit(mapRoleToVisitInfo(mostRecentRole));
                  setRecentVisits(visitorRoles.slice(0, 5).map(mapRoleToVisitInfo));
                  setShowPreviousVisitCard(true);
                }
              }
            }
          }
        })
        .catch(console.error);
    }
  }, [prefilledPersonId, open, customerId, setValue]);

  // Admin checkbox to allow registration without CPF
  const [allowWithoutCPF, setAllowWithoutCPF] = useState(false);
  // "É estrangeiro": a foreigner is identified by a passport, not a CPF — which is required
  // by this form, so without the declaration the cadastro simply could not be submitted for
  // one. See utils/foreigner.ts; the rule is shared with the other three cadastros.
  const isForeigner = !!watch('is_foreigner');
  const cpfRequiredHere = isCpfRequired({
    requiredByConfig: fieldConfig?.standard_fields?.cpf?.required ?? true,
    waiverAllowed: isAdmin,
    waiverChecked: allowWithoutCPF,
    isForeigner,
  });

  // Re-sync photo requirements whenever the user switches between Visitante / Prestador.
  // `roleType` / `effectiveFieldConfigType` are declared with the field config above — the standard
  // and custom fields now follow the same toggle this effect already followed.
  useEffect(() => {
    if (!roleType || Object.keys(allPersonTypeFieldConfigs).length === 0) return;
    setPhotoRequirements(allPersonTypeFieldConfigs[effectiveFieldConfigType]?.photo_requirements ?? {});
  }, [roleType, effectiveFieldConfigType, allPersonTypeFieldConfigs]);

  // Check if form can be submitted (all required fields filled)
  // For existing persons without CPF, allow registration (they were already approved without CPF)
  const cpfFieldEnabled = fieldConfig?.standard_fields?.cpf?.enabled ?? true;
  // The SAME rule the CPF field itself uses (cpfRequiredHere), not a second copy of it. The
  // copy that used to live here had never heard of the estrangeiro declaration, so the form
  // hid the CPF, the operator filled in everything that was left, and "Liberar Acesso" stayed
  // grey because of a field they could no longer see. See identityComplete.
  const isCPFValid = identityComplete({
    cpfFieldEnabled,
    cpfRequired: cpfRequiredHere,
    cpf,
    isForeigner,
    passport: watch('passport_number'),
    existingPersonWithoutCpf: !!(existingPerson && !existingPerson.cpf),
  });
  const canSubmit = !!(firstName?.trim() && lastName?.trim() && selectedResponsible && isCPFValid);

  // Pré-Cadastro registers the PERSON and leaves the visit open — so it demands everything the
  // condomínio requires about the person (documents, photos, custom fields) and deliberately
  // nothing about where they are going. It used to ask for a name and nothing else, which let an
  // operator create a record missing the very fields the admin had marked obrigatório.
  const preRegMissing = useMemo(
    () =>
      missingIdentityRequirements({
        config: fieldConfig,
        photoRequirements,
        isExistingPerson: !!existingPerson,
        cpfWaived: !!(isAdmin && allowWithoutCPF) || !cpfFieldEnabled,
        values: {
          firstName,
          lastName,
          cpf,
          rg: watchedRG,
          email: watchedEmail,
          phone,
          birthDate,
          extraData: watchedExtraData,
        },
        photos: {
          identity: capturedPhoto,
          documentFront: documentFrontPhoto,
          documentBack: documentBackPhoto,
        },
      }),
    [
      fieldConfig, photoRequirements, existingPerson, isAdmin, allowWithoutCPF, cpfFieldEnabled,
      firstName, lastName, cpf, watchedRG, watchedEmail, phone, birthDate, watchedExtraData,
      capturedPhoto, documentFrontPhoto, documentBackPhoto,
    ],
  );
  const canPreRegSubmit = preRegMissing.length === 0;

  // Debug: Log form validation state
  useEffect(() => {
    if (currentStep === 'form') {
      console.log(' Form validation state:', {
        firstName: firstName?.trim(),
        lastName: lastName?.trim(),
        selectedResponsible: selectedResponsible,
        isCPFValid,
        canSubmit,
        isSubmitting
      });
    }
  }, [firstName, lastName, selectedResponsible, isCPFValid, canSubmit, isSubmitting, currentStep]);

  // Clear CPF error when allowWithoutCPF changes
  useEffect(() => {
    if (isAdmin && allowWithoutCPF) {
      // Clear CPF error when admin allows registration without CPF
      setValue('cpf', cpf || '', { shouldValidate: false });
    }
  }, [allowWithoutCPF, isAdmin, cpf, setValue]);

  // CPF auto-search - check if CPF is valid and already taken
  useEffect(() => {
    const checkCPF = async () => {
      if (!cpf || existingPerson) {
        // Don't check if no CPF or if we're adding a visit to an existing person
        setCpfCheckStatus('idle');
        setCpfExistingPerson(null);
        return;
      }

      const normalized = cpf.replace(/\D/g, '');

      // Only check when CPF is complete (11 digits)
      if (normalized.length !== 11) {
        setCpfCheckStatus('idle');
        setCpfExistingPerson(null);
        return;
      }

      // Start checking
      setCpfCheckStatus('checking');

      // First, validate CPF format (checksum)
      if (!isValidCPF(normalized)) {
        setCpfCheckStatus('invalid');
        setCpfExistingPerson(null);
        return;
      }

      try {
        // Search for person with this CPF
        const response = await fetchWithAuth(`/api/v1/customers/${customerId}/people?search=${normalized}&limit=1`);

        if (response.ok) {
          const data = await response.json();
          const people = data.data || data || [];

          if (people.length > 0) {
            const person = people[0];
            // CPF is already taken
            setCpfCheckStatus('taken');
            setCpfExistingPerson({
              id: person.id,
              name: `${person.first_name || ''} ${person.last_name || ''}`.trim(),
              photo_url: person.profile_picture_url || person.identity_photo_url
            });
          } else {
            // CPF is valid and not taken
            setCpfCheckStatus('valid');
            setCpfExistingPerson(null);
          }
        } else {
          setCpfCheckStatus('invalid');
          setCpfExistingPerson(null);
        }
      } catch (error) {
        console.error('Error checking CPF:', error);
        setCpfCheckStatus('invalid');
        setCpfExistingPerson(null);
      }
    };

    // Debounce the check by 500ms
    const timer = setTimeout(checkCPF, 500);
    return () => clearTimeout(timer);
  }, [cpf, customerId, existingPerson]);

  // Backend now handles filtering by unit/department, so no client-side filtering needed
  const filteredResidents = residents;

  // Fetch residents/employees for the responsible person dropdown with server-side search
  const fetchResidents = useCallback(async (
    customerType?: string,
    searchTerm?: string,
    unitId?: string,
    departmentId?: string
  ) => {
    if (!customerId) return;

    setIsLoadingResidents(true);
    setResidents([]);

    try {
      // Use staff role for empresas/fábricas, resident for condos
      const roleType = customerType === 'empresa' || customerType === 'fabrica'
        ? 'staff'
        : 'resident';

      // Build query parameters
      const params = new URLSearchParams({
        role_type: roleType,
        include_inactive_roles: 'false',
        page_size: '1000',  // Increased from 50 to handle large units/departments
      });

      // Add search if provided
      if (searchTerm && searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      // Add unit_id filter for condominios
      if (unitId && roleType === 'resident') {
        params.append('unit_id', unitId);
      }

      // Add department_id filter for empresas
      if (departmentId && roleType === 'staff') {
        params.append('department_id', departmentId);
      }

      logger.info('visitor.modal', 'Fetching residents with filters', {
        customerId,
        roleType,
        searchTerm,
        unitId,
        departmentId,
        queryString: params.toString(),
      });

      const response = await fetchWithAuth(
        `/api/v1/customers/${customerId}/people?${params.toString()}`
      );

      if (response.ok) {
        const data = await response.json();
        const people = data.data || [];

        logger.info('visitor.modal', 'Residents fetched successfully', {
          count: people.length,
        });

        setResidents(people);
      } else {
        logger.error('visitor.modal', 'Failed to fetch residents', {
          status: response.status,
          statusText: response.statusText,
        });
        setResidents([]);
      }
    } catch (error) {
      logger.error('visitor.modal', 'Error fetching residents', error as Error);
      setResidents([]);
    } finally {
      setIsLoadingResidents(false);
    }
  }, [customerId]);

  // Fetch blocks for condo customers
  const fetchBlocks = useCallback(async () => {
    console.log(' fetchBlocks called', { customerId });
    if (!customerId) {
      console.log(' No customerId, skipping fetch');
      return;
    }

    setIsLoadingBlocks(true);
    try {
      console.log(' Calling condoApi.getBlocks...');
      const response = await condoApi.getBlocks(customerId);
      const blocksData = response.data?.data || response.data || [];
      console.log(' Blocks fetched successfully', {
        count: blocksData.length,
        blocks: blocksData
      });
      logger.debug('visitor.modal', 'Fetched blocks', { count: blocksData.length });
      setBlocks(blocksData);
      if (blocksData.length === 1 && !selectedBlock) {
        const b = blocksData[0];
        setSelectedBlock({ value: b.id, label: b.name });
      }
    } catch (error) {
      console.error(' Failed to fetch blocks', error);
      logger.error('visitor.modal', 'Failed to fetch blocks', error as Error);
    } finally {
      setIsLoadingBlocks(false);
    }
  }, [customerId]);

  // Fetch units for selected block
  const fetchUnits = useCallback(async (blockId: string) => {
    try {
      const response = await condoApi.getUnits(blockId);
      const unitsData = response.data?.data || response.data || [];
      logger.debug('visitor.modal', 'Fetched units', { blockId, count: unitsData.length });
      setUnits(unitsData);
    } catch (error) {
      logger.error('visitor.modal', 'Failed to fetch units', error as Error);
    }
  }, []);

  // Fetch departments for empresa customers
  const fetchDepartments = useCallback(async () => {
    if (!customerId) return;

    try {
      const response = await condoApi.getDepartments(customerId);
      const raw = response.data?.data ?? response.data;
      const deptData = Array.isArray(raw) ? raw : [];
      logger.debug('visitor.modal', 'Fetched departments', { count: deptData.length });
      setDepartments(deptData);
    } catch (error) {
      logger.error('visitor.modal', 'Failed to fetch departments', error as Error);
    }
  }, [customerId]);

  // Load blocks/departments when modal opens (but not residents - they load on search)
  useEffect(() => {
    console.log(' Load blocks/departments effect triggered', {
      open,
      customerId,
      customerType: selectedCustomer?.type
    });

    if (open && customerId) {
      // Load blocks for condos or departments for empresas
      if (selectedCustomer?.type === 'empresa' || selectedCustomer?.type === 'fabrica') {
        console.log(' Fetching departments for empresa/fabrica');
        fetchDepartments();
      } else {
        console.log(' Fetching blocks for condo');
        fetchBlocks();
      }

      // Load visitor entrance types, photo requirements, and whatsapp template from customer settings
      condoApi.getCustomerSettings(customerId).then((resp) => {
        const types = resp.data?.visitor_entrance_types ?? [];
        setVisitorEntranceTypes(types);
        // Pre-select the first type by default
        setSelectedEntranceTypeId(types.length > 0 ? types[0].id : null);
        // Load photo requirements — store full map so roleType switching works
        const fieldConfigs = resp.data?.condo_data?.person_type_field_configs ?? {};
        setAllPersonTypeFieldConfigs(fieldConfigs);
        const initKey = (personType === 'service_provider') ? 'service_provider' : 'visitor';
        setPhotoRequirements(fieldConfigs[initKey]?.photo_requirements ?? {});
        // Load whatsapp notification template
        setWhatsappTemplate(resp.data?.visitor_settings?.whatsapp_notification_template || undefined);
        // Load visitor custom period flag (default true)
        setVisitorCustomPeriodEnabled(resp.data?.visitor_settings?.visitor_custom_period_enabled ?? true);
        setVisitorCustomPeriodMaxDays(resp.data?.visitor_settings?.visitor_custom_period_max_days ?? 365);
        setPorteiroCanManageContractors(resp.data?.porteiro_can_manage_contractors ?? false);
        // Visita a múltiplos destinos, read from the blob that matches the customer's TYPE —
        // exactly as services.MultiDestinationVisitsEnabled does, so a stale empresa_data on a
        // condomínio cannot put a destination picker on the form.
        const multiDestinationBlob = (selectedCustomer?.type === 'empresa' || selectedCustomer?.type === 'fabrica')
          ? resp.data?.empresa_data
          : resp.data?.condo_data;
        setMultiDestinationEnabled(multiDestinationBlob?.multi_destination_visits_enabled ?? false);
      }).catch(() => {
        setVisitorEntranceTypes([]);
        setPhotoRequirements({});
        setWhatsappTemplate(undefined);
        setVisitorCustomPeriodEnabled(true);
        setVisitorCustomPeriodMaxDays(365);
        setPorteiroCanManageContractors(false);
        // A settings read that failed is not a customer with the feature on: the form falls back
        // to the single destination it has always had.
        setMultiDestinationEnabled(false);
      });

      // Load prestador custom period config
      condoApi.getServiceProviderConfig(customerId).then((resp) => {
        setSPCustomPeriodEnabled(resp.data?.sp_custom_period_enabled ?? false);
        setSPCustomPeriodMaxDays(resp.data?.sp_custom_period_max_days ?? 365);
      }).catch(() => {
        setSPCustomPeriodEnabled(false);
        setSPCustomPeriodMaxDays(365);
      });
    }
  }, [open, customerId, selectedCustomer?.type, fetchBlocks, fetchDepartments]);

  // Fetch streets — always try; if the API returns data it's a casa-type condo
  useEffect(() => {
    if (!open || !customerId) return;
    condoApi.getStreets(customerId).then((res: any) => {
      const data: any[] = res.data?.data || res.data || [];
      setStreets(data.filter((s: any) => s.is_active !== false));
    }).catch(() => setStreets([]));
  }, [open, customerId]);

  // When a street is selected, load all units for the customer to filter by street
  useEffect(() => {
    if (!selectedStreet || !customerId) { setAllCustomerUnits([]); return; }
    fetchWithAuth(`/api/v1/customers/${customerId}/units`)
      .then(r => okJson<any>(r, 'Erro ao carregar as unidades'))
      .then((data: any[]) => setAllCustomerUnits(data || []))
      // An empty picker silently blocks the operator from finishing the form, so say why.
      .catch((err: any) => {
        setAllCustomerUnits([]);
        toast.error(err?.message || 'Erro ao carregar as unidades');
      });
  }, [selectedStreet, customerId]);

  // Re-fetch entrance types every time we reach the form step (catches stale data in Electron embedded mode)
  useEffect(() => {
    if (currentStep === 'form' && customerId) {
      condoApi.getCustomerSettings(customerId).then((resp) => {
        const types = resp.data?.visitor_entrance_types ?? [];
        setVisitorEntranceTypes(types);
        setSelectedEntranceTypeId(types.length > 0 ? types[0].id : null);
      }).catch(() => {
        setVisitorEntranceTypes([]);
      });
    }
  }, [currentStep, customerId]);

  // Countdown for entrance type picker — resets when picker opens, auto-dismisses at 0
  const pickerCountdownAutoConfirmedRef = useRef(false);
  useEffect(() => {
    if (!entranceTypePickerData) {
      setPickerCountdown(PICKER_COUNTDOWN_SECONDS);
      pickerCountdownAutoConfirmedRef.current = false;
      return;
    }
    if (pickerCountdown <= 0) {
      if (!pickerCountdownAutoConfirmedRef.current) {
        pickerCountdownAutoConfirmedRef.current = true;
        // Timed out: no check-in, sync all devices, visitor stays in awaiting_entry
        setEntranceTypePickerData(null);
        pendingSyncRef.current?.(false);
        pendingSyncRef.current = null;
        onSuccess?.();
        onClose();
      }
      return;
    }
    const id = setTimeout(() => setPickerCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entranceTypePickerData, pickerCountdown]);

  // Numpad / number key shortcut for entrance type picker
  useEffect(() => {
    if (!entranceTypePickerData) return;
    const handleKeyDown = async (e: KeyboardEvent) => {
      const num = parseInt(e.key, 10);
      if (isNaN(num) || num < 1 || num > visitorEntranceTypes.length) return;
      const et = visitorEntranceTypes[num - 1];
      if (!et) return;
      e.preventDefault();
      const { personId, roleId } = entranceTypePickerData;
      setEntranceTypePickerData(null);
      if (et.action === 'auto') {
        try {
          await condoApi.manualCheckIn(customerId, personId, roleId);
          toast.success('Entrada registrada!');
        } catch {
          toast.error('Erro ao registrar entrada');
        }
        pendingSyncRef.current?.(true);
      } else {
        pendingSyncRef.current?.(false);
      }
      pendingSyncRef.current = null;
      onSuccess?.();
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entranceTypePickerData, visitorEntranceTypes, customerId]);

  // Server-side search for residents - fetch when search term or unit changes
  useEffect(() => {
    if (!open || !customerId) return;

    // Fetch if there's a search term OR a unit selected
    const shouldFetch = debouncedResidentSearch.trim() || selectedUnit?.value;

    if (shouldFetch) {
      logger.info('visitor.modal', 'Triggering resident fetch', {
        hasSearch: !!debouncedResidentSearch.trim(),
        hasUnit: !!selectedUnit?.value,
        unitId: selectedUnit?.value,
        customerType: selectedCustomer?.type
      });

      fetchResidents(
        selectedCustomer?.type,
        debouncedResidentSearch,
        selectedUnit?.value,  // Used for unit_id when customer is condominio
        selectedUnit?.value   // Used for department_id when customer is empresa
      );
    } else {
      // Clear results when no search term and no unit selected
      logger.info('visitor.modal', 'Clearing residents - no search or unit selected');
      setResidents([]);
    }
    // Note: fetchResidents is NOT in deps array - we call it explicitly with tracked params
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId, selectedCustomer?.type, debouncedResidentSearch, selectedUnit?.value]);

  // Debug: Log residents state changes
  useEffect(() => {
    logger.info('visitor.modal', 'Residents state updated', {
      count: residents.length,
      firstResident: residents[0] ? `${residents[0].first_name} ${residents[0].last_name}` : null
    });
  }, [residents]);

  // Load units only when a block is selected (lazy loading)
  useEffect(() => {
    if (selectedBlock) {
      // Load units for specific block only when selected
      fetchUnits(selectedBlock.value);
    } else {
      // Clear units when no block is selected
      setUnits([]);
    }
  }, [selectedBlock, fetchUnits]);

  // Initialize selected block and unit from props (when coming from unit detail page)
  useEffect(() => {
    if (!open || !initialBlockId || !initialUnitId) return;
    if (blocks.length === 0) return;

    // Set selected block if not already set
    if (!selectedBlock) {
      const block = blocks.find(b => b.id === initialBlockId);
      if (block) {
        setSelectedBlock({ value: block.id, label: block.name });
        // Trigger unit fetch for this block (units are only loaded on demand)
        if (units.length === 0) {
          fetchUnits(initialBlockId);
        }
      }
      return; // wait for units to load
    }

    // Set selected unit once units are loaded
    if (!selectedUnit && units.length > 0) {
      const unit = units.find(u => u.id === initialUnitId);
      if (unit) {
        const labelParts: string[] = [];
        if (unit.block?.name) labelParts.push(unit.block.name);
        if (unit.street?.name) {
          labelParts.push(unit.street_number ? `${unit.street.name}, ${unit.street_number}` : unit.street.name);
        }
        labelParts.push(unit.number || unit.name);
        setSelectedUnit({
          value: unit.id,
          label: labelParts.join(' – '),
          blockId: unit.block_id,
          blockName: unit.block?.name
        });
      }
    }
  }, [open, initialBlockId, initialUnitId, blocks, units, selectedBlock, selectedUnit, fetchUnits]);

  // Auto-select department when initialDepartmentId is provided (empresa customers)
  useEffect(() => {
    if (!open || !initialDepartmentId || selectedUnit) return;
    if (departments.length === 0) return;
    const dept = departments.find(d => d.id === initialDepartmentId);
    if (dept) {
      setSelectedUnit({ value: dept.id, label: dept.name });
    }
  }, [open, initialDepartmentId, departments, selectedUnit]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      // CRITICAL: Reset focus to prevent keyboard trap on Windows
      // Blur any active element before modal unmounts
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      // Small delay to ensure focus is properly released before cleanup
      setTimeout(() => {
        // Reset focus to body to restore keyboard functionality
        if (document.body) {
          document.body.focus();
        }
      }, 50);

      setCurrentStep('search');
      setSearchInput('');
      setSearchResults([]);
      setHasSearched(false);
      setExistingPerson(null);
      setFullName('');
      setCpfCheckStatus('idle');
      setCpfExistingPerson(null);
      setAccessType('single_visit');
      setIsAlwaysEnabled(false);
      setDisclaimerAccepted(false);
      setAlwaysEnabledUntil('');
      setIsIndefinite(false);
      setUseCustomPeriodAccess(false);
      setCustomPeriodUntil('');
      setVehicleList([]);
      setPersonVehicles([]);
      setShowVehicleForm(false);
      setNewVehiclePlate('');
      setNewVehicleModel('');
      setNewVehicleColor('');
      setCompanyName('');
      setContractorId('');
      setShowNewContractor(false);
      setNewContractorName('');
      setSchedule({ type: 'always' });
      setProviderSubType('single_visit');
      setRequiresIntercom(true);
      setCapturedPhoto(null);
      setDocumentFrontPhoto(null);
      setDocumentBackPhoto(null);
      setVehiclePhoto(null);
      setSelectedIPCamera('');
      setIPCameras([]);
      setOcrMismatches({});
      setOcrProcessing(false);
      setSelectedBlock(null);
      setSelectedUnit(null);
      setSelectedResponsible(null);
      setWaitForAppApproval(false);
      setPersonAvailableUnits([]);
      setSelectedStreet(null);
      setStreetNumberSearch('');
      setAllCustomerUnits([]);
      // The next visitor is not going where the last one was: a leftover extra destination would
      // quietly put them on another unidade's list.
      setExtraDestinations([]);
      setPickingDestination(false);
      setPhotoRequirements({});
      setResidentSearchTerm('');
      setResidents([]);
      setPreviousVisit(null);
      setRecentVisits([]);
      setShowPreviousVisitCard(false);
      setIsRepeatingVisit(false);
      resetOCR();
      reset({ role_type: '' });
    }
  }, [open, reset, resetOCR]);

  // Fetch contractors when modal opens (for service provider company selector)
  useEffect(() => {
    if (open && customerId) {
      condoApi.listContractors(customerId)
        .then(r => setContractors(r.data?.data ?? []))
        .catch(() => {});
    }
  }, [open, customerId]);

  // Trigger validation when modal opens to enable submit button if defaults are valid
  useEffect(() => {
    if (open && currentStep === 'form') {
      // Small delay to ensure form is rendered
      setTimeout(() => {
        trigger();
      }, 100);
    }
  }, [open, currentStep, trigger]);

  // ============================================
  // Inline IP Camera Functions for visitor photo
  // ============================================

  // Keep selectedIPCameraRef in sync without triggering dep chains
  useEffect(() => {
    selectedIPCameraRef.current = selectedIPCamera;
  }, [selectedIPCamera]);

  // Load IP cameras for inline preview
  // NOTE: uses selectedIPCameraRef (not state) so changing selectedIPCamera
  // doesn't recreate this function and cause a double-fetch loop.
  const loadIPCameras = useCallback(async () => {
    if (!customerId) return;

    setIsLoadingCameras(true);
    try {
      const response = await fetchWithAuth(
        `/api/v1/registration-cameras?customer_id=${customerId}&category=visitante`
      );

      if (response.ok) {
        const data = await response.json();
        const cameras = data.cameras || data.data || [];
        setIPCameras(cameras);

        // Auto-select: prefer saved camera from localStorage, fallback to first
        if (cameras.length > 0 && !selectedIPCameraRef.current) {
          const savedCamId = customerId
            ? localStorage.getItem(`panther_monitor_cam_${customerId}`)
            : null;
          const savedExists = savedCamId && cameras.some((c: any) => String(c.id) === savedCamId);
          setSelectedIPCamera(savedExists ? savedCamId : cameras[0].id);
        }
      }
    } catch (error) {
      logger.error('visitor.modal', 'Failed to load IP cameras', error as Error);
      setIPCameras([]);
    } finally {
      setIsLoadingCameras(false);
    }
  }, [customerId]); // no selectedIPCamera dep → won't recreate on select change

  // Build snapshot URL for HTTP cameras
  const buildMjpegUrl = useCallback((camera: any): string => {
    const scheme = camera.port === 443 ? 'https' : 'http';
    let auth = '';
    if (camera.username) {
      const user = encodeURIComponent(camera.username);
      const pass = camera.password ? encodeURIComponent(camera.password) : '';
      auth = pass ? `${user}:${pass}@` : `${user}@`;
    }
    const snapshotPath = camera.snapshot_path || '';
    const mjpegPath = snapshotPath.includes('/picture')
      ? snapshotPath.replace('/picture', '/httppreview')
      : '/ISAPI/Streaming/channels/101/httppreview';
    const portStr = (scheme === 'http' && camera.port !== 80) || (scheme === 'https' && camera.port !== 443)
      ? `:${camera.port}`
      : '';
    return `${scheme}://${auth}${camera.ip_address}${portStr}${mjpegPath}`;
  }, []);

  // Stop live preview (clear intervals and cleanup RTSP)
  const stopLivePreview = useCallback(async () => {
    // Stop RTSP live stream
    if (currentStreamIdRef.current && isInElectron) {
      console.log('[VisitorModal] Stopping stream:', currentStreamIdRef.current);
      await window.electronAPI?.stopIPCameraStream?.(currentStreamIdRef.current);
      currentStreamIdRef.current = null;
    }

    // Clear HTTP refresh interval
    if (httpPollerRef.current) {
      httpPollerRef.current.stop();
      httpPollerRef.current = null;
    }

    // Clear RTSP capture interval (legacy)
    if (rtspIntervalRef.current) {
      clearInterval(rtspIntervalRef.current);
      rtspIntervalRef.current = null;
    }

    // Reset state
    setLivePreviewUrl(null);
    setRtspPreviewUrl(null);
    setHttpSnapshotUrl(null);
    setIsCapturingFrame(false);
    setIsStreaming(false);
    setIsStreamReconnecting(false);
    lastFrameTimeRef.current = 0;
    isCapturingRef.current = false;
  }, [isInElectron]);

  // Snapshot polling fallback for HTTP cameras (Electron only).
  // Used when ffmpeg MJPEG stream fails (e.g. Hikvision Digest auth rejection).
  // Adaptive interval (150ms floor, widens to 3s on slow/failed fetches), matching
  // iOS/Android's polling loop instead of a fixed 2s tick.
  const startSnapshotPolling = useCallback((camera: any) => {
    const config = {
      protocol: 'http' as const,
      ipAddress: camera.ip_address,
      port: camera.port,
      username: camera.username,
      password: camera.password,
      snapshotPath: camera.snapshot_path || '/ISAPI/Streaming/channels/101/picture',
    };
    const doCapture = async (): Promise<boolean> => {
      if (isCapturingRef.current) return false;
      isCapturingRef.current = true;
      try {
        const result = await window.electronAPI?.captureIPCameraSnapshot?.(config);
        if (result?.success && result.data) {
          setHttpSnapshotUrl(result.data);
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        isCapturingRef.current = false;
      }
    };
    httpPollerRef.current?.stop();
    httpPollerRef.current = startAdaptiveSnapshotPolling(doCapture);
    console.log('[VisitorModal] Snapshot polling fallback started');
  }, []);

  // Start HTTP live preview for a camera.
  // In Electron: snapshot polling via Electron IPC (handles Digest auth).
  // In browser: MJPEG URL via img.src (Basic auth only).
  const startHttpPreview = useCallback((camera: any) => {
    if (isInElectron) {
      startSnapshotPolling(camera);
    } else {
      setLivePreviewUrl(buildMjpegUrl(camera));
    }
  }, [isInElectron, buildMjpegUrl, startSnapshotPolling]);

  // Start RTSP live streaming for a camera (requires Electron)
  const startRtspStream = useCallback(async (camera: any) => {
    if (!isInElectron || camera.protocol !== 'rtsp') return;

    // Guard against concurrent starts (e.g. effect firing twice during camera load)
    if (isStartingStreamRef.current) {
      console.log('[VisitorModal] startRtspStream: already starting, skipping duplicate call');
      return;
    }
    isStartingStreamRef.current = true;

    try {
      // Stop any existing stream first
      await stopLivePreview();

      const streamId = `visitor-stream-${camera.id}-${Date.now()}`;
      currentStreamIdRef.current = streamId;
      lastFrameTimeRef.current = 0;

      setIsStreaming(true);
      setIsStreamReconnecting(false);
      setIsCapturingFrame(false);

      // Start continuous ffmpeg stream (ONE process outputting frames continuously)
      // Using 'low' quality for monitoring to save bandwidth/performance
      const result = await window.electronAPI?.startIPCameraStream?.({
        streamId,
        ipAddress: camera.ip_address.replace(/^(https?:\/\/|rtsp:\/\/)/i, ''),
        port: camera.port,
        username: camera.username,
        password: camera.password,
        rtspPath: camera.rtsp_path?.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F]/g, '').trim().replace(/\?\?+/g, '?'),
        fps: 15,
        quality: 'low', // Low quality for monitoring preview (saves bandwidth)
      });

      if (!result || !result.success) {
        console.error('[VisitorModal] Failed to start stream:', result?.error);
        setIsStreaming(false);
        currentStreamIdRef.current = null;
      }
    } catch (err) {
      console.error('[VisitorModal] Error starting stream:', err);
      setIsStreaming(false);
      currentStreamIdRef.current = null;
    } finally {
      isStartingStreamRef.current = false;
    }
  }, [isInElectron, stopLivePreview]);

  // Keep ipCameras accessible in callbacks without causing dep churn
  const ipCamerasRef = useRef<any[]>([]);
  useEffect(() => {
    ipCamerasRef.current = ipCameras;
  }, [ipCameras]);

  // Start camera preview when source is IP camera.
  // Uses ipCamerasRef so changing the camera list doesn't recreate this
  // function and doesn't trigger the start-preview effect a second time.
  const startCameraPreview = useCallback((cameraId: string) => {
    const camera = ipCamerasRef.current.find((c: any) => c.id === cameraId);
    if (!camera) return;

    console.log('[VisitorModal] Starting camera preview:', {
      id: camera.id,
      protocol: camera.protocol,
      isInElectron,
      willUseStreaming: camera.protocol === 'rtsp' && isInElectron
    });

    // For RTSP: startRtspStream already awaits stopLivePreview internally.
    // For HTTP: stop first (async, but HTTP snapshots are stateless so ordering is fine).
    if (camera.protocol === 'http' || camera.protocol === 'mjpeg') {
      console.log('[VisitorModal] Using HTTP snapshots');
      stopLivePreview().then(() => startHttpPreview(camera));
    } else if (camera.protocol === 'rtsp' && isInElectron) {
      console.log('[VisitorModal] Using RTSP streaming');
      startRtspStream(camera);
    }
  }, [stopLivePreview, startHttpPreview, startRtspStream, isInElectron]);
  // NOTE: ipCameras intentionally omitted — accessed via ipCamerasRef above

  // Effect: Load cameras when going to form step
  useEffect(() => {
    if (open && customerId && currentStep === 'form') {
      loadIPCameras();
    }
  }, [open, customerId, currentStep, loadIPCameras]);

  // Effect: Start camera preview when cameras are loaded.
  // Deps: only data values (selectedIPCamera, ipCameras.length) — NOT startCameraPreview.
  // Including startCameraPreview would cause a second fire when cameras load because
  // ipCameras changing recreates startCameraPreview in the same render cycle.
  useEffect(() => {
    if (selectedIPCamera && ipCameras.length > 0 && open && currentStep === 'form') {
      startCameraPreview(selectedIPCamera);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIPCamera, ipCameras.length, open, currentStep]);

  // Cleanup on unmount or modal close
  useEffect(() => {
    if (!open) {
      stopLivePreview();
    }
    return () => {
      stopLivePreview();
    };
  }, [open, stopLivePreview]);

  // Listen for incoming video frames from continuous RTSP stream
  useEffect(() => {
    if (!isInElectron || !window.electronAPI?.onIPCameraFrame || !window.electronAPI?.onIPCameraStreamEnded) {
      return;
    }

    // Listen for video frames
    const removeFrameListener = window.electronAPI.onIPCameraFrame((data: any) => {
      // Only process frames for our current stream
      if (data.streamId === currentStreamIdRef.current) {
        setRtspPreviewUrl(data.frame);
        setIsCapturingFrame(false);
        lastFrameTimeRef.current = Date.now();
        // Clear reconnecting banner as soon as a frame arrives
        setIsStreamReconnecting(false);
      }
      // Silently ignore frames from old/stopped streams
    });

    // Listen for stream ended events (only sent when truly done — not during reconnect)
    const removeStreamEndedListener = window.electronAPI.onIPCameraStreamEnded((data: any) => {
      if (data.streamId === currentStreamIdRef.current) {
        setIsStreaming(false);
        setIsStreamReconnecting(false);
        setReconnectStatus(null);
        setStreamError(null);
        currentStreamIdRef.current = null;
      }
    });

    // Listen for reconnection attempts
    const removeReconnectingListener = window.electronAPI.onIPCameraStreamReconnecting?.((data: any) => {
      if (data.streamId === currentStreamIdRef.current) {
        setIsStreamReconnecting(true);
        setReconnectStatus(`Reconectando... (tentativa ${data.attempt}/${data.maxAttempts})`);
        setStreamError(null); // Clear previous errors
      }
    });

    // Listen for successful reconnection
    const removeReconnectedListener = window.electronAPI.onIPCameraStreamReconnected?.((data: any) => {
      if (data.streamId === currentStreamIdRef.current) {
        setIsStreamReconnecting(false);
        setReconnectStatus(null);
        setStreamError(null);
        // Show brief success toast
        toast.success('Câmera reconectada!', { duration: 2000 });
      }
    });

    // Listen for stream errors
    const removeErrorListener = window.electronAPI.onIPCameraStreamError?.((data: any) => {
      if (data.streamId === currentStreamIdRef.current) {
        setStreamError(data.message);
        // Don't stop the stream - let auto-reconnect handle it
      }
    });

    return () => {
      removeFrameListener();
      removeStreamEndedListener();
      removeReconnectingListener?.();
      removeReconnectedListener?.();
      removeErrorListener?.();
    };
  }, [isInElectron]);

  // Detect frame stall: if streaming but no new frame for > 3 s, show reconnecting banner
  useEffect(() => {
    if (!isStreaming) {
      setIsStreamReconnecting(false);
      return;
    }
    const interval = setInterval(() => {
      if (lastFrameTimeRef.current > 0 && Date.now() - lastFrameTimeRef.current > 3000) {
        setIsStreamReconnecting(true);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [isStreaming]);

  // Keepalive: Automatically send keepalive signals every 3s to prevent backend
  // from killing the stream after 12s of inactivity (KEEPALIVE_TIMEOUT in main.ts)
  useStreamKeepalive(currentStreamIdRef.current);

  // Handle photo capture from any modal
  const handlePhotoCapture = (result: PhotoResult) => {
    setCapturedPhoto(result);
    setShowUploadModal(false);
    setShowWebcamModal(false);
    setShowIDPhotoModal(false);
    // Also stop inline camera preview when photo is captured
    stopLivePreview();
  };

  // Handle document front photo capture with OCR processing
  const handleDocumentFrontCapture = (result: PhotoResult) => {
    setDocumentFrontPhoto(result);
    setShowDocumentFrontModal(false);
    setShowDocFrontUploadModal(false);
    setShowDocFrontWebcamModal(false);

    // Trigger OCR processing for document front (only for new visitors in Electron)
    if (result.base64 && ocrAvailable && !existingPerson) {
      processDocumentOCR(result.base64);
    }
  };

  // Handle document back photo capture
  const handleDocumentBackCapture = (result: PhotoResult) => {
    setDocumentBackPhoto(result);
    setShowDocumentBackModal(false);
    setShowDocBackUploadModal(false);
    setShowDocBackWebcamModal(false);
  };

  // Handle vehicle photo capture
  const handleVehiclePhotoCapture = (result: PhotoResult) => {
    setVehiclePhoto(result);
    setShowVehiclePhotoModal(false);
    setShowVehicleUploadModal(false);
    setShowVehicleWebcamModal(false);
  };

  // Process document with OCR and handle auto-fill + mismatch detection
  const processDocumentOCR = useCallback(async (imageBase64: string) => {
    if (!ocrAvailable || existingPerson) {
      // Don't process OCR if not available or if we're editing an existing person
      return;
    }

    setOcrProcessing(true);
    setOcrMismatches({});

    try {
      const result = await processOCRImage(imageBase64);

      if (result) {
        logger.info('visitor.modal', 'OCR result', {
          firstName: result.firstName,
          lastName: result.lastName,
          cpf: result.cpf,
          confidence: result.confidence
        });

        const currentFirstName = watch('first_name')?.trim().toUpperCase();
        const currentLastName = watch('last_name')?.trim().toUpperCase();
        const currentCpf = watch('cpf')?.replace(/\D/g, '');

        const newMismatches: typeof ocrMismatches = {};

        // Handle first_name
        if (result.firstName) {
          const ocrFirstName = result.firstName.toUpperCase();
          if (!currentFirstName) {
            // Empty field - auto-fill
            setValue('first_name', result.firstName);
          } else if (currentFirstName !== ocrFirstName) {
            // Mismatch - show warning
            newMismatches.first_name = result.firstName;
          }
        }

        // Handle last_name
        if (result.lastName) {
          const ocrLastName = result.lastName.toUpperCase();
          if (!currentLastName) {
            // Empty field - auto-fill
            setValue('last_name', result.lastName);
          } else if (currentLastName !== ocrLastName) {
            // Mismatch - show warning
            newMismatches.last_name = result.lastName;
          }
        }

        // Handle CPF
        if (result.cpf) {
          const ocrCpfDigits = result.cpf.replace(/\D/g, '');
          if (!currentCpf) {
            // Empty field - auto-fill with formatted CPF
            setValue('cpf', formatCPF(result.cpf));
          } else if (currentCpf !== ocrCpfDigits) {
            // Mismatch - show warning
            newMismatches.cpf = formatCPF(result.cpf);
          }
        }

        setOcrMismatches(newMismatches);

        if (Object.keys(newMismatches).length > 0) {
          toast('Dados do documento diferem dos campos preenchidos');
        } else if (result.firstName || result.cpf) {
          toast.success('Dados extraidos do documento com sucesso!');
        }
      }
    } catch (error) {
      logger.error('visitor.modal', 'OCR processing failed', error as Error);
      toast.error('Erro ao processar documento');
    } finally {
      setOcrProcessing(false);
    }
  }, [ocrAvailable, existingPerson, processOCRImage, watch, setValue]);

  // Previous Visit Card Component
  const PreviousVisitCard: React.FC<{
    previousVisit: PreviousVisitInfo;
    onRepeatVisit: () => void;
    isLoading?: boolean;
    isLoadingBlocks?: boolean;
  }> = ({ previousVisit, onRepeatVisit, isLoading = false, isLoadingBlocks = false }) => {
    const formatDate = (isoDate: string) => {
      try {
        const date = new Date(isoDate);
        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch {
        return 'Data inválida';
      }
    };

    return (
      <div className={`border rounded-lg p-3 mb-4 ${
        darkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'
      }`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <FileText className={`${darkMode ? 'text-blue-400' : 'text-blue-600'} flex-shrink-0`} size={20} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className={`text-sm font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                  Última Visita
                </h3>
                {previousVisit.roleType && (
                  <span className={`text-xs font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                    previousVisit.roleType === 'service_provider'
                      ? darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'
                      : darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {previousVisit.roleType === 'service_provider' ? 'Prestador' : 'Visitante'}
                  </span>
                )}
              </div>

              <div className={`space-y-0.5 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <div>
                  <span className="font-medium">Autorizado por:</span> {previousVisit.authorizedByName || <span className={darkMode ? 'text-gray-500 italic' : 'text-gray-400 italic'}>Não informado</span>}
                </div>
                <div>
                  <span className="font-medium">Local:</span>{' '}
                  {(selectedCustomer?.type === 'empresa' || selectedCustomer?.type === 'fabrica')
                    ? (previousVisit.unit || <span className={darkMode ? 'text-gray-500 italic' : 'text-gray-400 italic'}>Não informado</span>)
                    : (previousVisit.block && previousVisit.unit
                        ? `Bloco ${previousVisit.block} - ${previousVisit.unit}`
                        : <span className={darkMode ? 'text-gray-500 italic' : 'text-gray-400 italic'}>Não informado</span>)
                  }
                </div>
                <div>
                  <span className="font-medium">Data:</span> {previousVisit.lastVisitDate ? formatDate(previousVisit.lastVisitDate) : <span className={darkMode ? 'text-gray-500 italic' : 'text-gray-400 italic'}>Não informado</span>}
                </div>
                {previousVisit.vehiclePlate && (
                  <div>
                    <span className="font-medium">Veículo:</span> {previousVisit.vehiclePlate}
                  </div>
                )}
                {previousVisit.purpose && (
                  <div>
                    <span className="font-medium">Motivo:</span> {previousVisit.purpose}
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onRepeatVisit}
            disabled={isLoading || isLoadingBlocks}
            className={`px-4 py-2 rounded-md font-medium flex items-center gap-2 transition-colors whitespace-nowrap flex-shrink-0 ${
              isLoading || isLoadingBlocks
                ? 'bg-blue-400 cursor-not-allowed text-white'
                : darkMode
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            <RefreshCw size={16} className={isLoading || isLoadingBlocks ? 'animate-spin' : ''} />
            {isLoading || isLoadingBlocks
              ? 'Carregando...'
              : previousVisit.roleType === 'service_provider'
                ? 'Repetir como Prestador'
                : 'Repetir como Visitante'
            }
          </button>
        </div>
      </div>
    );
  };

  const handleVisitorSearch = async () => {
    if (!searchInput.trim()) {
      toast.error('Digite um CPF ou nome para buscar');
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    setSearchResults([]);

    try {
      const query = searchInput.trim();
      let results: VisitorSearchResult[] = [];

      if (checkCPFFormat(query)) {
        // Normalize CPF to remove dots and dashes before sending to API
        const normalizedCPF = normalizeCPF(query);
        console.log(' CPF Search - Original:', query, 'Normalized:', normalizedCPF);
        try {
          const url = `/api/v1/customers/${customerId}/people/search/cpf/${normalizedCPF}?include_inactive_roles=true`;
          console.log(' CPF Search URL:', url);
          const response = await fetchWithAuth(url);
          if (response.ok) {
            const data = await response.json();
            const person = data.data;
            if (person) {
              results = [{
                id: person.id,
                first_name: person.first_name,
                last_name: person.last_name,
                cpf: person.cpf,
                phone: person.phone,
                profile_picture_url: person.profile_picture_url,
                identity_photo_url: person.identity_photo?.photo_url || person.profile_picture_url,
              }];
            }
          }
        } catch (error: any) {
          if (error.response?.status !== 404) {
            throw error;
          }
        }
      } else {
        // For name search, also normalize if it happens to be a CPF (for consistency)
        const searchTerm = checkCPFFormat(query) ? normalizeCPF(query) : query;
        const searchUrl = `/api/v1/customers/${customerId}/people?search=${encodeURIComponent(searchTerm)}&page_size=50&_t=${Date.now()}`;
        console.log(' Name search URL:', searchUrl);
        const response = await fetchWithAuth(searchUrl);
        if (response.ok) {
          const data = await response.json();
          const people = data.data || [];

          // DEBUG: Log first result to see what fields are available
          if (people.length > 0) {
            console.log(' First visitor search result:', people[0]);
            console.log(' Photo fields:', {
              profile_picture_url: people[0].profile_picture_url,
              identity_photo_url: people[0].identity_photo_url,
            });
          }

          results = people.map((p: any) => ({
            id: p.id,
            first_name: p.first_name,
            last_name: p.last_name,
            cpf: p.cpf,
            phone: p.phone,
            profile_picture_url: p.profile_picture_url,
            identity_photo_url: p.identity_photo?.photo_url || p.profile_picture_url,
          }));
        }
      }

      console.log(` Search complete: ${results.length} visitors found`);
      if (results.length > 0) {
        console.log(' Sample result:', results[0]);
      }

      setSearchResults(results);
    } catch (error: any) {
      logger.error('visitor.modal', 'Failed to search visitors', error as Error);
      toast.error('Erro ao buscar visitantes');
    } finally {
      setIsSearching(false);
    }
  };

  // Auto-select first search result when results appear
  useEffect(() => {
    if (searchResults.length > 0) {
      setSelectedResultIndex(0);
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        firstResultRef.current?.focus();
      }, 100);
    }
  }, [searchResults]);

  const handleSelectVisitor = async (visitor: VisitorSearchResult) => {
    try {
      // Fetch full person details with photos and roles included
      const response = await fetchWithAuth(
        `/api/v1/customers/${customerId}/people/${visitor.id}?include_photos=true&include_roles=true`
      );
      if (response.ok) {
        const data = await response.json();
        const person = data.data || data;

        // DEBUG: Log person data to check if roles are included
        console.log(' Person data with roles:', {
          id: person.id,
          name: `${person.first_name} ${person.last_name}`,
          hasRoles: !!person.roles,
          rolesCount: person.roles?.length || 0,
          roles: person.roles?.map((r: any) => ({
            id: r.id, role_type: r.role_type,
            metadata_visitor_state: r.metadata?.visitor_state,
            role_metadata_visitor_state: r.role_metadata?.visitor_state,
            last_entry_at: r.metadata?.last_entry_at,
          }))
        });

        // Check if visitor is currently inside (has active visit with entry but no exit)
        const activeVisit = person.roles?.find((role: any) =>
          (role.role_type === 'visitor' || role.role_type === 'service_provider') &&
          (role.role_metadata?.last_entry_at || role.metadata?.last_entry_at) &&
          !(role.role_metadata?.last_exit_at || role.metadata?.last_exit_at)
        );

        if (activeVisit) {
          // Visitor is currently inside - show re-entry confirmation modal
          console.log(' Visitor is currently inside, showing re-entry confirmation');

          const rm = activeVisit.role_metadata || activeVisit.metadata || {};
          const unitDisplay = rm.unit_display || rm.unit || 'Não especificada';
          const entryTime = rm.last_entry_at || activeVisit.created_at;

          setActiveVisitInfo({
            personId: person.id,
            roleId: activeVisit.id,
            personName: `${person.first_name} ${person.last_name}`,
            unit: unitDisplay,
            entryTime: entryTime,
            person: person,
            visitor: visitor
          });
          setReentryVariant('inside');
          setShowReentryConfirmation(true);
          return;
        }

        // Check if visitor is awaiting entry (registered but hasn't entered yet)
        const awaitingVisit = person.roles?.find((role: any) =>
          (role.role_type === 'visitor' || role.role_type === 'service_provider') &&
          (role.metadata?.visitor_state === 'awaiting_entry' ||
           role.role_metadata?.visitor_state === 'awaiting_entry')
        );

        if (awaitingVisit && !isOpenClearance(awaitingVisit)) {
          console.log(' Visitor is awaiting entry, showing confirmation');
          const meta = awaitingVisit.metadata || awaitingVisit.role_metadata || {};
          const unitDisplay = meta.unit_display || meta.unit || 'Não especificada';
          const registrationTime = awaitingVisit.created_at || awaitingVisit.valid_from;

          setActiveVisitInfo({
            personId: person.id,
            roleId: awaitingVisit.id,
            personName: `${person.first_name} ${person.last_name}`,
            unit: unitDisplay,
            entryTime: registrationTime,
            person: person,
            visitor: visitor
          });
          setReentryVariant(reentryCaptionForRole(awaitingVisit));
          setShowReentryConfirmation(true);
          return;
        }

        // Catch any remaining active role (e.g. always_enabled in "exited" state).
        // GetPerson returns ALL roles (active + inactive), so we must check is_active explicitly.
        // Exclude pre_registered — those are future visits, not active conflicts.
        const anyActiveConflict = person.roles?.find((role: any) =>
          (role.role_type === 'visitor' || role.role_type === 'service_provider') && role.is_active === true &&
          role.role_metadata?.visitor_state !== 'pre_registered'
        );
        if (anyActiveConflict && !isOpenClearance(anyActiveConflict)) {
          console.log(' Found conflicting active role (e.g. always_enabled/exited), showing confirmation', anyActiveConflict);
          const rm = anyActiveConflict.role_metadata || anyActiveConflict.metadata || {};
          setActiveVisitInfo({
            personId: person.id,
            roleId: anyActiveConflict.id,
            personName: `${person.first_name} ${person.last_name}`,
            unit: rm.unit_display || rm.unit || 'Não especificada',
            entryTime: rm.last_entry_at || anyActiveConflict.created_at || '',
            person: person,
            visitor: visitor,
          });
          // Says what it IS — an open liberação, not someone at the gate. The deletePersonRole
          // path below keys on both variants, so behaviour is unchanged.
          setReentryVariant('active_role');
          setShowReentryConfirmation(true);
          return;
        }

        console.log(' Visitor is NOT inside, proceeding to form...');

        // First, use the photo from the search result if available
        const _visPhoto = resolvePhotoUrl(visitor);
        if (_visPhoto) {
          const photoUrl = getAbsoluteImageUrl(_visPhoto);
          console.log(' Setting identity photo from search result:', photoUrl);
          setCapturedPhoto({
            base64: photoUrl,
            blob: null as any,
            width: 0,
            height: 0,
            faceDetected: false,
          });
        }

        setExistingPerson(person);
        if (person.vehicles && Array.isArray(person.vehicles)) setPersonVehicles(person.vehicles);

        // Populate form fields so validation passes
        setValue('first_name', person.first_name);
        setValue('last_name', person.last_name);
        setValue('cpf', person.cpf || '');
        setValue('phone', person.phone || '');

        console.log(' Person data received:', {
          id: person.id,
          identity_photo_url: person.identity_photo_url,
          profile_picture_url: person.profile_picture_url,
          document_front_url: person.document_front_url,
          document_back_url: person.document_back_url,
          vehicles: person.vehicles,
          allFields: Object.keys(person)
        });

        // Load identity photo - try from visitor first, then from full person data
        let identityPhotoUrl: string | null | undefined = resolvePhotoUrl(visitor);

        // If not in visitor, try from full person data
        if (!identityPhotoUrl) {
          // Try identity_photo relationship
          if (person.identity_photo?.photo_url) {
            identityPhotoUrl = person.identity_photo.photo_url;
          }
          // Try profile_picture_url
          else if (person.profile_picture_url) {
            identityPhotoUrl = person.profile_picture_url;
          }
          // Try identity_photos array
          else if (person.identity_photos && Array.isArray(person.identity_photos) && person.identity_photos.length > 0) {
            identityPhotoUrl = person.identity_photos[0].photo_url;
          }
          // Try finding identity photo in photos array
          else if (person.photos && Array.isArray(person.photos)) {
            const identityPhoto = person.photos.find((p: any) =>
              p.photo_type === 'identity' || p.photo_type === 'profile' || p.is_primary
            );
            if (identityPhoto) {
              identityPhotoUrl = identityPhoto.photo_url;
            }
          }
        }

        if (identityPhotoUrl) {
          const photoUrl = getAbsoluteImageUrl(identityPhotoUrl);
          console.log(' Setting identity photo:', photoUrl);
          setCapturedPhoto({
            base64: photoUrl,
            blob: null as any,
            width: 0,
            height: 0,
            faceDetected: false,
          });
        } else {
          console.log(' No identity photo found for person');
        }

        // Load document and vehicle photos from person data
        if (person.document_front_url) {
          const docFrontUrl = getAbsoluteImageUrl(person.document_front_url);
          console.log(' Setting document front photo:', docFrontUrl);
          setDocumentFrontPhoto({
            base64: docFrontUrl,
            blob: null as any,
            width: 0,
            height: 0,
            faceDetected: false,
          });
        }

        if (person.document_back_url) {
          const docBackUrl = getAbsoluteImageUrl(person.document_back_url);
          console.log(' Setting document back photo:', docBackUrl);
          setDocumentBackPhoto({
            base64: docBackUrl,
            blob: null as any,
            width: 0,
            height: 0,
            faceDetected: false,
          });
        }

        // Load vehicle photo if person has vehicles
        if (person.vehicles && Array.isArray(person.vehicles) && person.vehicles.length > 0) {
          const vehicle = person.vehicles[0]; // Use first vehicle
          const _vPhotoUrl3 = vehicle.vehicle_photo_url || vehicle.photo_url;
          if (_vPhotoUrl3) {
            const vehicleUrl = getAbsoluteImageUrl(_vPhotoUrl3);
            console.log(' Setting vehicle photo:', vehicleUrl);
            setVehiclePhoto({
              base64: vehicleUrl,
              blob: null as any,
              width: 0,
              height: 0,
              faceDetected: false,
            });
          }
        }

        // Extract previous visit information for repeat visit feature
        console.log(' [Search] Checking for previous visits...');
        if (person.roles && Array.isArray(person.roles) && person.roles.length > 0) {
          console.log(` [Search] Found ${person.roles.length} roles:`, person.roles.map((r: any) => ({
            role_type: r.role_type,
            created_at: r.created_at,
            has_metadata: !!r.role_metadata,
            authorized_by: r.role_metadata?.authorized_by_resident_id
          })));

          // Filter visitor/service_provider roles with metadata
          const visitorRoles = person.roles.filter(
            (role: any) => (role.role_type === 'visitor' || role.role_type === 'service_provider') && role.role_metadata
          );

          console.log(` [Search] Found ${visitorRoles.length} visitor roles with metadata`);

          if (visitorRoles.length > 0) {
            // Sort by created_at descending to get most recent
            const mostRecentRole = visitorRoles.sort(
              (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )[0];

            const metadata = mostRecentRole.role_metadata;
            console.log(' [Search] Most recent role metadata:', metadata);

            // Show previous visit card if we have ANY data to repeat
            if (metadata) {
              console.log(' [Search] Previous visit found with data!');
              setPreviousVisit(mapRoleToVisitInfo(mostRecentRole));
              setRecentVisits(visitorRoles.slice(0, 5).map(mapRoleToVisitInfo));
              setShowPreviousVisitCard(true);
            } else {
              console.log(' [Search] Most recent role has no metadata');
            }
          } else {
            console.log(' [Search] No visitor roles with metadata found');
          }
        } else {
          console.log(' [Search] No roles found in person data');
        }

        // Go to form (card will show inside form if previous visit was found)
        console.log(' Setting current step to FORM');
        // Clear search state to hide search results dropdown
        setHasSearched(false);
        setSearchResults([]);
        setSearchInput('');
        setCurrentStep('form');
      } else {
        toast.error('Erro ao carregar dados do visitante');
      }
    } catch (error) {
      logger.error('visitor.modal', 'Failed to fetch visitor details', error as Error);
      toast.error('Erro ao carregar dados do visitante');
    }
  };

  const handleCreateNew = () => {
    setExistingPerson(null);
    if (checkCPFFormat(searchInput)) {
      setValue('cpf', searchInput.trim());
    }
    // Clear search state to hide search results dropdown
    setHasSearched(false);
    setSearchResults([]);
    setSearchInput('');
    setCurrentStep('form');
  };

  const handleRepeatVisit = async (visitOverride?: PreviousVisitInfo) => {
    const visit = visitOverride ?? previousVisit;
    if (!visit) {
      console.error(' handleRepeatVisit: No previous visit data');
      return;
    }
    // The auto-select-resident effect below reads `previousVisit.authorizedByResidentId`
    // directly, not the local `visit` var — keep it in sync so repeating an older visit
    // (not just the most recent one) picks the right responsible resident. That effect also
    // guards on `!selectedResponsible` to avoid clobbering a manual pick, but that guard also
    // blocks it after the page's own auto-repeat already filled selectedResponsible from the
    // most recent visit — clear it so re-selection isn't silently skipped.
    if (visitOverride && visitOverride !== previousVisit) {
      setPreviousVisit(visitOverride);
      setSelectedResponsible(null);
      setValue('authorized_by_resident_id', '');
    }

    console.log(' handleRepeatVisit: Starting with data:', visit);
    console.log(' Current state:', {
      blocks: blocks?.length,
      units: units?.length,
      residents: residents?.length,
      selectedCustomerType: selectedCustomer?.type
    });

    setIsRepeatingVisitLoading(true);

    try {
      // Pre-fill vehicle plate and purpose first
      if (visit.vehiclePlate) {
        console.log(' Setting vehicle plate:', visit.vehiclePlate);
        setVehicleList([{ plate: visit.vehiclePlate, model: '', color: '' }]);
      }
      if (visit.companyName) {
        setCompanyName(visit.companyName);
        // Pre-fill contractor dropdown if contractor_id is available
        const prevContractorId = (visit as any).contractorId;
        if (prevContractorId) setContractorId(prevContractorId);
      }
      if (visit.purpose) {
        console.log(' Setting purpose:', visit.purpose);
        setValue('purpose', visit.purpose);
      }
      if (visit.roleType) {
        setValue('role_type', visit.roleType);
        if (visit.roleType === 'service_provider' && visit.serviceType) {
          setValue('service_type', visit.serviceType);
        }
      }

      // This used to restore the previous role's "Sempre Habilitado" / "Período de Acesso
      // Personalizado" window. Those modes no longer have any UI on this form, so restoring them
      // would switch on state nobody can see or correct — and a restored custom period fails submit
      // validation pointing at a date field that is not on screen. A repeated visit is a visit.

      // Handle empresa/fabrica customers separately (use department, not block/unit)
      const isEmpresaCustomer = selectedCustomer?.type === 'empresa' || selectedCustomer?.type === 'fabrica';
      if (isEmpresaCustomer) {
        // Find department by UUID first, fallback to name match
        let dept: any = null;
        if (visit.unitId) {
          dept = departments.find((d: any) => d.id === visit.unitId);
        }
        if (!dept && visit.unit) {
          dept = departments.find((d: any) => d.name === visit.unit);
        }

        if (dept) {
          setSelectedUnit({ value: dept.id, label: dept.name });
          console.log(' Auto-filled department for repeat visit:', dept.name);
        } else {
          console.warn(' Department not found for repeat visit, unitId:', visit.unitId, 'name:', visit.unit);
        }

        // Fetch employees and auto-select responsible person
        setIsRepeatingVisit(true);
        await fetchResidents(selectedCustomer?.type, '', undefined, dept?.id);

        setShowPreviousVisitCard(false);
        setIsRepeatingVisitLoading(false);
        return;
      }

      // Check if we have block/unit data (condo customers)
      if (!visit.block || !visit.unit) {
        console.error(' Missing block or unit in previous visit data');
        toast('Dados incompletos da última visita');
        setShowPreviousVisitCard(false);
        setIsRepeatingVisitLoading(false);
        return;
      }

      // Check if blocks are loaded
      if (!blocks || blocks.length === 0) {
        console.error(' Blocks not loaded');
        toast('Aguarde... carregando blocos', { icon: '' });
        setIsRepeatingVisitLoading(false);
        return;
      }

      // Find matching block - SMART LOGIC: If multiple blocks with same name, find the one with the unit
      console.log(` Looking for block: "${visit.block}" in ${blocks.length} blocks`);

      // Get all blocks with matching name
      const matchingBlocks = blocks.filter(
        (b: any) => b.name === visit.block || b.id === visit.block
      );

      if (matchingBlocks.length === 0) {
        console.error(' No blocks found with name:', visit.block);
        console.log('Available blocks:', blocks.map((b: any) => ({ id: b.id, name: b.name })));
        toast('Bloco anterior não encontrado');
        setShowPreviousVisitCard(false);
        setIsRepeatingVisitLoading(false);
        return;
      }

      console.log(` Found ${matchingBlocks.length} block(s) with name "${visit.block}"`);

      // If multiple blocks with same name, we need to find the one that has the unit
      let matchingBlock: any = null;
      let matchingUnit: any = null;
      let unitsData: any[] = [];

      if (matchingBlocks.length === 1) {
        // Only one block, use it
        matchingBlock = matchingBlocks[0];
        console.log(' Single block found:', matchingBlock);
      } else {
        // Multiple blocks with same name - find the one with the unit
        console.log(' Multiple blocks with same name, searching for the one with unit:', visit.unit);

        for (const block of matchingBlocks) {
          console.log(`   Checking block ID ${block.id} for unit "${visit.unit}"`);

          try {
            const unitsResponse = await condoApi.getUnits(block.id);
            const units = unitsResponse.data?.data || unitsResponse.data || [];

            const unit = units.find(
              (u: any) => u.number === visit.unit || u.name === visit.unit
            );

            if (unit) {
              console.log(`   Found unit in block ID ${block.id}!`);
              matchingBlock = block;
              matchingUnit = unit;
              unitsData = units;
              break;
            } else {
              console.log(`   Unit not found in block ID ${block.id}`);
            }
          } catch (error) {
            console.error(`   Error fetching units for block ID ${block.id}:`, error);
          }
        }

        if (!matchingBlock) {
          console.error(' Unit not found in any block with name:', visit.block);
          toast('Unidade anterior não encontrada em nenhum bloco');
          setShowPreviousVisitCard(false);
          setIsRepeatingVisitLoading(false);
          return;
        }
      }

      console.log(' Selected block:', matchingBlock);

      // Set selected block
      setSelectedBlock({ value: matchingBlock.id, label: matchingBlock.name });

      // Fetch units for that block (if not already fetched)
      if (unitsData.length === 0) {
        console.log(' Fetching units for block:', matchingBlock.id);
        const unitsResponse = await condoApi.getUnits(matchingBlock.id);
        unitsData = unitsResponse.data?.data || unitsResponse.data || [];
        console.log(` Fetched ${unitsData.length} units`);
      }
      setUnits(unitsData);

      // Find matching unit (if not already found)
      if (!matchingUnit) {
        console.log(` Looking for unit: "${visit.unit}" in ${unitsData.length} units`);

        // Try multiple matching strategies
        // 1. Exact match on number, name, or identifier
        matchingUnit = unitsData.find(
          (u: any) =>
            u.number === visit.unit ||
            u.name === visit.unit ||
            u.identifier === visit.unit ||
            u.unit_number === visit.unit
        );

        // 2. If no exact match, try partial match (unit number contains the search string)
        if (!matchingUnit) {
          console.log(' Trying partial match...');
          matchingUnit = unitsData.find((u: any) => {
            const unitNum = u.number || u.unit_number || u.identifier || u.name || '';
            // Check if the unit number contains the previous visit unit (e.g., "Q8LT13" in "Q8 - Q8LT13 - Address")
            return unitNum.includes(visit.unit) || visit.unit.includes(unitNum);
          });
        }

        // 3. If still no match, try extracting unit code from formatted strings
        if (!matchingUnit) {
          console.log(' Trying to extract unit code from formatted names...');
          matchingUnit = unitsData.find((u: any) => {
            const unitStr = u.number || u.name || '';
            // Extract potential unit code (e.g., "Q8LT13" from "Q8 - Q8LT13 - Address")
            const match = unitStr.match(/\b([A-Z0-9]+LT[0-9]+)\b/i);
            if (match) {
              return match[1] === visit.unit;
            }
            return false;
          });
        }

        if (!matchingUnit) {
          console.error(' Unit not found after all matching strategies:', visit.unit);
          console.log('Available units:', unitsData.map((u: any) => ({
            id: u.id,
            number: u.number,
            unit_number: u.unit_number,
            identifier: u.identifier,
            name: u.name
          })));
          toast('Unidade anterior não encontrada');
          setShowPreviousVisitCard(false);
          setIsRepeatingVisitLoading(false);
          return;
        }
      }

      console.log(' Found unit:', matchingUnit);

      // Set selected unit
      setSelectedUnit({
        value: matchingUnit.id,
        label: `${matchingBlock.name} - ${matchingUnit.number || matchingUnit.name}`,
        blockId: matchingBlock.id,
        blockName: matchingBlock.name
      });

      // Auto-populate Rua + Número if the unit has a street
      if (matchingUnit.street?.id) {
        setSelectedStreet({ value: matchingUnit.street.id, label: matchingUnit.street.name });
        setStreetNumberSearch(matchingUnit.street_number || '');
      } else {
        setSelectedStreet(null);
        setStreetNumberSearch('');
      }

      // Fetch residents for that unit and set flag to auto-select resident
      console.log(' Fetching residents for unit:', matchingUnit.id);
      setIsRepeatingVisit(true); // This will trigger the useEffect to auto-select resident
      await fetchResidents(
        selectedCustomer?.type,
        '', // no search term, get all residents
        matchingUnit.id
      );

      console.log(' Repeat visit setup complete - waiting for resident auto-selection');

      // Hide the card after successful setup
      setShowPreviousVisitCard(false);
      setIsRepeatingVisitLoading(false);

    } catch (error) {
      console.error(' Error in handleRepeatVisit:', error);
      toast.error('Erro ao carregar dados da última visita');
      setShowPreviousVisitCard(false);
      setIsRepeatingVisitLoading(false);
    }
  };

  // Auto-trigger repeat visit when previous visit data is detected
  useEffect(() => {
    // Only auto-trigger if:
    // 1. We have previous visit data
    // 2. The card is shown (meaning data just loaded)
    // 3. We're NOT already in the process of repeating a visit
    // 4. Reference data (blocks for condo, departments for empresa) is loaded
    const readyToRepeat = previousVisit && showPreviousVisitCard && !isRepeatingVisit && !isRepeatingVisitLoading;
    if (!readyToRepeat) return;

    const isEmpresaCustomer = selectedCustomer?.type === 'empresa' || selectedCustomer?.type === 'fabrica';
    if (isEmpresaCustomer) {
      if (departments.length > 0) {
        console.log(' Auto-triggering repeat visit (departments loaded)');
        handleRepeatVisit();
      } else {
        console.log(' Waiting for departments to load before auto-triggering repeat visit...');
      }
    } else {
      if (blocks && blocks.length > 0) {
        console.log(' Auto-triggering repeat visit with previous data (blocks loaded)');
        handleRepeatVisit();
      } else {
        console.log(' Waiting for blocks to load before auto-triggering repeat visit...');
      }
    }
  }, [previousVisit, showPreviousVisitCard, isRepeatingVisit, isRepeatingVisitLoading, blocks, departments, selectedCustomer?.type]);

  // Auto-select resident after residents are loaded — for an explicit "Repetir Visita" click,
  // AND for landing directly on an existing person (e.g. "Nova Visita" from a profile modal):
  // that flow only pre-fills the "Morador Responsável" DISPLAY from previousVisit, it never
  // actually set selectedResponsible state, so the field looked filled but wasn't really —
  // canSubmit stayed false and anything gated on selectedResponsible (like the "aguardar
  // aprovação via app" checkbox) silently never appeared. !selectedResponsible guards against
  // clobbering a manual re-selection once the user has made one.
  useEffect(() => {
    if ((isRepeatingVisit || existingPerson) && residents.length > 0 && previousVisit && !selectedResponsible) {
      console.log(` Auto-selecting resident from ${residents.length} residents`);
      console.log('Looking for resident ID:', previousVisit.authorizedByResidentId);

      const matchingResident = residents.find(
        (r: any) => r.id === previousVisit.authorizedByResidentId
      );

      if (matchingResident) {
        console.log(' Found matching resident:', matchingResident);
        const residentOption = {
          value: matchingResident.id,
          label: `${matchingResident.first_name} ${matchingResident.last_name}`
        };
        setSelectedResponsible(residentOption);
        setValue('authorized_by_resident_id', matchingResident.id, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
        console.log(' Set selectedResponsible:', residentOption);
        if (isRepeatingVisit) toast.success('Morador selecionado automaticamente!');
      } else {
        console.warn(' Resident not found in loaded residents');
        console.log('Available resident IDs:', residents.map((r: any) => r.id));
        if (isRepeatingVisit) toast('Morador anterior não encontrado. Selecione manualmente.');
      }

      // Clear the flag (no-op if it was already false, e.g. the existingPerson-only trigger)
      setIsRepeatingVisit(false);
    }
  }, [residents, isRepeatingVisit, previousVisit, existingPerson, selectedResponsible, setValue]);

  // Sync the Interfonar toggle to this person's last-known preference — same trigger conditions
  // as the auto-select-resident effect above (landing directly on an existing person, OR an
  // explicit "Repetir Visita" click). Without this it silently stayed on the hardcoded
  // useState(true) default regardless of what was actually set on their prior registration,
  // which is why it always showed "SIM" even for someone who'd previously been set to "NÃO".
  useEffect(() => {
    if ((isRepeatingVisit || existingPerson) && previousVisit) {
      setRequiresIntercom(previousVisit.requiresIntercom ?? true);
    }
  }, [previousVisit, isRepeatingVisit, existingPerson]);

  // Auto-select resident from initialResidentId prop (e.g. "Autorizar Visita" from morador row)
  useEffect(() => {
    if (!initialResidentId || !residents.length || selectedResponsible) return;
    const match = residents.find((r: any) => r.id === initialResidentId);
    if (match) {
      const opt = { value: match.id, label: `${match.first_name} ${match.last_name}` };
      setSelectedResponsible(opt);
      setValue('authorized_by_resident_id', match.id, { shouldValidate: true, shouldDirty: true });
    }
  }, [initialResidentId, residents, selectedResponsible, setValue]);

  // Show confirmation modal before actual submission
  const onSubmit = async (data: any) => {
    setPendingFormData(data);
    setShowConfirmModal(true);
    setDisclaimerAcknowledged(false);
  };

  // Pre-registration submit — reads current form values directly, no confirm dialog
  const handlePreRegSubmit = async () => {
    setIsPreRegSubmitting(true);
    try {
      const result = await submitPreRegistration({
        token: preRegistrationToken!,
        first_name: firstName || '',
        last_name: lastName || '',
        cpf: watch('cpf') || undefined,
        phone: watch('phone') || undefined,
        visiting_unit_id: selectedUnit?.value || undefined,
        comment: newVisitorNote || undefined,
        photo_base64: capturedPhoto?.base64 || undefined,
        captcha_token: 'bypass',
      });
      if (result.success) {
        toast.success('Pré-registro criado com sucesso!');
        onPreRegistrationCreated?.();
        onClose();
      } else {
        toast.error(result.error || 'Erro ao criar pré-registro');
      }
    } catch (err) {
      toast.error('Erro ao criar pré-registro');
    } finally {
      setIsPreRegSubmitting(false);
    }
  };

  // Enter key to submit: When all required fields filled, press Enter to submit
  useEffect(() => {
    if (!open || currentStep !== 'form') return;

    const handleEnterKey = (e: KeyboardEvent) => {
      // Only handle Enter key
      if (e.key !== 'Enter') return;

      const target = e.target as HTMLElement;

      // Don't interfere with textareas
      if (target.tagName === 'TEXTAREA') return;

      // Don't interfere with buttons
      if (target.tagName === 'BUTTON') return;

      // Don't interfere with React-Select when menu is open
      if (document.querySelector('.react-select__menu')) return;

      console.log(' Enter pressed in visitor modal');
      console.log(' Form state:', { canSubmit, firstName, lastName, selectedResponsible });

      // If form is ready to submit, click the Cadastrar button
      if (canSubmit) {
        e.preventDefault();
        e.stopPropagation();

        const submitButton = document.querySelector<HTMLButtonElement>('button[type="submit"]:not([disabled])');
        console.log(' Submit button found:', !!submitButton);

        if (submitButton) {
          console.log(' Clicking Cadastrar button via Enter key!');
          submitButton.click();
        }
      } else {
        console.log(' Form not ready to submit (canSubmit=false)');
      }
    };

    document.addEventListener('keydown', handleEnterKey);
    return () => document.removeEventListener('keydown', handleEnterKey);
  }, [open, currentStep, canSubmit, firstName, lastName, selectedResponsible]);

  // Handle confirmation modal confirm
  const handleConfirmSubmit = async () => {
    if (!pendingFormData || isActuallySubmitting) return;

    setIsActuallySubmitting(true);

    try {
      await handleActualSubmit(pendingFormData);
      setPendingFormData(null);
      setDisclaimerAcknowledged(false);
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Visitor registration failed:', error);
      toast.error('Erro ao cadastrar visitante. Tente novamente.');
    } finally {
      setIsActuallySubmitting(false);
    }
  };

  // Handle confirmation modal cancel
  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setPendingFormData(null);
    setDisclaimerAcknowledged(false);
  };

  // Handle re-entry confirmation confirm
  const handleReentryConfirm = () => {
    if (!activeVisitInfo) return;

    console.log(' Re-entry confirmed, proceeding to registration form');
    setPendingReentry(true);
    setShowReentryConfirmation(false);

    const { person, visitor } = activeVisitInfo;

    // Execute the same logic as in handleSelectVisitor after the activeVisit check
    console.log(' Visitor re-entry approved, proceeding to form...');

    // First, use the photo from the search result if available
    const _rePhoto = resolvePhotoUrl(visitor);
    if (_rePhoto) {
      const photoUrl = getAbsoluteImageUrl(_rePhoto);
      console.log(' Setting identity photo from search result:', photoUrl);
      setCapturedPhoto({
        base64: photoUrl,
        blob: null as any,
        width: 0,
        height: 0,
        faceDetected: false,
      });
    }

    setExistingPerson(person);
    if (person.vehicles && Array.isArray(person.vehicles)) setPersonVehicles(person.vehicles);

    // Populate form fields so validation passes
    setValue('first_name', person.first_name);
    setValue('last_name', person.last_name);
    setValue('cpf', person.cpf || '');
    setValue('phone', person.phone || '');

    console.log(' Person data for re-entry:', {
      id: person.id,
      identity_photo_url: person.identity_photo_url,
      profile_picture_url: person.profile_picture_url
    });

    // Load identity photo - try from visitor first, then from full person data
    let identityPhotoUrl: string | null | undefined = resolvePhotoUrl(visitor);

    // If not in visitor, try from full person data
    if (!identityPhotoUrl) {
      // Try identity_photo relationship
      if (person.identity_photo?.photo_url) {
        identityPhotoUrl = person.identity_photo.photo_url;
      }
      // Try profile_picture_url
      else if (person.profile_picture_url) {
        identityPhotoUrl = person.profile_picture_url;
      }
      // Try identity_photos array
      else if (person.identity_photos && Array.isArray(person.identity_photos) && person.identity_photos.length > 0) {
        identityPhotoUrl = person.identity_photos[0].photo_url;
      }
      // Try finding identity photo in photos array
      else if (person.photos && Array.isArray(person.photos)) {
        const identityPhoto = person.photos.find((p: any) =>
          p.photo_type === 'identity' || p.photo_type === 'profile'
        );
        if (identityPhoto?.photo_url) {
          identityPhotoUrl = identityPhoto.photo_url;
        }
      }
    }

    if (identityPhotoUrl) {
      const absolutePhotoUrl = getAbsoluteImageUrl(identityPhotoUrl);
      console.log(' Found identity photo, setting it:', absolutePhotoUrl);
      setCapturedPhoto({
        base64: absolutePhotoUrl,
        blob: null as any,
        width: 0,
        height: 0,
        faceDetected: false,
      });
    } else {
      console.log(' No identity photo found for person');
    }

    // Load document photos if available
    if (person.document_front_url) {
      const docFrontUrl = getAbsoluteImageUrl(person.document_front_url);
      console.log(' Found document front photo:', docFrontUrl);
      setDocumentFrontPhoto({
        base64: docFrontUrl,
        blob: null as any,
        width: 0,
        height: 0,
        faceDetected: false,
      });
    }

    if (person.document_back_url) {
      const docBackUrl = getAbsoluteImageUrl(person.document_back_url);
      console.log(' Found document back photo:', docBackUrl);
      setDocumentBackPhoto({
        base64: docBackUrl,
        blob: null as any,
        width: 0,
        height: 0,
        faceDetected: false,
      });
    }

    // Load vehicle data if available
    if (person.vehicles && person.vehicles.length > 0) {
      const vehicle = person.vehicles[0];
      console.log(' Found vehicle data:', vehicle);
      setVehicleList([{ plate: vehicle.license_plate || '', model: vehicle.model || '', color: vehicle.color || '' }]);
      if (vehicle.photo_url) {
        const vehiclePhotoUrl = getAbsoluteImageUrl(vehicle.photo_url);
        console.log(' Found vehicle photo:', vehiclePhotoUrl);
        setVehiclePhoto({
          base64: vehiclePhotoUrl,
          blob: null as any,
          width: 0,
          height: 0,
          faceDetected: false,
        });
      }
    }

    // Pre-fill previous visit info from the conflict role's metadata
    // so "Autorizado por" and other fields can be repeated
    const conflictRole = (person.roles || []).find((r: any) => r.id === activeVisitInfo.roleId)
      || (person.roles || []).find((r: any) =>
          (r.role_type === 'visitor' || r.role_type === 'service_provider') &&
          (r.is_active === true || r.role_metadata)
        );
    if (conflictRole?.role_metadata) {
      setPreviousVisit(mapRoleToVisitInfo(conflictRole));
      const recentRoles = (person.roles || [])
        .filter((r: any) => (r.role_type === 'visitor' || r.role_type === 'service_provider') && r.role_metadata)
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setRecentVisits(recentRoles.slice(0, 5).map(mapRoleToVisitInfo));
      setShowPreviousVisitCard(true);
    }

    // Show form
    setCurrentStep('form');
    toast.success('Visita anterior será finalizada ao cadastrar nova entrada', {
      duration: 4000
    });
  };

  // Handle re-entry confirmation cancel
  const handleReentryCancel = () => {
    console.log(' Re-entry cancelled');
    setShowReentryConfirmation(false);
    setActiveVisitInfo(null);
    setPendingReentry(false);
    onClose(); // close the registration modal too
  };

  // Handle Enter key in confirmation modal
  useEffect(() => {
    if (!showConfirmModal) return;

    const handleEnterKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !isActuallySubmitting) {
        e.preventDefault();
        console.log(' Enter pressed in confirmation modal, checkbox:', disclaimerAcknowledged);
        if (!disclaimerAcknowledged) {
          // First Enter: Check the checkbox
          console.log(' Checking checkbox');
          setDisclaimerAcknowledged(true);
        } else {
          // Second Enter: Submit
          console.log(' Submitting form');
          handleConfirmSubmit();
        }
      }
    };

    window.addEventListener('keydown', handleEnterKey);
    return () => {
      window.removeEventListener('keydown', handleEnterKey);
    };
  }, [showConfirmModal, disclaimerAcknowledged, isActuallySubmitting, handleConfirmSubmit]);

  // Handle CPF match click - open person detail modal
  const handleCPFMatchClick = () => {
    if (!cpfExistingPerson?.id) return;

    // Close this modal
    onClose();

    // Small delay for animation
    setTimeout(() => {
      openPersonDetail(cpfExistingPerson.id, true, darkMode);
    }, 150);
  };

  // Handle conclude note
  /**
   * The backend REQUIRES a reason (customer_notes_handler.go:468 — "Reason is required when
   * concluding a note"); the old call sent none, so every attempt from this screen 400'd. The
   * reason now comes from PersonNotesAlert's prompt, and failures are re-thrown so the prompt can
   * stay open with the operator's text rather than being swallowed into a toast.
   */
  const handleConcludeNote = async (noteId: string, reason: string) => {
    setConcludingNoteId(noteId);
    try {
      await concludeNote(noteId, reason);
      toast.success('Nota concluída');
    } catch (err: any) {
      throw new Error(err?.message || 'Erro ao concluir nota');
    } finally {
      setConcludingNoteId(null);
    }
  };

  // Shared by handleActualSubmit and handlePreCadastroSubmit — uploads document front/back and
  // vehicle photos, updates the person record with their URLs, and creates vehicle records.
  // Identity photo upload is handled separately by each caller.
  const uploadDocumentsAndVehicles = async (personId: string) => {
    // Upload document photos and update person record with document URLs
    const documentUpdates: { document_front_url?: string; document_back_url?: string } = {};
    // Vehicle photo attaches to the Vehicle record (Vehicle.photo_url) below — Person has no
    // vehicle_photo_url column, so setting it via documentUpdates/updatePerson is a silent no-op.
    let vehiclePhotoUrl: string | undefined;

    // Upload document front photo if captured
    if (documentFrontPhoto?.blob) {
      try {
        const formData = new FormData();
        formData.append('photo', documentFrontPhoto.blob, 'document_front.jpg');
        formData.append('customer_id', customerId);
        formData.append('photo_type', 'document_front'); // Mark as document photo

        // Upload to get URL, then update person record
        const docResponse = await api.post(`/people/${personId}/photos`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Get the photo URL from response
        const docPhotoUrl = docResponse.data?.data?.photo_url || docResponse.data?.photo_url;
        if (docPhotoUrl) {
          documentUpdates.document_front_url = docPhotoUrl;
        }
        logger.info('visitor.modal', 'Document front photo uploaded', { personId });
      } catch (photoError) {
        // Non-fatal — the visitor is still registered — but it must not pass unseen: the
        // operator is the only one who can retake the photo, and only while they still
        // remember this registration.
        logger.error('visitor.modal', 'Failed to upload document front photo', photoError as Error);
        toast.error('Visitante registrado, mas a foto da FRENTE do documento não foi salva. Edite o cadastro e envie novamente.');
      }
    }

    // Upload document back photo if captured
    if (documentBackPhoto?.blob) {
      try {
        const formData = new FormData();
        formData.append('photo', documentBackPhoto.blob, 'document_back.jpg');
        formData.append('customer_id', customerId);
        formData.append('photo_type', 'document_back'); // Mark as document photo

        const docResponse = await api.post(`/people/${personId}/photos`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Get the photo URL from response
        const docPhotoUrl = docResponse.data?.data?.photo_url || docResponse.data?.photo_url;
        if (docPhotoUrl) {
          documentUpdates.document_back_url = docPhotoUrl;
        }
        logger.info('visitor.modal', 'Document back photo uploaded', { personId });
      } catch (photoError) {
        logger.error('visitor.modal', 'Failed to upload document back photo', photoError as Error);
        toast.error('Visitante registrado, mas a foto do VERSO do documento não foi salva. Edite o cadastro e envie novamente.');
      }
    }

    // Upload vehicle photo if captured
    if (vehiclePhoto?.blob) {
      try {
        const formData = new FormData();
        formData.append('photo', vehiclePhoto.blob, 'vehicle_photo.jpg');
        formData.append('customer_id', customerId);
        formData.append('photo_type', 'vehicle'); // Mark as vehicle photo

        const vehicleResponse = await api.post(`/people/${personId}/photos`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Get the photo URL from response
        vehiclePhotoUrl = vehicleResponse.data?.data?.photo_url || vehicleResponse.data?.photo_url;
        logger.info('visitor.modal', 'Vehicle photo uploaded', { personId });
      } catch (photoError) {
        // The vehicle is created right after this with whatever photo_url we have. Failing
        // here silently produced a vehicle with no photo and a success message.
        logger.error('visitor.modal', 'Failed to upload vehicle photo', photoError as Error);
        toast.error('Visitante registrado, mas a foto do veículo não foi salva. Edite o veículo e envie novamente.');
      }
    }

    // Update person record with document URLs if we have any
    if (Object.keys(documentUpdates).length > 0) {
      try {
        await condoApi.updatePerson(customerId, personId, documentUpdates);
        logger.info('visitor.modal', 'Person document URLs updated', { personId, documentUpdates });
      } catch (updateError) {
        logger.error('visitor.modal', 'Failed to update person document URLs', updateError as Error);
      }
    }

    // Create vehicle records for all added vehicles. This flow was the only one of the seven
    // person-registration screens that checked the POST status; createVehiclesForPerson is that
    // behaviour extracted, so the other six stop swallowing failures too. Difference from
    // before: a duplicate plate (409) now warns as well, rather than being dropped in silence.
    const vehicleFailures = await createVehiclesForPerson(
      customerId,
      personId,
      roleType === 'service_provider' ? 'service_provider' : 'visitor',
      vehicleList,
      { photoUrl: vehiclePhotoUrl || undefined },
    );
    const vehicleWarning = describeVehicleFailures(vehicleFailures);
    if (vehicleWarning) {
      logger.warn('visitor.modal', 'Vehicle creation had failures', { personId, vehicleFailures });
      toast.error(vehicleWarning);
    }
  };

  const handleActualSubmit = async (data: any) => {
    // "Cadastrar Acesso" lets the operator correct the name or add a missing CPF of someone already
    // in the base. Decide (and validate) that here, with the other guard clauses, so a bad value is
    // refused before anything is written — not halfway through creating the visit.
    let identityUpdate: PersonIdentityUpdate | null = null;
    if (existingPerson) {
      try {
        identityUpdate = buildPersonIdentityUpdate(existingPerson, {
          fullName: fullName || `${existingPerson.first_name || ''} ${existingPerson.last_name || ''}`,
          cpf: data.cpf || '',
          // Without this the telefone typed for an already-registered visitor was accepted by
          // the field, never sent, and silently lost — only brand-new people kept their phone.
          phone: data.phone || '',
          // The rest of the condomínio's configured fields, which this screen now renders. A
          // field that is shown but never sent is worse than one that is hidden.
          rg: data.rg || '',
          birthDate: birthDate || '',
          isMinor: isUnder18,
          extraData: data.extra_data || {},
        });
      } catch (identityError: any) {
        toast.error(identityError?.message || 'Verifique o nome e o CPF');
        return;
      }
    }

    // Validate always-enabled fields before submitting
    if (roleType === 'visitor' && isAlwaysEnabled && !disclaimerAccepted) {
      toast.error('Confirme os riscos de segurança para habilitar o acesso permanente');
      return;
    }
    if (roleType === 'visitor' && isAlwaysEnabled && !isIndefinite && !alwaysEnabledUntil) {
      toast.error('Informe a data de expiração do acesso ou marque como indefinido');
      return;
    }
    if (roleType === 'visitor' && isAlwaysEnabled && !isIndefinite && alwaysEnabledUntil) {
      const parsed = new Date(alwaysEnabledUntil);
      if (isNaN(parsed.getTime()) || parsed <= new Date()) {
        toast.error('Informe uma data futura válida');
        return;
      }
    }
    if (isCustomPeriodValidationRequired(roleType, isAlwaysEnabled, useCustomPeriodAccess)) {
      if (!customPeriodUntil) {
        toast.error('Informe a data de validade do período personalizado');
        return;
      }
      const parsed = new Date(customPeriodUntil);
      if (isNaN(parsed.getTime()) || parsed <= new Date()) {
        toast.error('A data de validade deve ser uma data futura');
        return;
      }
      // Liberar visita caps at 6 months (180 days), regardless of the customer's larger config.
      const maxDays = Math.min(roleType === 'service_provider' ? spCustomPeriodMaxDays : visitorCustomPeriodMaxDays, 180);
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + maxDays);
      if (parsed > maxDate) {
        toast.error(`O período não pode exceder 6 meses (${maxDays} dias)`);
        return;
      }
    }

    // Validate required photos (only for new persons, not existing ones)
    if (!existingPerson) {
      if (photoRequirements.identity_photo && !capturedPhoto) {
        toast.error('Foto de Identificação é obrigatória');
        return;
      }
      if (photoRequirements.document_front && !documentFrontPhoto) {
        toast.error('Foto do Documento (Frente) é obrigatória');
        return;
      }
      if (photoRequirements.document_back && !documentBackPhoto) {
        toast.error('Foto do Documento (Verso) é obrigatória');
        return;
      }
    }

    // Data de Nascimento isn't RHF-registered (plain birthDate state, masked input), so its
    // configured "required" flag needs its own check here instead of a register() rule.
    if ((fieldConfig?.standard_fields?.date_of_birth?.required ?? false) && !birthDate.trim()) {
      toast.error('Data de Nascimento é obrigatória');
      return;
    }

    try {
      // If this is a re-entry, first resolve the active/awaiting visit
      if (pendingReentry && activeVisitInfo) {
        if (reentryVariant === 'awaiting_entry' || reentryVariant === 'active_role') {
          // Visitor is awaiting entry — delete the pending role instead of checking out
          console.log(' Deleting awaiting-entry role before creating new entrance', {
            personId: activeVisitInfo.personId,
            roleId: activeVisitInfo.roleId
          });
          try {
            await condoApi.deletePersonRole(customerId, activeVisitInfo.personId, activeVisitInfo.roleId);
            console.log(' Awaiting-entry role deleted successfully');
            toast.success('Cadastro anterior cancelado automaticamente');
          } catch (deleteError) {
            console.error(' Failed to delete awaiting-entry role:', deleteError);
            toast.error('Erro ao cancelar cadastro anterior. Tente novamente.');
            throw deleteError;
          }
        } else {
          // Visitor is inside — check them out first
          console.log(' Checking out active visit before creating new entrance', {
            personId: activeVisitInfo.personId,
            roleId: activeVisitInfo.roleId
          });
          try {
            await condoApi.manualCheckOut(
              customerId,
              activeVisitInfo.personId,
              activeVisitInfo.roleId
            );
            console.log(' Active visit checked out successfully');
            toast.success('Visita anterior finalizada automaticamente');
          } catch (checkOutError) {
            console.error(' Failed to check out active visit:', checkOutError);
            toast.error('Erro ao finalizar visita anterior. Tente novamente.');
            throw checkOutError;
          }
        }

        // Clear re-entry state
        setPendingReentry(false);
        setActiveVisitInfo(null);
      }

      let personId: string;

      if (existingPerson) {
        personId = existingPerson.id;

        // Name/CPF corrections made on this screen go up before the visit is created, so the visit
        // is never recorded against data the operator just told us was wrong.
        if (identityUpdate) {
          await condoApi.updatePerson(customerId, personId, identityUpdate);
          setExistingPerson({ ...existingPerson, ...identityUpdate } as Person);
        }
      } else {
        const personPayload = {
          customer_id: customerId,
          first_name: data.first_name,
          last_name: data.last_name,
          cpf: data.cpf || undefined,
          phone: data.phone,
          rg: data.rg || undefined,
          email: data.email || undefined,
          birth_date: (() => {
            if (!birthDate || birthDate.length !== 10) return undefined;
            // birthDate is DD/MM/YYYY — convert to YYYY-MM-DDT00:00:00Z for backend
            const [dd, mm, yyyy] = birthDate.split('/');
            if (!dd || !mm || !yyyy || yyyy.length !== 4) return undefined;
            return `${yyyy}-${mm}-${dd}T00:00:00Z`;
          })(),
          is_minor: isUnder18,
          ...foreignerPayload(data),
          is_active: true,
          extra_data: data.extra_data && Object.keys(data.extra_data).length > 0 ? data.extra_data : undefined,
        };

        const personResponse = await condoApi.createPerson(customerId, personPayload);
        if (!personResponse.data || !personResponse.data.data) {
          throw new Error('Failed to create person');
        }
        personId = personResponse.data.data.id;
      }

      // Create note if provided (BEFORE role creation)
      if (newVisitorNote.trim()) {
        try {
          await condoApi.createCustomerNote(
            customerId,
            newVisitorNote.trim(),
            { person_id: personId }
          );
          logger.info('visitor.modal', 'Note created for new visitor', { personId });
        } catch (noteError) {
          // Log error but don't fail registration
          logger.error('visitor.modal', 'Failed to create note', noteError as Error, { personId });
          toast.error('Aviso: Visitante cadastrado, mas nota não foi salva');
        }
      }

      const roleMetadata: any = {
        authorized_by_resident_id: data.authorized_by_resident_id,
      };

      // CRITICAL: Use the form state (selectedResponsible, selectedBlock, selectedUnit) instead of resident object
      // The form state has the correct values that the user just selected
      if (selectedResponsible) {
        // The select label includes a " - unit/relation" suffix for disambiguation
        // (e.g. "João Silva - Quadra A 101 (Dep.)") — keep only the actual name.
        roleMetadata.authorized_by_name = cleanAuthorizedByName(selectedResponsible.label);

        // Use the selected block and unit from form state
        if (selectedBlock && selectedUnit) {
          roleMetadata.block = selectedBlock.label; // e.g., "A", "B", "Torre 1"
          roleMetadata.unit_id = selectedUnit.value; // UUID for clickable navigation

          // Extract just the unit number from the unit label
          // selectedUnit.label might be "Bloco A - 1201" or just "1201"
          const unitLabel = selectedUnit.label;
          if (unitLabel.includes(' - ')) {
            // Format: "Bloco A - 1201" -> extract "1201"
            roleMetadata.unit = unitLabel.split(' - ')[1];
          } else {
            // Format: "1201" -> use as-is
            roleMetadata.unit = unitLabel;
          }

          // Store full display format for backward compatibility
          roleMetadata.unit_display = unitLabel;

          console.log(' Stored metadata from form state:', {
            authorized_by_name: roleMetadata.authorized_by_name,
            block: roleMetadata.block,
            unit: roleMetadata.unit,
            unit_display: roleMetadata.unit_display,
            selectedBlock: selectedBlock,
            selectedUnit: selectedUnit
          });
        } else if (selectedUnit) {
          // For empresas/factories where there's no block concept
          roleMetadata.unit_id = selectedUnit.value;
          roleMetadata.unit = selectedUnit.label;
          roleMetadata.unit_display = selectedUnit.label;
          console.log(' Stored metadata (no block - empresa/factory):', {
            authorized_by_name: roleMetadata.authorized_by_name,
            unit: roleMetadata.unit
          });
        } else {
          console.warn(' No unit selected in form state');
        }
      } else {
        console.error(' No responsible person selected in form state');
      }

      if (vehiclePlate) {
        roleMetadata.vehicle_plate = vehiclePlate;
      }

      // HOW they arrived, when the operator was actually asked. Without this, a visitor with a car
      // on file who walked in was indistinguishable from one whose vehicle section was simply
      // never touched — and the log showed the car either way.
      const arrivalMode = arrivalModeFor({
        hasRegisteredVehicles: personVehicles.length > 0,
        hadAttachedVehicle: offeredPlates.length > 0,
        selectedPlate: vehiclePlate,
      });
      if (arrivalMode) {
        roleMetadata.arrival_mode = arrivalMode;
      }

      if (data.role_type === 'service_provider') {
        const selectedContractor = contractors.find(c => c.id === contractorId);
        if (selectedContractor) {
          roleMetadata.contractor_id = selectedContractor.id;
          roleMetadata.contractor_name = selectedContractor.name;
          roleMetadata.company_name = selectedContractor.name;
        } else if (companyName) {
          roleMetadata.company_name = companyName;
        }
      }

      if (data.purpose) {
        roleMetadata.purpose = data.purpose;
      }

      // Add intercom requirement
      roleMetadata.requires_intercom = requiresIntercom;

      // Map schedule to entry_window fields for service providers (backend keys)
      if (data.role_type === 'service_provider') {
        if (providerSubType === 'single_visit') {
          // Behaves exactly like Visitante — single visit access
          roleMetadata.access_type = 'single_visit';
          roleMetadata.visit_count = 0;
          roleMetadata.max_visits = 1;
        } else if (providerSubType === 'always') {
          // "Sempre Permitido" — no time restriction, sync immediately like an employee
          roleMetadata.entry_window_start = '00:00';
          roleMetadata.entry_window_end = '23:59';
          roleMetadata.allowed_days = '[0,1,2,3,4,5,6]';
          roleMetadata.no_window_restriction = true;
        } else if (providerSubType === 'custom' && schedule.type === 'custom' && schedule.time_restrictions) {
          roleMetadata.entry_window_start = schedule.time_restrictions.start_time;
          roleMetadata.entry_window_end = schedule.time_restrictions.end_time;
          if (schedule.day_restrictions?.days_of_week?.length) {
            roleMetadata.allowed_days = JSON.stringify(schedule.day_restrictions.days_of_week);
          }
        }
      }

      // CRITICAL FIX: Ensure role_type is never empty/undefined
      // This field is required for proper device sync with correct userType and validity
      const roleType = data.role_type || 'visitor';
      if (!data.role_type) {
        console.error('[VisitorRegistrationModal] role_type is missing from form data, defaulting to "visitor"');
        logger.error('visitor.modal', 'role_type missing from form data', { formData: data });
      }

      if (roleType === 'visitor') {
        if (isAlwaysEnabled) {
          roleMetadata.always_enabled = true;
          roleMetadata.access_type = 'temporary';
          roleMetadata.max_visits = 999;
          roleMetadata.visit_count = 0;
          if (isIndefinite) roleMetadata.indefinite = true;
        } else if (useCustomPeriodAccess && customPeriodUntil) {
          // Custom period: bounded, capped multi-visit access until selected date —
          // separate flag from always_enabled, no disclaimer required.
          roleMetadata.custom_period_access = true;
          roleMetadata.access_type = 'temporary';
          roleMetadata.max_visits = 999;
          roleMetadata.visit_count = 0;
        } else {
          roleMetadata.access_type = accessType;
          roleMetadata.visit_count = 0;
          roleMetadata.max_visits = accessType === 'single_visit' ? 1 : 999;
        }
      }

      // service_provider with custom period — bypasses the daily entry window/allowed
      // days/saída obrigatória entirely for the duration of the period (backend routes
      // custom_period_access roles through the visitor-style bypass, not the SP scheduler).
      if (roleType === 'service_provider' && useCustomPeriodAccess && customPeriodUntil) {
        roleMetadata.custom_period_access = true;
        roleMetadata.access_type = 'temporary';
        roleMetadata.max_visits = 999;
        roleMetadata.visit_count = 0;
      }

      // Per-weekday "grade de horário" attached to the custom period (visitor + prestador).
      // Informational for the portaria — stored + displayed, never device-enforced.
      if (useCustomPeriodAccess && customPeriodUntil && visitSchedule.length > 0) {
        roleMetadata.visit_schedule = visitSchedule;
      }

      // Where this visit is going. Empty for the overwhelming majority — one destination keeps
      // the exact payload, and the exact backend behaviour, it has always had. With several, the
      // backend builds one role per destination sharing a visit group (services/visit_destinations.go)
      // so each unidade gets its own row, liberação and revocation while the entry and the exit
      // move together.
      // Nothing without a primary: the role being created here IS the first destination, so a
      // form whose unidade picker was cleared must fall back to the ordinary single-role path
      // rather than send a list whose first entry is not the role it is attached to.
      const destinations = primaryDestination ? destinationsPayload(visitDestinations) : [];

      // Create role with is_authorized: false - we'll authorize after uploading photo
      const rolePayload = {
        ...(destinations.length > 1 ? { destinations } : {}),
        role_type: roleType, // Use validated role_type (never empty)
        valid_from: new Date().toISOString(),
        valid_until: isAlwaysEnabled
          ? (() => {
              if (isIndefinite) {
                // Hardware endTime = 1 year from now; logically "forever"
                return new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();
              }
              // Set to end of the selected day (local midnight → +23:59:59)
              const d = new Date(alwaysEnabledUntil);
              d.setHours(23, 59, 59, 999);
              return d.toISOString();
            })()
          : useCustomPeriodAccess && customPeriodUntil
            ? (() => {
                const d = new Date(customPeriodUntil);
                d.setHours(23, 59, 59, 999);
                return d.toISOString();
              })()
          : (data.valid_until ? new Date(data.valid_until).toISOString() : undefined),
        role_metadata: roleMetadata,
        is_authorized: false, // Will authorize after photo upload to trigger device sync
      };

      // Debug log to verify role_type is set correctly
      console.log('[VisitorRegistrationModal] Creating role with payload:', {
        role_type: rolePayload.role_type,
        valid_from: rolePayload.valid_from,
        valid_until: rolePayload.valid_until,
        person_name: `${data.first_name} ${data.last_name}`,
      });

      let roleResponse;
      try {
        roleResponse = await condoApi.createPersonRole(customerId, personId, rolePayload);
      } catch (createErr: any) {
        const errCode = createErr?.response?.data?.code || createErr?.code;
        if (errCode === 'PERSON_VALIDATION_002') {
          // Auto-resolve: fetch active roles, delete/checkout the conflicting one, then retry
          console.warn('[VisitorRegistrationModal] PERSON_VALIDATION_002 — auto-resolving conflicting active role');
          const rolesResp = await condoApi.getPersonRoles(customerId, personId);
          const activeRoles: any[] = rolesResp.data?.data || rolesResp.data || [];
          const conflict = activeRoles.find((r: any) => r.role_type === rolePayload.role_type);
          if (conflict) {
            const state = conflict.role_metadata?.visitor_state;
            if (state === 'inside') {
              await condoApi.manualCheckOut(customerId, personId, conflict.id);
            } else {
              await condoApi.deletePersonRole(customerId, personId, conflict.id);
            }
            roleResponse = await condoApi.createPersonRole(customerId, personId, rolePayload);
          } else {
            throw createErr;
          }
        } else {
          throw createErr;
        }
      }
      const roleId = roleResponse.data?.data?.id || roleResponse.data?.id;

      // Verify role was created with correct role_type
      const createdRole = roleResponse.data?.data || roleResponse.data;
      console.log('[VisitorRegistrationModal] Role created successfully:', {
        role_id: roleId,
        role_type: createdRole?.role_type,
        person_name: `${data.first_name} ${data.last_name}`,
      });

      if (!createdRole?.role_type) {
        console.error('[VisitorRegistrationModal] WARNING: Backend returned role without role_type!', createdRole);
        logger.error('visitor.modal', 'Backend returned role without role_type', { roleResponse: createdRole });
      }

      // Upload identity photo (ID photo for face recognition) FIRST before authorizing
      // This ensures the photo is ready when device sync is triggered
      // When blob is null but base64 is a data URL (e.g. from pre-registration), convert it to a blob
      let photoBlob = capturedPhoto?.blob || null;
      if (!photoBlob && capturedPhoto?.base64?.startsWith('data:')) {
        try {
          const res = await fetch(capturedPhoto.base64);
          photoBlob = await res.blob();
        } catch {
          // ignore conversion errors - photo upload will be skipped
        }
      }
      if (photoBlob) {
        try {
          const formData = new FormData();
          // photoBlob may have been rebuilt from capturedPhoto.base64 above, so the original
          // is taken from capturedPhoto rather than assumed to travel with the blob.
          appendIdentityPhoto(formData, { blob: photoBlob, original: capturedPhoto?.original }, 'identity_photo');
          formData.append('customer_id', customerId);

          const photoResponse = await api.post(`/people/${personId}/photos`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          // Set as primary photo for device sync
          const photoId = photoResponse.data?.data?.id || photoResponse.data?.id;
          if (photoId) {
            await api.put(`/identity-photos/${photoId}/set-primary`);
            logger.info('visitor.modal', 'Identity photo uploaded and set as primary', { personId, photoId });
          }
        } catch (photoError) {
          logger.error('visitor.modal', 'Failed to upload identity photo', photoError as Error);
          toast.error('Aviso: Não foi possível salvar a foto de identificação');
        }
      }

      await uploadDocumentsAndVehicles(personId);

      // NOW authorize the role - this triggers device sync with photo already set.
      // Skipped entirely when staff checked "aguardar aprovação via app" — the role stays
      // pending (is_authorized: false, as already created above) until the responsible
      // resident/staff approves or denies it from their app.
      if (roleId && waitForAppApproval) {
        toast.success(
          selectedResponsible
            ? `Aguardando aprovação de ${selectedResponsible.label} pelo app.`
            : 'Aguardando aprovação do responsável pelo app.'
        );
      } else if (roleId) {
        try {
          await condoApi.authorizePersonRole(customerId, personId, roleId);
          logger.info('visitor.modal', 'Role authorized', { personId, roleId, roleType });

          // Hybrid sync runs in background — modal closes immediately, toasts show progress
          // Service providers are NOT synced here — scheduler owns their device lifecycle.
          // Exceptions that sync immediately:
          //   - no_window_restriction = true ("Sempre Permitido")
          //   - providerSubType === 'single_visit' (behaves exactly like a visitor)
          const isElectron = typeof window !== 'undefined' && !!window.electronAPI;
          const isNoWindowRestriction = roleMetadata.no_window_restriction === true;
          const isProviderSingleVisit = roleType === 'service_provider' && providerSubType === 'single_visit';

          // Build a deferred sync function so entrance type picker can call it after selection.
          // willCheckIn=true → skip entrada devices (visitor already entering); false → include all.
          const _triggerSync = (willCheckIn: boolean) => {
          if (isElectron && (roleType !== 'service_provider' || isNoWindowRestriction || isProviderSingleVisit)) {
          // Fire-and-forget: do NOT await this block
          (async () => { try {
            // Fetch both expiry values from the role just created
            let accessExpiresAt: string | undefined;    // saida/passagem: now+14h
            let entryWindowExpiresAt: string | undefined; // entrada: now+10-15min
            let visitorState: string | undefined;
            let roleValidFrom: string | undefined;        // when the role was authorized
            try {
              const roleResp = await fetchWithAuth(`/api/v1/customers/${customerId}/people/${personId}/roles`);
              if (roleResp.ok) {
                const roleBody = await roleResp.json();
                const roles: any[] = roleBody.data || roleBody || [];
                const role = roles.find((r: any) => r.id === roleId);
                accessExpiresAt = role?.role_metadata?.access_expires_at || role?.valid_until;
                entryWindowExpiresAt = role?.role_metadata?.entry_window_expires_at;
                visitorState = role?.role_metadata?.visitor_state;
                roleValidFrom = role?.valid_from || undefined;
              }
            } catch {}
            // Windows come from the ONE canonical implementation (fully unit-tested)
            // instead of a third inline copy of the fallback rules.
            const roleTiming = {
              valid_from: roleValidFrom,
              valid_until: undefined as string | undefined,
              role_metadata: {
                access_expires_at: accessExpiresAt,
                entry_window_expires_at: entryWindowExpiresAt,
              },
            };
            accessExpiresAt = resolveDeviceSyncTimes(roleTiming, null).validUntil;
            entryWindowExpiresAt = resolveDeviceSyncTimes(roleTiming, 'entrada').validUntil;

            // Photo is already in memory as a data URI — no auth fetch needed
            const photoDataUri = capturedPhoto?.base64 || undefined;

            const validFrom = resolveDeviceSyncTimes(roleTiming, null).validFrom;
            const personSyncData = {
              person_id: personId,
              first_name: data.first_name,
              last_name: data.last_name,
              identity_photo_url: photoDataUri,
              profile_picture_url: photoDataUri,
              isAllowed: true,
              isVisitor: true,
              validFrom,
              validUntil: accessExpiresAt, // default (saida/passagem); entrada overridden per-device below
            };

            // Fetch zones/devices for this person (role was just authorized so zones are set)
            try {
              const zonesResp = await api.get(
                `/customers/${customerId}/people/${personId}/effective-access-zones`
              );
              const zones: any[] = zonesResp.data.zones || [];
              const deviceIdSet = new Set<string>();
              const deviceNameMap = new Map<string, string>();
              const deviceDirectionMap = new Map<string, 'entrada' | 'saida' | 'passagem'>();
              zones.forEach((zone: any) => {
                zone.devices?.forEach((d: any) => {
                  deviceIdSet.add(d.id);
                  deviceNameMap.set(d.id, d.name || d.id);
                  if (zone.entry_type && !deviceDirectionMap.has(d.id)) {
                    deviceDirectionMap.set(d.id, zone.entry_type);
                  }
                });
              });
              const deviceIds = Array.from(deviceIdSet);

              if (deviceIds.length > 0) {
                const deviceDetails = await Promise.all(
                  deviceIds.map(async (deviceId: string): Promise<DeviceInfo | null> => {
                    try {
                      const res = await api.get(`/iot-devices/${deviceId}`);
                      const d = res.data;
                      const mfr = d.manufacturer?.toLowerCase();
                      const vendor: 'hikvision' | 'controlid' | 'rfid' =
                        mfr === 'controlid' ? 'controlid' :
                        mfr === 'hikvision' ? 'hikvision' :
                        ({ facial_recognition: 'hikvision', rfid_reader: 'rfid', access_controller: 'controlid', controlid_face: 'controlid', controlid_rfid: 'controlid' } as any)[d.device_type] || 'hikvision';
                      return { id: d.id, ip_address: d.ip_address, local_ip_address: d.local_ip_address, username: d.username || '', password: d.password || '', vendor, port: d.port, direction: deviceDirectionMap.get(deviceId) };
                    } catch { return null; }
                  })
                );
                const allDevices = deviceDetails.filter((d): d is DeviceInfo => d !== null);
                // Skip entrada devices when visitor is already inside or will be auto-checked-in
                const validDevices = (willCheckIn || visitorState === 'inside')
                  ? allDevices.filter(d => d.direction !== 'entrada')
                  : allDevices;

                if (validDevices.length > 0) {
                  const toastId = `visitor-sync-${personId}`;
                  const total = validDevices.length;
                  const successfulDevices: { device_id: string; device_name: string; source?: string }[] = [];
                  const failedDevices: { device_id: string; device_name: string; error: string }[] = [];

                  // One surface for every sync; parallel fan-out, so pause/stop are not offered.
                  const reporter = makeSyncReporter({
                    subject: {
                      id: personId,
                      name: `${data.first_name || ''} ${data.last_name || ''}`.trim() || 'Visitante',
                      photoUrl: photoDataUri,
                    },
                    total,
                    controls: false,
                  });
                  reporter.start();

                  await Promise.all(validDevices.map(async (deviceInfo) => {
                    const deviceName = deviceNameMap.get(deviceInfo.id) || deviceInfo.id;
                    // entrada: short entry window; saida/passagem: full access window
                    const deviceSyncData = deviceInfo.direction === 'entrada'
                      ? { ...personSyncData, validUntil: entryWindowExpiresAt }
                      : personSyncData;
                    // Executes, records the outcome in BOTH the audit trail and the
                    // reconciliation ledger, and classifies. See utils/syncDeviceAndReport.
                    const outcome = await syncDeviceAndReport({
                      customerId,
                      personId,
                      device: deviceInfo,
                      payload: deviceSyncData,
                      deviceName,
                    });

                    if (outcome.synced) {
                      successfulDevices.push(outcome.synced);
                      reporter.record(deviceName, 'ok');
                    } else {
                      failedDevices.push(outcome.failed);
                      reporter.record(deviceName, 'failed', outcome.failed.error);
                    }
                  }));

                  reporter.finish();

                  // One toast per distinct CAUSE (not per device) — see utils/deviceSyncError.
                  for (const group of groupDeviceFailures(failedDevices)) {
                    logger.warn('visitor.modal', 'device failures', {
                      devices: group.deviceNames.join(', '),
                      message: group.message,
                    });
                    toast.error(deviceFailureToast(group), { duration: 7000 });
                  }

                  if (successfulDevices.length > 0) {
                    const localCount = successfulDevices.filter(d => d.source === 'local').length;
                    const publicCount = successfulDevices.filter(d => d.source === 'public').length;
                    const cloudCount = successfulDevices.filter(d => d.source === 'cloud').length;
                    const modeBreakdown: string[] = [];
                    if (localCount > 0) modeBreakdown.push(`${localCount} local`);
                    if (publicCount > 0) modeBreakdown.push(`${publicCount} público`);
                    if (cloudCount > 0) modeBreakdown.push(`${cloudCount} nuvem`);
                    const visitorSyncMessage = formatSyncSuccess(
                      successfulDevices.map(d => d.device_name),
                      modeBreakdown,
                      successfulDevices.length + failedDevices.length,
                    );
                    // No summary toast — the pinned card already carries the verdict.
                  }
                }
              }
            } catch (hybridErr) {
              // ARCHITECTURE RULE: in Electron, device sync is executed locally only —
              // never delegated to the cloud. Surface the failure instead of hiding it
              // behind a silent cloud attempt.
              logger.warn('visitor.modal', 'Hybrid sync failed', { error: String(hybridErr) });
              toast.error('Falha ao sincronizar com os dispositivos localmente — verifique a conexão com os equipamentos', { duration: 6000 });
            }
          } catch { /* swallow — background sync */ } })();
          } else if (!isElectron) {
            // Non-Electron only: cloud sync (fire-and-forget). In Electron, scheduled
            // service providers get NO client-initiated sync here — the scheduler owns
            // their device lifecycle, and the cloud must not be asked to reach devices
            // on Electron's behalf.
            api.post(`/customers/${customerId}/people/${personId}/sync`)
              .then(() => logger.info('visitor.modal', 'Visitor auto-synced to devices', { personId }))
              .catch(err => {
                logger.error('visitor.modal', 'Auto-sync failed (non-critical)', err as Error);
              });
          }
          }; // end _triggerSync

          // If entrance types are configured for visitors/single-visit prestadores, defer sync until type is chosen.
          // Otherwise fire immediately (existing behavior for non-visitor or no entrance types).
          const showEntrancePicker = visitorEntranceTypes.length > 0 &&
            (data.role_type === 'visitor' || (data.role_type === 'service_provider' && providerSubType === 'single_visit'));
          if (showEntrancePicker) {
            pendingSyncRef.current = _triggerSync;
          } else {
            _triggerSync(autoGiveEntrada);
          }
        } catch (authError) {
          logger.error('visitor.modal', 'Failed to authorize role', authError as Error);
          toast('Visitante criado, mas sincronização com dispositivos pode estar pendente');
        }
      }

      toast.success(
        existingPerson
          ? `Acesso ${data.role_type === 'visitor' ? 'de visitante' : 'de prestador'} adicionado com sucesso!`
          : data.role_type === 'visitor'
          ? 'Visitante cadastrado com sucesso!'
          : 'Prestador de serviço cadastrado com sucesso!'
      );

      // Entrance type picker: show post-registration type selection
      // IMPORTANT: do NOT call onSuccess()/onClose() here — onSuccess in OperationalView
      // navigates away and unmounts the modal, destroying the portal overlay.
      const showEntrancePickerAfter = visitorEntranceTypes.length > 0 && roleId &&
        (data.role_type === 'visitor' || (data.role_type === 'service_provider' && providerSubType === 'single_visit'));
      if (showEntrancePickerAfter) {
        const personName = existingPerson
          ? `${existingPerson.first_name} ${existingPerson.last_name}`.trim()
          : ([data.first_name, data.last_name].filter(Boolean).join(' ') || fullName.trim());
        setPickerCountdown(PICKER_COUNTDOWN_SECONDS);
        pickerCountdownAutoConfirmedRef.current = false;
        setEntranceTypePickerData({
          personName,
          unitLabel: selectedUnit?.label ?? '',
          personId,
          roleId,
        });
        return; // stay mounted so portals can render
      }

      // Fallback: legacy auto-give entrada toggle (no entrance types configured)
      const isSingleVisitType = data.role_type === 'visitor' || (data.role_type === 'service_provider' && providerSubType === 'single_visit');
      if (autoGiveEntrada && isSingleVisitType && roleId) {
        try {
          await condoApi.manualCheckIn(customerId, personId, roleId);
          toast.success('Entrada registrada automaticamente!');
          logger.info('visitor.modal', 'Auto check-in successful', { personId, roleId });
        } catch (checkInError) {
          logger.error('visitor.modal', 'Auto check-in failed', checkInError as Error);
        }
      }

      // Close modal — sync continues in background via toasts
      onSuccess?.();
      onClose();
    } catch (error: any) {
      logger.error('visitor.modal', 'Failed to save visitor', error as Error);
      toast.error(error.response?.data?.error || 'Falha ao cadastrar');
    }
  };

  // Pré-Cadastro: create person + visitor role (awaiting_entry) if a unit is selected
  const handlePreCadastroSubmit = async () => {
    if (isActuallySubmitting) return;
    setIsActuallySubmitting(true);
    try {
      let personId: string;
      let personName: string;

      if (existingPerson) {
        personId = existingPerson.id;
        personName = `${existingPerson.first_name} ${existingPerson.last_name}`;
        toast.success(`${personName} já está cadastrado(a) no sistema`);
        onSuccess?.();
        onClose();
        return;
      }

      const data = getValues();
      const firstName = (data.first_name || '').trim();
      const lastName = (data.last_name || '').trim();

      // The shared rule, not a presence check: "a a" has both halves and is still not a name,
      // and this one goes on the gate screen. Same sentence every other name field uses.
      const nameProblem = validateFullName(`${firstName} ${lastName}`);
      if (nameProblem) {
        toast.error(nameProblem);
        return;
      }

      personName = `${firstName} ${lastName}`;
      const personPayload: any = {
        first_name: firstName,
        last_name: lastName,
        cpf: data.cpf?.trim() || undefined,
        phone: data.phone?.trim() || undefined,
        rg: (data as any).rg?.trim() || undefined,
        email: (data as any).email?.trim() || undefined,
        birth_date: (() => {
          const bd = birthDate;
          if (!bd || bd.length !== 10) return undefined;
          const [dd, mm, yyyy] = bd.split('/');
          if (!dd || !mm || !yyyy || yyyy.length !== 4) return undefined;
          return `${yyyy}-${mm}-${dd}T00:00:00Z`;
        })(),
        is_minor: isUnder18,
        ...foreignerPayload(data),
        is_active: true,
        extra_data: data.extra_data && Object.keys(data.extra_data).length > 0 ? data.extra_data : undefined,
      };

      console.log('[Pré-Cadastro] Creating person:', personPayload, 'for customer:', customerId);
      const personResponse = await condoApi.createPerson(customerId, personPayload);
      console.log('[Pré-Cadastro] Response:', personResponse?.data);

      const createdPerson = personResponse?.data?.data;
      if (!createdPerson?.id) throw new Error('Resposta inválida do servidor');
      personId = createdPerson.id;

      // Upload identity photo if captured
      let photoBlob = capturedPhoto?.blob || null;
      if (!photoBlob && capturedPhoto?.base64?.startsWith('data:')) {
        try {
          const res = await fetch(capturedPhoto.base64);
          photoBlob = await res.blob();
        } catch { /* ignore */ }
      }
      if (photoBlob) {
        try {
          const formData = new FormData();
          // See the sibling call above: the original comes from capturedPhoto, not photoBlob.
          appendIdentityPhoto(formData, { blob: photoBlob, original: capturedPhoto?.original }, 'identity_photo');
          formData.append('customer_id', customerId);
          const photoResponse = await api.post(`/people/${personId}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const photoId = photoResponse.data?.data?.id || photoResponse.data?.id;
          if (photoId) await api.put(`/identity-photos/${photoId}/set-primary`);
        } catch {
          toast.error('Aviso: Não foi possível salvar a foto de identificação');
        }
      }

      // Document front/back and vehicle photos + vehicle records — same as full registration
      await uploadDocumentsAndVehicles(personId);

      // The role is created ALWAYS, with or without a unit.
      //
      // It used to be created only `if (selectedUnit?.value)`. Without a unit the person row was
      // written and no role with it — and every visitor list does
      // `INNER JOIN person_roles ... WHERE role_type = ?`, so a person with zero roles cannot
      // appear anywhere. The operator got "Pré-cadastro realizado!" for somebody who could then
      // never be found, searched for, or granted access.
      //
      // is_authorized: false → skips initializeVisitorStateMachine (no 15-min entry window timer).
      // visitor_state: 'pre_registered' → excluded from the active-visit list, not from search.
      let roleCreated = false;
      try {
        await condoApi.createPersonRole(
          customerId,
          personId,
          buildPreRegistrationRolePayload({
            roleType: getValues().role_type || 'visitor',
            unit: selectedUnit?.value ? { value: selectedUnit.value, label: selectedUnit.label } : undefined,
            block: selectedBlock?.label,
            responsible: selectedResponsible?.value
              ? { value: selectedResponsible.value, label: selectedResponsible.label }
              : undefined,
          }),
        );
        roleCreated = true;
      } catch (roleError: any) {
        // Previously swallowed. A failure here leaves exactly the orphan this change exists to
        // prevent, so it must never be reported as an unqualified success.
        console.error('[Pré-Cadastro] Failed to create role:', roleError);
      }

      if (roleCreated) {
        toast.success(`Pré-cadastro de ${personName} realizado!`, { duration: 5000 });
      } else {
        toast.error(
          `${personName} foi cadastrado, mas não aparecerá na lista — a função não pôde ser criada. Edite a pessoa para concluir.`,
          { duration: 8000 },
        );
      }
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('[Pré-Cadastro] Error:', error);

      // 409 = CPF already registered — fetch the existing person and load them into the form
      if (error?.response?.status === 409) {
        const existingId = error?.response?.data?.metadata?.existing_id;
        if (existingId) {
          try {
            const res = await fetchWithAuth(`/api/v1/customers/${customerId}/people/${existingId}?include_photos=true&include_roles=true`);
            if (res.ok) {
              const personData = await res.json();
              const person = personData.data || personData;
              toast('Pessoa já cadastrada — selecione para continuar', { duration: 4000 });
              // Load person into form, same as if they clicked from search results
              setExistingPerson(person);
              setValue('first_name', person.first_name);
              setValue('last_name', person.last_name);
              setValue('cpf', person.cpf || '');
              setValue('phone', person.phone || '');
              if (person.identity_photo?.photo_url || person.profile_picture_url) {
                setCapturedPhoto({
                  base64: person.identity_photo?.photo_url || person.profile_picture_url,
                  blob: null as any, width: 0, height: 0, faceDetected: false,
                });
              }
              setCurrentStep('form');
              return;
            }
          } catch { /* fall through to generic error */ }
        }
        toast.error('CPF já cadastrado no sistema');
        return;
      }

      const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Falha ao realizar pré-cadastro';
      toast.error(msg);
    } finally {
      setIsActuallySubmitting(false);
    }
  };

  // Renders one field from the admin's registration configuration.
  //
  // Component scope on purpose: BOTH registration forms on this screen need it. It used to live
  // inside the new-visitor branch, so "Cadastrar Acesso" (the same screen, for a person already
  // registered) had no way to reach it and simply hardcoded three fields — which is why RG, Data
  // de Nascimento and every custom field were missing there.
  const defaultVisitorConfig = {
    standard_fields: {
      phone: { enabled: true, required: false, order: 0 },
      date_of_birth: { enabled: true, required: false, order: 1 },
      cpf: { enabled: true, required: true, order: 2 },
      email: { enabled: true, required: false },
      rg: { enabled: true, required: false },
    }
  };
  const effectiveConfig = fieldConfig ?? defaultVisitorConfig;

  const renderConfigurableVisitorField = (key: string, isCustom: boolean, colSpan: 1 | 2) => {
    const cls = colSpan === 2 ? 'col-span-2' : undefined;
    if (!isCustom) {
      switch (key) {
        case 'phone': {
          const phoneRequired = effectiveConfig.standard_fields?.phone?.required ?? false;
          return (
            <div key="phone" className={cls}>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Telefone{phoneRequired && <span className="text-red-500"> *</span>}
              </label>
              <div className="flex gap-2 mt-1">
                <input
                  {...register('phone', { required: phoneRequired ? 'Telefone é obrigatório' : false })}
                  type="tel"
                  placeholder={isForeigner ? '+58 412 1234567' : '(00) 00000-0000'}
                  className={`flex-1 block rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                    darkMode
                      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                      : 'border border-gray-300'
                  }`}
                  onChange={(e) => {
                    // The Brazilian mask slices the first two digits into a DDD, so a foreign
                    // number comes out as "(58) 41212-34567" — undialable. The leading "+" is
                    // also what stops condo-manager masking it again on the way back out.
                    if (isForeigner) {
                      setValue('phone', formatForeignPhone(e.target.value), { shouldValidate: false });
                      return;
                    }
                    // Shared helper: the inline version here split 5+4
                    // unconditionally, so a 10-digit landline rendered
                    // "(12) 34567-890" instead of "(12) 3456-7890".
                    setValue('phone', maskPhoneInput(e.target.value), { shouldValidate: false });
                  }}
                />
                {phone && phone.replace(/\D/g, '').length >= 10 && (
                  <button
                    type="button"
                    onClick={() => {
                      const formattedPhone = formatPhoneForWhatsApp(phone);
                      window.open(`https://wa.me/${formattedPhone}`, '_blank');
                    }}
                    className={`flex-shrink-0 px-3 py-2 rounded-md transition-colors flex items-center gap-1 ${
                      darkMode
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}
                    title="Abrir WhatsApp"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          );
        }
        case 'date_of_birth': {
          const dobRequired = effectiveConfig.standard_fields?.date_of_birth?.required ?? false;
          return (
            <div key="date_of_birth" className={cls}>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Data de Nascimento{dobRequired && <span className="text-red-500"> *</span>}
              </label>
              <input
                type="text"
                placeholder="DD/MM/AAAA"
                value={birthDate}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
                  let masked = digits;
                  if (digits.length > 2) masked = digits.slice(0, 2) + '/' + digits.slice(2);
                  if (digits.length > 4) masked = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
                  setBirthDate(masked);
                }}
                maxLength={10}
                className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  darkMode
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                    : 'border border-gray-300'
                }`}
              />
              <label className="flex items-center cursor-pointer mt-1.5">
                <input
                  type="checkbox"
                  checked={isUnder18}
                  onChange={(e) => setIsUnder18(e.target.checked)}
                  className="h-3 w-3 rounded border-gray-600 text-blue-600 focus:ring-blue-500 mr-1.5"
                />
                <span className="text-xs text-gray-400">Menor de 18 anos</span>
              </label>
            </div>
          );
        }
        case 'cpf':
          if (compactMode) return null;
          return (
            <div key="cpf" className={cls}>
              {isAdmin && (
                <div className="mb-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allowWithoutCPF}
                      onChange={(e) => {
                        setAllowWithoutCPF(e.target.checked);
                        setTimeout(() => trigger('cpf'), 0);
                      }}
                      className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Permitir cadastro sem CPF
                    </span>
                  </label>
                  {allowWithoutCPF && (
                    <div className={`mt-2 p-2 rounded-md ${darkMode ? 'bg-amber-900/30 border border-amber-700' : 'bg-amber-50 border border-amber-200'}`}>
                      <div className="flex items-start">
                        <AlertTriangle className={`h-4 w-4 mt-0.5 mr-2 flex-shrink-0 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                        <p className={`text-xs ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                          <strong>Aviso:</strong> Cadastrar pessoa sem CPF pode dificultar a identificação futura e impedir a verificação de duplicatas. Use apenas quando absolutamente necessário.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                CPF {(effectiveConfig.standard_fields?.cpf?.required ?? true) && !(isAdmin && allowWithoutCPF) && '*'}
              </label>
              <input
                {...register('cpf', {
                  validate: (value) => {
                    if (isAdmin && allowWithoutCPF) return true;
                    const cpfReq = effectiveConfig.standard_fields?.cpf?.required ?? true;
                    if (cpfReq && (!value || value.trim() === '')) return 'CPF é obrigatório';
                    return true;
                  }
                })}
                type="text"
                placeholder="000.000.000-00"
                onChange={(e) => {
                  const formatted = formatCPF(e.target.value);
                  setValue('cpf', formatted);
                  trigger('cpf');
                }}
                className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  darkMode
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                    : 'border border-gray-300'
                }`}
              />
              {errors.cpf && (
                <p className="mt-1 text-xs text-red-500">{String(errors.cpf.message || "")}</p>
              )}
              {ocrMismatches.cpf && (
                <div className={`flex items-center mt-1 text-xs ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                  <AlertTriangle className="h-3 w-3 mr-1 flex-shrink-0" />
                  <span>Documento: <strong>{ocrMismatches.cpf}</strong></span>
                </div>
              )}
              {cpfCheckStatus === 'checking' && (
                <div className={`flex items-center mt-1 text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                  <Loader2 className="h-3 w-3 mr-1 flex-shrink-0 animate-spin" />
                  <span>Verificando CPF...</span>
                </div>
              )}
              {cpfCheckStatus === 'valid' && (
                <div className={`flex items-center mt-1 text-xs ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                  <CheckCircle2 className="h-3 w-3 mr-1 flex-shrink-0" />
                  <span>CPF válido e disponível</span>
                </div>
              )}
              {cpfCheckStatus === 'invalid' && (
                <div className={`flex items-center mt-1 text-xs ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                  <AlertCircle className="h-3 w-3 mr-1 flex-shrink-0" />
                  <span>CPF inválido</span>
                </div>
              )}
            </div>
          );
        case 'email':
          return (
            <div key="email" className={cls}>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email</label>
              <input
                {...register('email')}
                type="email"
                placeholder="email@exemplo.com"
                className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  darkMode
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                    : 'border border-gray-300'
                }`}
              />
            </div>
          );
        case 'rg': {
          const rgRequired = effectiveConfig.standard_fields?.rg?.required ?? false;
          return (
            <div key="rg" className={cls}>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                RG{rgRequired && <span className="text-red-500"> *</span>}
              </label>
              <input
                {...register('rg', { required: rgRequired ? 'RG é obrigatório' : false })}
                type="text"
                placeholder="RG"
                className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  darkMode
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                    : 'border border-gray-300'
                }`}
              />
            </div>
          );
        }
        default:
          return null;
      }
    } else {
      // Same config the key came from — `configurableFieldsFor(effectiveConfig)`
      // produced it, so looking it up anywhere else can only lose it.
      const customField = effectiveConfig?.custom_fields?.find(f => f.id === key);
      if (!customField) return null;
      return (
        <div key={customField.id} className={cls}>
          <label className={cn("block text-sm font-medium mb-1", darkMode ? 'text-gray-300' : 'text-gray-700')}>
            {customField.label}{customField.required && <span className="text-red-500"> *</span>}
          </label>
          {customField.type === 'select' ? (
            <select
              {...register(`extra_data.${customField.id}`, { required: customField.required ? `${customField.label} é obrigatório` : false })}
              className={cn("mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm", darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border border-gray-300')}
            >
              <option value="">Selecione…</option>
              {customField.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          ) : customField.type === 'textarea' ? (
            <textarea
              {...register(`extra_data.${customField.id}`, { required: customField.required ? `${customField.label} é obrigatório` : false })}
              rows={2}
              className={cn("mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm", darkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'border border-gray-300')}
            />
          ) : customField.type === 'date' ? (
            <>
            <input
              type="text"
              placeholder="DD/MM/AAAA"
              {...register(`extra_data.${customField.id}`, { required: customField.required ? `${customField.label} é obrigatório` : false })}
              value={watch(`extra_data.${customField.id}`) || ''}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
                let masked = digits;
                if (digits.length > 2) masked = digits.slice(0, 2) + '/' + digits.slice(2);
                if (digits.length > 4) masked = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
                setValue(`extra_data.${customField.id}`, masked, { shouldValidate: true });
              }}
              maxLength={10}
              className={cn("mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm", darkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'border border-gray-300')}
            />
            {/* A CNH expiry is the one date on this form that means something right now —
                advisory, never blocking: driving in on an expired licence is the
                condomínio's call, not the form's. */}
            {isCnhValidadeField(customField.label) && (
              <CnhValidadeHint value={watch(`extra_data.${customField.id}`)} darkMode={darkMode} />
            )}
            </>
          ) : customField.type === 'phone' ? (
            <input
              type="tel"
              placeholder="(00) 00000-0000"
              {...register(`extra_data.${customField.id}`, { required: customField.required ? `${customField.label} é obrigatório` : false })}
              value={watch(`extra_data.${customField.id}`) || ''}
              onChange={(e) => {
                const d = e.target.value.replace(/\D/g, '').slice(0, 11);
                let m = d;
                if (d.length > 2) m = `(${d.slice(0, 2)}) ${d.slice(2)}`;
                if (d.length > 7) m = `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
                setValue(`extra_data.${customField.id}`, m, { shouldValidate: true });
              }}
              maxLength={16}
              className={cn("mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm", darkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'border border-gray-300')}
            />
          ) : (
            <input
              type={customField.type}
              {...register(`extra_data.${customField.id}`, { required: customField.required ? `${customField.label} é obrigatório` : false })}
              className={cn("mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm", darkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'border border-gray-300')}
            />
          )}
        </div>
      );
    }
  };

  if (!embedded && !open) return null;

  // Modal content component (reused in both modal and embedded modes)
  const modalContent = (
    <div className={`${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white'} rounded-lg w-full ${
      embedded ? 'h-full flex' : 'max-h-[90vh]'
    } flex flex-col ${
      !embedded && (currentStep === 'search' ? 'max-w-2xl' : 'max-w-[88rem]')
    }`}>
          <div className={`flex-shrink-0 px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
            <div className="flex items-center gap-3">
              {embedded && (
                <button
                  onClick={onClose}
                  className={`p-1.5 hover:bg-gray-800 rounded-lg transition-colors`}
                  title="Voltar"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-400" />
                </button>
              )}
              <div className="flex items-baseline gap-2 flex-wrap">
                <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {currentStep === 'search' && 'Buscar Visitante'}
                  {currentStep === 'form' && (existingPerson ? 'Cadastrar Acesso' : 'Novo Visitante')}
                </h3>
                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {currentStep === 'search' && '· Busque por CPF ou nome para verificar se já está cadastrado'}
                  {currentStep === 'form' && existingPerson && (
                    <>
                      {/* Reads the live draft, not the stored record: the name and CPF are editable
                          below, and a header that disagreed with the field the operator is typing in
                          looks exactly like a bug. */}
                      · <span className={darkMode ? 'text-blue-400' : 'text-blue-600'}>{fullName || `${existingPerson.first_name || ''} ${existingPerson.last_name || ''}`.trim()}</span>
                      {(cpf || existingPerson.cpf) && (
                        <> · <span className={darkMode ? 'text-amber-400 font-medium' : 'text-amber-600 font-medium'}>CPF: {isAdmin ? formatCPF(cpf || existingPerson.cpf || '') : maskCPF(cpf || existingPerson.cpf || '', roles || [])}</span></>
                      )}
                      {(phone || existingPerson.phone) && (
                        <> · <Phone className="h-3 w-3 inline-block mb-0.5" /> <span className={darkMode ? 'text-green-400 font-medium' : 'text-green-700 font-medium'}>{phone || existingPerson.phone}</span></>
                      )}
                    </>
                  )}
                  {currentStep === 'form' && !existingPerson && '· Preencha os dados para cadastrar um novo visitante'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {currentStep === 'form' && existingPerson && (
                <button
                  onClick={() => {
                    onClose();
                    setTimeout(() => openPersonDetail(existingPerson.id, false, darkMode), 150);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  title="Abrir perfil completo da pessoa"
                >
                  <ExternalLink className="h-4 w-4" />
                  Ver Perfil
                </button>
              )}
              {!embedded && (
                <button onClick={onClose} className={`${darkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'}`}>
                  <X className="h-6 w-6" />
                </button>
              )}
            </div>
          </div>

          {/* Checking visitor state loading overlay */}
          {isCheckingVisitorState && (
            <div className="flex-1 flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Verificando...</p>
              </div>
            </div>
          )}

          {/* Scrollable Content Area */}
          <div className={`min-h-0 ${isCheckingVisitorState ? 'hidden' : ''} ${
            currentStep === 'search' ? 'overflow-y-auto custom-scrollbar' : 'flex-1 flex flex-col'
          }`}>
            {/* Step 1: Search */}
            {currentStep === 'search' && (
            <div className="px-6 py-6">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Buscar por CPF ou Nome
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className={`h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                    <input
                      type="text"
                      value={searchInput}
                      // A keystroke must never remove text the operator can see — see searchInputMask.
                      onChange={(e) => setSearchInput(maskSearchValue(e.target.value))}
                      placeholder="Digite CPF ou nome do visitante..."
                      className={`block w-full pl-10 pr-4 rounded-lg shadow-sm py-3 text-base focus:outline-none focus:ring-2 ${
                        darkMode
                          ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-primary-500 focus:ring-primary-500'
                          : 'border border-gray-300 text-gray-900 bg-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500'
                      }`}
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          // If search results exist, select the first one
                          if (searchResults.length > 0) {
                            handleSelectVisitor(searchResults[0]);
                          } else if (hasSearched && searchResults.length === 0) {
                            // If searched and no results, create new visitor
                            handleCreateNew();
                          } else {
                            // Otherwise, trigger search
                            handleVisitorSearch();
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleVisitorSearch}
                    disabled={searchInput.trim().length < 3 || isSearching}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white disabled:cursor-not-allowed ${
                      darkMode
                        ? 'bg-primary-600 hover:bg-primary-700 disabled:bg-gray-700 disabled:text-gray-500'
                        : 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400'
                    }`}
                  >
                    {isSearching ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Buscando...</span>
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4" />
                        <span>Buscar</span>
                      </>
                    )}
                  </button>
                </div>

                {/* New Visitor Button */}
                <button
                  type="button"
                  onClick={handleCreateNew}
                  className={`w-full mt-4 group relative overflow-hidden rounded-xl border-2 border-dashed transition-all duration-300 p-6 ${
                    darkMode
                      ? 'border-gray-600 hover:border-green-500 bg-gray-800 hover:bg-gray-800/80'
                      : 'border-gray-300 hover:border-primary-400 bg-gradient-to-br from-gray-50 to-white hover:from-primary-50 hover:to-white'
                  }`}
                >
                  <div className="flex items-center justify-center gap-4">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Plus className="h-7 w-7 text-white" />
                    </div>
                    <div className="text-left">
                      <span className={`block text-lg font-semibold transition-colors ${
                        darkMode ? 'text-white group-hover:text-green-400' : 'text-gray-900 group-hover:text-primary-700'
                      }`}>
                        Cadastrar novo visitante
                      </span>
                      <span className={`block text-sm transition-colors ${
                        darkMode ? 'text-gray-400 group-hover:text-green-500' : 'text-gray-500 group-hover:text-primary-600'
                      }`}>
                        Clique aqui para criar um novo cadastro
                      </span>
                    </div>
                    <ArrowRight className={`h-5 w-5 group-hover:translate-x-1 transition-all duration-300 ml-auto ${
                      darkMode ? 'text-gray-500 group-hover:text-green-400' : 'text-gray-400 group-hover:text-primary-500'
                    }`} />
                  </div>
                </button>

                {/* Search Results */}
                {hasSearched && (
                  <div className="mt-4">
                    {searchResults.length > 0 ? (
                      <div className={`border rounded-lg ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className={`px-4 py-2 border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                          <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {searchResults.length} visitante{searchResults.length > 1 ? 's' : ''} encontrado{searchResults.length > 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className={`max-h-64 overflow-y-auto ${darkMode ? '' : ''}`}>
                          {searchResults.map((visitor, index) => {
                            // DEBUG: Log photo availability for first result
                            if (index === 0) {
                              console.log(' Rendering first visitor:', {
                                name: `${visitor.first_name} ${visitor.last_name}`,
                                hasIdentityPhoto: !!visitor.identity_photo_url,
                                hasProfilePicture: !!visitor.profile_picture_url,
                                identity_photo_url: visitor.identity_photo_url,
                                profile_picture_url: visitor.profile_picture_url,
                              });
                            }

                            const photoUrl = visitor.identity_photo_url || visitor.profile_picture_url;
                            const hasPhoto = !!photoUrl;

                            return (
                              <button
                                key={visitor.id}
                                ref={index === 0 ? firstResultRef : null}
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log(' CLICKED on visitor:', visitor.first_name, visitor.last_name);
                                  handleSelectVisitor(visitor);
                                }}
                                className={`w-full px-4 py-3 flex items-center gap-3 transition-all text-left ${
                                  index > 0 ? (darkMode ? 'border-t border-gray-700' : 'border-t border-gray-200') : ''
                                } ${
                                  index === 0
                                    ? 'bg-gradient-to-r from-blue-600/50 to-blue-700/50 shadow-2xl ring-4 ring-blue-400 border-2 border-blue-300'
                                    : darkMode
                                      ? 'hover:bg-gray-800'
                                      : 'hover:bg-gray-50'
                                }`}
                              >
                                {/* Person Photo - Larger and more prominent */}
                                {hasPhoto ? (
                                  <img
                                    src={getAbsoluteImageUrl(photoUrl)}
                                    alt={`${visitor.first_name} ${visitor.last_name}`}
                                    className={`w-11 h-14 rounded-md object-cover ring-2 flex-shrink-0 shadow-md ${darkMode ? 'ring-gray-600' : 'ring-gray-300'}`}
                                    onError={(e) => {
                                      // Fallback to placeholder if image fails to load
                                      const target = e.target as HTMLImageElement;
                                      console.warn(' Image failed to load:', photoUrl);
                                      target.style.display = 'none';
                                      target.nextElementSibling?.classList.remove('hidden');
                                    }}
                                    onLoad={() => {
                                      if (index === 0) {
                                        console.log(' First visitor photo loaded successfully');
                                      }
                                    }}
                                  />
                                ) : null}
                                {/* Fallback placeholder - shown if no photo or image fails */}
                                <div
                                  className={`w-11 h-14 rounded-md flex items-center justify-center flex-shrink-0 shadow-md ${
                                    hasPhoto ? 'hidden' : ''
                                  } ${darkMode ? 'bg-gray-700 ring-2 ring-gray-600' : 'bg-gray-200 ring-2 ring-gray-300'}`}
                                >
                                  <User className={`h-7 w-7 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                                </div>

                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {visitor.first_name} {visitor.last_name}
                                  </p>
                                  <p className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {visitor.cpf && `CPF: ${visitor.cpf}`}
                                    {visitor.cpf && visitor.phone && ' • '}
                                    {visitor.phone && `Tel: ${visitor.phone}`}
                                  </p>
                                </div>
                                {index === 0 && (
                                  <kbd className="inline-flex items-center px-2 py-1 text-xs font-bold bg-green-600 text-white rounded border border-green-500 shadow-lg mr-2 animate-pulse">
                                    ENTER
                                  </kbd>
                                )}
                                <ArrowRight className={`h-4 w-4 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className={`text-center py-8 rounded-xl border ${
                        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gradient-to-br from-gray-50 to-white border-gray-200'
                      }`}>
                        <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                          <UserCheck className={`h-8 w-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        </div>
                        <p className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Nenhum visitante encontrado</p>
                        <p className={`text-sm mt-1 mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {checkCPFFormat(searchInput)
                            ? 'Este CPF não está cadastrado no sistema'
                            : 'Nenhum visitante encontrado com este nome'}
                        </p>
                        <button
                          type="button"
                          onClick={handleCreateNew}
                          className="group inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 rounded-xl text-white font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300"
                        >
                          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Plus className="h-5 w-5" />
                          </div>
                          <span className="text-base">Cadastrar novo visitante</span>
                          <kbd className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold bg-blue-500 text-white rounded border border-blue-400 shadow-sm">
                            ENTER
                          </kbd>
                          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          )}

          {/* Step 2: Unified Form with Photo + All Fields (MATCHING Visitors.tsx EXACTLY) */}
          {currentStep === 'form' && (
            <form
              id="visitor-form"
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col flex-1 min-h-0"
            >
              {/* Pre-Registration Banner */}
              {preRegistrationComment && (
                <div className={`mx-6 mt-4 mb-2 p-3 rounded-lg border-l-4 ${
                  darkMode
                    ? 'bg-blue-950 border-blue-500 text-blue-200'
                    : 'bg-blue-50 border-blue-500 text-blue-800'
                }`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-semibold mb-1 ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                        Pré-Cadastro via QR Code
                      </p>
                      <p className={`text-xs ${darkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                        <span className="font-medium">Comentário:</span> "{preRegistrationComment}"
                      </p>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                         Dados pré-preenchidos pelo visitante. Valide e complete o cadastro.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-1 min-h-0">
                {/* Settings Sidebar - Camera monitoring, toggles and previous visit on the left */}
                <div className={`w-[26rem] flex-shrink-0 border-r flex flex-col gap-2 overflow-hidden ${
                  darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}>
                  {/* Top: Monitoramento Camera Stream */}
                  <div className={`flex-shrink-0 ${darkMode ? 'bg-gray-950' : 'bg-gray-100 border-b border-gray-200'}`}>
                    {/* Header row */}
                    <div className="flex items-center justify-between px-2.5 pt-2 pb-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                        <p className={`text-[10px] font-semibold uppercase tracking-widest ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Monitoramento</p>
                      </div>
                      {/* Refresh button — always visible when a camera is selected */}
                      {selectedIPCamera && !isLoadingCameras && (
                        <button
                          type="button"
                          onClick={async () => {
                            await stopLivePreview();
                            setTimeout(() => startCameraPreview(selectedIPCamera), 120);
                          }}
                          className={`p-1 rounded-md transition-all hover:scale-110 ${darkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-500 hover:text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-500 hover:text-gray-700'}`}
                          title="Reconectar câmera"
                          tabIndex={-1}
                          data-no-tab="true"
                        >
                          <RefreshCw className="h-3 w-3" />
                        </button>
                      )}
                    </div>

                    {/* Video area */}
                    <div className="px-2 pb-0">
                    <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-black">
                      {isLoadingCameras ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Loader2 className="h-8 w-8 text-white animate-spin" />
                        </div>
                      ) : rtspPreviewUrl ? (
                        /* RTSP stream: frames via Electron IPC as base64 */
                        <>
                          <img
                            key={rtspPreviewUrl}
                            src={rtspPreviewUrl}
                            alt="Camera visitante"
                            className="w-full h-full object-cover"
                          />
                          {isStreamReconnecting ? (
                            <div className="absolute top-1.5 left-1.5 flex items-center gap-1 bg-yellow-600/90 backdrop-blur-sm text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-full">
                              <Loader2 className="w-2 h-2 animate-spin" />
                              RECONECTANDO
                            </div>
                          ) : (
                            <div className="absolute top-1.5 left-1.5 flex items-center gap-1 bg-red-600/90 backdrop-blur-sm text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-full">
                              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                              AO VIVO
                            </div>
                          )}
                        </>
                      ) : httpSnapshotUrl ? (
                        /* HTTP snapshot polling fallback (Digest auth via Electron) */
                        <>
                          <img src={httpSnapshotUrl} alt="Camera" className="absolute inset-0 w-full h-full object-cover" />
                          <div className="absolute top-1.5 left-1.5 flex items-center gap-1 bg-red-600/90 backdrop-blur-sm text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-full">
                            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                            AO VIVO
                          </div>
                        </>
                      ) : livePreviewUrl ? (
                        /* HTTP/MJPEG browser fallback: shared connection via registry */
                        <MjpegLivePreview url={livePreviewUrl} className="absolute inset-0 w-full h-full" />
                      ) : ipCameras.length === 0 ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-gray-600">
                          <VideoOff className="h-7 w-7" />
                          <p className="text-[10px] font-medium">Sem câmera configurada</p>
                        </div>
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-gray-600">
                          <Loader2 className="h-7 w-7 animate-spin" />
                          <p className="text-[10px] font-medium">Conectando...</p>
                          {selectedIPCamera && (
                            <button
                              type="button"
                              onClick={async () => {
                                await stopLivePreview();
                                setTimeout(() => startCameraPreview(selectedIPCamera), 200);
                              }}
                              className="mt-0.5 flex items-center gap-1 px-2 py-1 rounded-md bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-gray-200 text-[10px] font-medium transition-all"
                              tabIndex={-1}
                              data-no-tab="true"
                            >
                              <RefreshCw className="h-2.5 w-2.5" />
                              Tentar novamente
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    </div>

                    {/* Camera picker bar — visible in all states when cameras exist */}
                    {ipCameras.length > 0 && (
                      <div className="flex items-center gap-1 px-2 pt-1.5 pb-2">
                        {ipCameras.length > 1 && (
                          <button
                            type="button"
                            tabIndex={-1}
                            data-no-tab="true"
                            onClick={async () => {
                              const idx = ipCameras.findIndex((c: any) => String(c.id) === selectedIPCamera);
                              const prev = ipCameras[(idx - 1 + ipCameras.length) % ipCameras.length];
                              const newId = String(prev.id);
                              await stopLivePreview();
                              setSelectedIPCamera(newId);
                              if (customerId) localStorage.setItem(`panther_monitor_cam_${customerId}`, newId);
                              setTimeout(() => startCameraPreview(newId), 150);
                            }}
                            className={`flex-shrink-0 p-1 rounded transition-all ${darkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-500 hover:text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-500 hover:text-gray-700'}`}
                          >
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                          </button>
                        )}
                        <div className={`flex-1 flex items-center gap-1.5 min-w-0 px-2 py-1 rounded-md border ${darkMode ? 'bg-gray-800/70 border-gray-700/60' : 'bg-white border-gray-300'}`}>
                          <svg className="h-2.5 w-2.5 flex-shrink-0 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.361a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" /></svg>
                          <span className={`text-xs font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                            {ipCameras.find((c: any) => String(c.id) === selectedIPCamera)?.name || `Câmera ${selectedIPCamera}`}
                          </span>
                          {ipCameras.length > 1 && (
                            <span className={`flex-shrink-0 text-[9px] ml-auto ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                              {(ipCameras.findIndex((c: any) => String(c.id) === selectedIPCamera) + 1)}/{ipCameras.length}
                            </span>
                          )}
                        </div>
                        {ipCameras.length > 1 && (
                          <button
                            type="button"
                            tabIndex={-1}
                            data-no-tab="true"
                            onClick={async () => {
                              const idx = ipCameras.findIndex((c: any) => String(c.id) === selectedIPCamera);
                              const next = ipCameras[(idx + 1) % ipCameras.length];
                              const newId = String(next.id);
                              await stopLivePreview();
                              setSelectedIPCamera(newId);
                              if (customerId) localStorage.setItem(`panther_monitor_cam_${customerId}`, newId);
                              setTimeout(() => startCameraPreview(newId), 150);
                            }}
                            className={`flex-shrink-0 p-1 rounded transition-all ${darkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-500 hover:text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-500 hover:text-gray-700'}`}
                          >
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="px-2">

                  {/* Interfonar Toggle Row */}
                  <button
                    type="button"
                    onClick={() => setRequiresIntercom(!requiresIntercom)}
                    className={`w-full mb-2 flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${
                      requiresIntercom
                        ? darkMode ? 'bg-amber-600/15 border-amber-500 text-amber-400' : 'bg-amber-50 border-amber-400 text-amber-700'
                        : darkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-white border-gray-300 text-gray-500'
                    }`}
                  >
                    <Phone className="h-4 w-4 flex-shrink-0" />
                    <span className={`text-xs font-semibold flex-1 text-left`}>Interfonar</span>
                    {/* Toggle switch */}
                    <div className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
                      requiresIntercom ? 'bg-amber-500' : darkMode ? 'bg-gray-600' : 'bg-gray-300'
                    }`}>
                      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                        requiresIntercom ? 'translate-x-4' : 'translate-x-0'
                      }`} />
                    </div>
                    <span className={`text-xs font-semibold w-6 text-right ${
                      requiresIntercom
                        ? darkMode ? 'text-amber-400' : 'text-amber-700'
                        : darkMode ? 'text-gray-600' : 'text-gray-400'
                    }`}>
                      {requiresIntercom ? 'SIM' : 'NÃO'}
                    </span>
                  </button>

                  {/* Visit History — "Última Visita" merged into "Visitas Anteriores" as one
                   * list. The most recent visit (index 0) gets the full label+icon repeat
                   * button; older ones get a compact icon-only repeat button. */}
                  {existingPerson && recentVisits.length > 0 && (
                    <div className={`border rounded-lg p-3 ${darkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                      <h3 className={`text-xs font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Visitas Anteriores
                      </h3>
                      <div className="space-y-1.5">
                        {recentVisits.map((visit, idx) => {
                          const isFirst = idx === 0;
                          const location = (selectedCustomer?.type === 'empresa' || selectedCustomer?.type === 'fabrica')
                            ? (visit.unit || 'Não informado')
                            : (visit.block && visit.unit ? `Bloco ${visit.block} - ${visit.unit}` : 'Não informado');
                          const linkClass = `text-left truncate hover:underline ${darkMode ? 'text-blue-400' : 'text-blue-600'}`;
                          const plainClass = `truncate ${darkMode ? 'text-gray-400' : 'text-gray-600'}`;
                          return (
                            <div
                              key={idx}
                              className={`text-[11px] px-2 py-1.5 rounded ${darkMode ? 'bg-gray-900/40' : 'bg-white'} ${
                                isFirst ? `border-2 ${darkMode ? 'border-blue-700' : 'border-blue-300'}` : ''
                              }`}
                            >
                              {/* Row 1: role badge */}
                              <div>
                                <span className={`inline-block text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                                  visit.roleType === 'service_provider'
                                    ? darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'
                                    : darkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-700'
                                }`}>
                                  {visit.roleType === 'service_provider' ? 'Prestador' : 'Visitante'}
                                </span>
                              </div>

                              {/* Row 2: authorized-by name */}
                              <div className="mt-1">
                                {visit.authorizedByResidentId ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      onClose();
                                      setTimeout(() => openPersonDetail(visit.authorizedByResidentId, false, darkMode), 150);
                                    }}
                                    className={`w-full ${linkClass}`}
                                  >
                                    {visit.authorizedByName || 'Não informado'}
                                  </button>
                                ) : (
                                  <div className={plainClass}>{visit.authorizedByName || 'Não informado'}</div>
                                )}
                              </div>

                              {/* Row 3: date + time */}
                              <div className={`mt-1 ${plainClass}`}>
                                {visit.lastVisitDate
                                  ? new Date(visit.lastVisitDate).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                                  : '—'}
                              </div>

                              {/* Row 4: unit, clickable — navigates to the unit's page */}
                              <div className="mt-1">
                                {visit.unitId ? (
                                  <button
                                    type="button"
                                    onClick={() => { onClose(); navigate(`/units?unit=${visit.unitId}`); }}
                                    className={`w-full ${linkClass}`}
                                  >
                                    {location}
                                  </button>
                                ) : (
                                  <div className={plainClass}>{location}</div>
                                )}
                              </div>

                              {/* Row 5: repeat visit — full label+icon for the most recent
                               * visit, compact icon-only for older ones */}
                              <div className={`mt-2 ${isFirst ? '' : 'flex justify-end'}`}>
                                {isFirst ? (
                                  <button
                                    type="button"
                                    onClick={() => handleRepeatVisit(visit)}
                                    disabled={isRepeatingVisitLoading || isLoadingBlocks}
                                    className={`w-full px-3 py-2 rounded-md text-xs font-medium flex items-center justify-center gap-2 transition-colors ${
                                      isRepeatingVisitLoading || isLoadingBlocks
                                        ? 'bg-blue-400 cursor-not-allowed text-white'
                                        : darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
                                    }`}
                                  >
                                    <RefreshCw size={14} className={isRepeatingVisitLoading || isLoadingBlocks ? 'animate-spin' : ''} />
                                    {isRepeatingVisitLoading || isLoadingBlocks ? 'Carregando...' : 'Repetir Visita'}
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleRepeatVisit(visit)}
                                    disabled={isRepeatingVisitLoading || isLoadingBlocks}
                                    title="Repetir Visita"
                                    className={`p-1.5 rounded-md transition-colors ${
                                      isRepeatingVisitLoading || isLoadingBlocks
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : darkMode ? 'text-blue-400 hover:bg-blue-500/20' : 'text-blue-600 hover:bg-blue-100'
                                    }`}
                                  >
                                    <RefreshCw size={13} className={isRepeatingVisitLoading || isLoadingBlocks ? 'animate-spin' : ''} />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  </div>
                </div>

                {/* Photos Bar Column */}
                <div className={`w-72 flex-shrink-0 border-r flex flex-col overflow-hidden ${
                  darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'
                }`}>
                  {/* Photos Bar - ID Photo (row 1), Doc Front + Doc Back (row 2), Vehicle (row 3) */}
                  <div className={`p-2 space-y-2 overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                    {/* Foto Label + Edit All button */}
                    <div className="flex items-center justify-between mb-1">
                      <p className={`text-[10px] font-medium uppercase ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Foto</p>
                      <button
                        type="button"
                        onClick={() => setShowAllPhotosModal(true)}
                        className={`text-[10px] font-medium px-2 py-0.5 rounded-md transition-colors ${
                          darkMode
                            ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                            : 'bg-gray-200 hover:bg-gray-300 text-gray-600'
                        }`}
                      >
                        Editar todas
                      </button>
                    </div>

                    {/* Row 1: ID Photo (circular for face) */}
                    <div>
                      <PhotoUploadArea
                        photo={capturedPhoto}
                        onUpload={() => setShowUploadModal(true)}
                        onWebcam={() => setShowWebcamModal(true)}
                        onIPCamera={() => setShowIDPhotoModal(true)}
                        onRemove={() => setCapturedPhoto(null)}
                        onZoom={(url, title, onReplace, onRemove) => {
                          setZoomPhotoUrl(url);
                          setZoomPhotoTitle(title);
                          setZoomPhotoActions({ onReplace, onRemove });
                        }}
                        shape="circle"
                        aspectRatio="aspect-[4/5]"
                        icon={UserCircle}
                        label="IDENTIFICAÇÃO"
                        required={!!photoRequirements.identity_photo}
                        title="Foto de Identificação"
                        darkMode={darkMode}
                      />
                    </div>

                    {/* Row 2: Doc Front + Doc Back + Vehicle Photo (side by side) */}
                    <div className="grid grid-cols-3 gap-2">
                      {/* Document Front */}
                      <PhotoUploadArea
                        photo={documentFrontPhoto}
                        onUpload={() => setShowDocFrontUploadModal(true)}
                        onWebcam={() => setShowDocFrontWebcamModal(true)}
                        onIPCamera={() => setShowDocumentFrontModal(true)}
                        onRemove={() => setDocumentFrontPhoto(null)}
                        onZoom={(url, title, onReplace, onRemove) => {
                          setZoomPhotoUrl(url);
                          setZoomPhotoTitle(title);
                          setZoomPhotoActions({ onReplace, onRemove });
                        }}
                        shape="rectangle"
                        aspectRatio="aspect-[3/2]"
                        icon={CreditCard}
                        badge="F"
                        label="DOC. FRENTE"
                        required={!!photoRequirements.document_front}
                        title="Foto de Documento (Frente)"
                        darkMode={darkMode}
                      />

                      {/* Document Back */}
                      <PhotoUploadArea
                        photo={documentBackPhoto}
                        onUpload={() => setShowDocBackUploadModal(true)}
                        onWebcam={() => setShowDocBackWebcamModal(true)}
                        onIPCamera={() => setShowDocumentBackModal(true)}
                        onRemove={() => setDocumentBackPhoto(null)}
                        onZoom={(url, title, onReplace, onRemove) => {
                          setZoomPhotoUrl(url);
                          setZoomPhotoTitle(title);
                          setZoomPhotoActions({ onReplace, onRemove });
                        }}
                        shape="rectangle"
                        aspectRatio="aspect-[3/2]"
                        icon={CreditCard}
                        badge="V"
                        label="DOC. VERSO"
                        required={!!photoRequirements.document_back}
                        title="Foto de Documento (Verso)"
                        darkMode={darkMode}
                      />

                      {/* Vehicle Photo */}
                      <PhotoUploadArea
                        photo={vehiclePhoto}
                        onUpload={() => setShowVehicleUploadModal(true)}
                        onWebcam={() => setShowVehicleWebcamModal(true)}
                        onIPCamera={() => setShowVehiclePhotoModal(true)}
                        onRemove={() => setVehiclePhoto(null)}
                        onZoom={(url, title, onReplace, onRemove) => {
                          setZoomPhotoUrl(url);
                          setZoomPhotoTitle(title);
                          setZoomPhotoActions({ onReplace, onRemove });
                        }}
                        shape="rectangle"
                        aspectRatio={VEHICLE_PHOTO_ASPECT_CLASS}
                        fit="contain"
                        icon={Car}
                        label="VEÍCULO"
                        title="Foto de Veículo"
                        darkMode={darkMode}
                      />
                    </div>
                  </div>

                  {/* Note section — below photos */}
                  <div className={`flex-1 flex flex-col border-t p-3 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-1.5 mb-2">
                      <MessageSquare className={`h-3.5 w-3.5 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <span className={`text-[10px] font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Nota Interna
                      </span>
                      <span className={`text-[10px] ml-auto ${newVisitorNote.length > 0 ? (darkMode ? 'text-gray-400' : 'text-gray-500') : (darkMode ? 'text-gray-600' : 'text-gray-400')}`}>
                        {newVisitorNote.length}/500
                      </span>
                    </div>
                    <textarea
                      value={newVisitorNote}
                      onChange={(e) => setNewVisitorNote(e.target.value)}
                      placeholder="Anotação interna sobre este visitante..."
                      maxLength={500}
                      className={`flex-1 w-full rounded-lg text-xs resize-none focus:outline-none focus:ring-1 focus:ring-blue-500/50 p-2.5 min-h-[80px] ${
                        darkMode
                          ? 'bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-600 focus:border-blue-500/50'
                          : 'bg-white border border-gray-300 text-gray-700 placeholder-gray-400 focus:border-blue-400'
                      }`}
                    />
                    <p className={`text-[10px] mt-1.5 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                      Visível apenas para operadores
                    </p>
                  </div>
                </div>

                {/* Right side - Form fields */}
                <div className={`flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4 ${darkMode ? 'bg-gray-900' : ''}`}>
                  {/* First thing on the screen when it applies: the resident already cleared this
                      person, for which unit, until when, and whether to interfonar. */}
                  <LiberacoesBanner
                    liberacoes={liberacoes}
                    selectedUnitId={selectedUnit?.value}
                    darkMode={darkMode}
                  />

                  {!existingPerson && (
                    <>
                      {/* Name and CPF Row - Layout changes based on compactMode */}
                      <div className="grid grid-cols-2 gap-3">
                        {compactMode ? (
                          /* Compact Mode: Single Name Field + CPF */
                          <>
                            <div className={(fieldConfig?.standard_fields?.cpf?.enabled ?? true) ? '' : 'col-span-2'}>
                              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Nome Completo *</label>
                              <input
                                value={fullName}
                                onChange={(e) => {
                                  // Letters only, as typed — see sanitizePersonName.
                                  const name = sanitizePersonName(e.target.value);
                                  setFullName(name);
                                  // Split name into first and last for form validation
                                  const parts = name.trim().split(/\s+/);
                                  const firstName = parts[0] || '';
                                  const lastName = parts.slice(1).join(' ') || parts[0] || '';
                                  setValue('first_name', firstName, { shouldValidate: true });
                                  setValue('last_name', lastName, { shouldValidate: true });
                                }}
                                onBlur={() => {
                                  // Trigger validation on blur
                                  trigger('first_name');
                                  trigger('last_name');
                                }}
                                type="text"
                                placeholder="Nome e sobrenome"
                                required
                                className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                  darkMode
                                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                                    : 'border border-gray-300'
                                }`}
                              />
                              {/* Hidden registered fields for validation */}
                              <input
                                type="hidden"
                                {...register('first_name', {
                                  required: 'Nome é obrigatório',
                                  validate: (v: string) =>
                                    (v || '').trim().length >= 2 || 'Primeiro nome muito curto',
                                })}
                              />
                              <input
                                type="hidden"
                                {...register('last_name', {
                                  required: 'Sobrenome é obrigatório',
                                  validate: (v: string) =>
                                    (v || '').trim().length >= 2 || 'Sobrenome muito curto',
                                })}
                              />
                              {errors.first_name && (
                                <p className="mt-1 text-xs text-red-500">{String(errors.first_name.message || "Nome é obrigatório")}</p>
                              )}
                              {!errors.first_name && fullName.trim() && fullName.trim().split(/\s+/).length < 2 && (
                                <p className="mt-1 text-xs text-amber-500">Digite nome e sobrenome</p>
                              )}
                            </div>
                            {/* CPF field - shown in compact mode on same row, only if enabled.
                                A foreigner is identified by the passport below; the CPF is not
                                just optional for one, it is not asked for at all. */}
                            {(fieldConfig?.standard_fields?.cpf?.enabled ?? true) && !isForeigner && <div>
                              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                CPF {cpfRequiredHere && '*'}
                              </label>
                              <input
                                {...register('cpf', {
                                  required: cpfRequiredHere && 'CPF é obrigatório',
                                  validate: (value) => {
                                    if (!value) return cpfRequiredHere ? 'CPF é obrigatório' : true;
                                    const cleanCPF = value.replace(/\D/g, '');
                                    if (cleanCPF.length !== 11) return 'CPF deve ter 11 dígitos';
                                    return true;
                                  }
                                })}
                                type="text"
                                placeholder="000.000.000-00"
                                className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                  darkMode
                                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                                    : 'border border-gray-300'
                                }`}
                              />
                              {errors.cpf && (
                                <p className="mt-1 text-xs text-red-500">{String(errors.cpf.message || "")}</p>
                              )}
                              {isAdmin && (
                                <label className="flex items-center cursor-pointer mt-1.5">
                                  <input
                                    type="checkbox"
                                    checked={allowWithoutCPF}
                                    onChange={(e) => {
                                      setAllowWithoutCPF(e.target.checked);
                                      setTimeout(() => trigger('cpf'), 0);
                                    }}
                                    className="h-3 w-3 rounded border-gray-600 text-blue-600 focus:ring-blue-500 mr-1.5"
                                  />
                                  <span className="text-xs text-gray-400">Permitir sem CPF</span>
                                </label>
                              )}
                            </div>}
                          </>
                        ) : (
                          /* Normal Mode: First Name + Last Name */
                          <>
                            <div>
                              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Nome *</label>
                              <input
                                {...register('first_name', {
                                  required: 'Nome é obrigatório',
                                  onChange: (e) => setValue(
                                    'first_name',
                                    forceUpper
                                      ? sanitizePersonName(e.target.value).toUpperCase()
                                      : sanitizePersonName(e.target.value),
                                    { shouldValidate: false },
                                  ),
                                })}
                                type="text"
                                required
                                style={forceUpper ? { textTransform: 'uppercase' } : undefined}
                                className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                  darkMode
                                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                                    : 'border border-gray-300'
                                }`}
                              />
                              {errors.first_name && (
                                <p className="mt-1 text-xs text-red-500">{String(errors.first_name.message || "")}</p>
                              )}
                              {ocrMismatches.first_name && (
                                <div className={`flex items-center mt-1 text-xs ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                  <AlertTriangle className="h-3 w-3 mr-1 flex-shrink-0" />
                                  <span>Documento: <strong>{ocrMismatches.first_name}</strong></span>
                                </div>
                              )}
                            </div>

                            <div>
                              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Sobrenome *</label>
                              <input
                                {...register('last_name', {
                                  required: 'Sobrenome é obrigatório',
                                  onChange: (e) => setValue(
                                    'last_name',
                                    forceUpper
                                      ? sanitizePersonName(e.target.value).toUpperCase()
                                      : sanitizePersonName(e.target.value),
                                    { shouldValidate: false },
                                  ),
                                })}
                                type="text"
                                required
                                style={forceUpper ? { textTransform: 'uppercase' } : undefined}
                                className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                  darkMode
                                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                                    : 'border border-gray-300'
                                }`}
                              />
                              {errors.last_name && (
                                <p className="mt-1 text-xs text-red-500">{String(errors.last_name.message || "")}</p>
                              )}
                              {ocrMismatches.last_name && (
                                <div className={`flex items-center mt-1 text-xs ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                  <AlertTriangle className="h-3 w-3 mr-1 flex-shrink-0" />
                                  <span>Documento: <strong>{ocrMismatches.last_name}</strong></span>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>

                      {/* É estrangeiro — before the configurable fields because it decides which
                          of them apply: a foreigner is identified by a passport, not a CPF. */}
                      <div className="mt-3">
                        <div className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${
                          isForeigner
                            ? darkMode ? 'border-primary-700 bg-primary-900/20' : 'border-primary-200 bg-primary-50'
                            : darkMode ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'
                        }`}>
                          <Globe className={`h-4 w-4 flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                          <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            Estrangeiro
                          </span>
                          {/* The current state in words — a lone switch does not say which way is on. */}
                          <span className={`ml-auto text-xs font-bold tracking-wide ${
                            isForeigner ? 'text-emerald-600' : darkMode ? 'text-gray-500' : 'text-gray-400'
                          }`}>
                            {isForeigner ? 'SIM' : 'NÃO'}
                          </span>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={isForeigner}
                            aria-label="Estrangeiro"
                            onClick={() => {
                              const next = !isForeigner;
                              setValue('is_foreigner', next, { shouldValidate: false });
                              // The CPF field is about to disappear. A hidden input still holding
                              // a half-typed CPF fails validation where nobody can see the error.
                              if (next) setValue('cpf', '', { shouldValidate: false });
                              // Only the CPF: re-validating the passport here puts "obrigatório"
                              // under a field the operator has not reached yet.
                              setTimeout(() => trigger('cpf'), 0);
                            }}
                            className={`h-5 w-9 flex-shrink-0 rounded-full transition-colors ${
                              isForeigner ? 'bg-emerald-500' : darkMode ? 'bg-gray-600' : 'bg-gray-300'
                            }`}
                          >
                            <span className={`block h-4 w-4 rounded-full bg-white transition-transform ${
                              isForeigner ? 'translate-x-[1.125rem]' : 'translate-x-0.5'
                            }`} />
                          </button>
                        </div>

                        {isForeigner && (
                          <div className="grid grid-cols-2 gap-3 mt-3">
                            <div>
                              <label
                                htmlFor="visitor_passport_number"
                                className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                              >
                                Número do Passaporte <span className="text-red-500">*</span>
                              </label>
                              <input
                                id="visitor_passport_number"
                                {...register('passport_number', {
                                  validate: (v: string) =>
                                    !isForeigner || !!normalizePassport(v) || 'Número do passaporte é obrigatório',
                                })}
                                type="text"
                                placeholder="AB123456"
                                maxLength={20}
                                // Normalised as typed, the way the column stores it: the number
                                // comes off a physical document and the spacing is the
                                // transcriber's, not the passport's.
                                // shouldValidate, or the error raised by the submit stays on
                                // screen while the operator types the number into the field.
                                // (Spreading register() and overriding its onChange means RHF
                                // never sees the keystroke itself.)
                                onChange={(e) => setValue('passport_number', normalizePassport(e.target.value), { shouldValidate: true })}
                                className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                  darkMode
                                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                                    : 'border border-gray-300'
                                }`}
                              />
                              {errors.passport_number && (
                                <p className="mt-1 text-xs text-red-500">{String(errors.passport_number.message || '')}</p>
                              )}
                            </div>
                            <div>
                              <label
                                htmlFor="visitor_country"
                                className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                              >
                                País
                              </label>
                              <select
                                id="visitor_country"
                                {...register('country')}
                                className={`mt-1 block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                                  darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border border-gray-300'
                                }`}
                              >
                                <option value="">Selecione…</option>
                                {countryOptions().map(({ code, name }) => (
                                  <option key={code} value={code}>{name}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Configurable fields — ordered and sized by admin config */}
                      {(() => {

                        return (
                          <>
                            <div className="grid grid-cols-2 gap-3">
                              {configurableFieldsFor(effectiveConfig)
                                .map(item => renderConfigurableVisitorField(item.key, item.isCustom, item.col_span))}
                            </div>

                            {/* CPF taken — shown below the field grid */}
                            {!compactMode && cpfCheckStatus === 'taken' && cpfExistingPerson && !existingPerson && (
                              <button
                                type="button"
                                onClick={handleCPFMatchClick}
                                className={cn(
                                  "w-full mt-2 p-3 rounded-lg border-2 flex items-center gap-3 transition-all",
                                  "hover:shadow-md active:scale-[0.98]",
                                  darkMode
                                    ? "bg-amber-900/20 border-amber-700 hover:bg-amber-900/30 hover:border-amber-600"
                                    : "bg-amber-50 border-amber-400 hover:bg-amber-100 hover:border-amber-500"
                                )}
                              >
                                <div className="flex-shrink-0">
                                  {cpfExistingPerson.photo_url ? (
                                    <img
                                      src={getAbsoluteImageUrl(cpfExistingPerson.photo_url)}
                                      alt={cpfExistingPerson.name}
                                      className="w-10 h-12 rounded-md object-cover border-2 border-amber-500"
                                    />
                                  ) : (
                                    <div className={cn(
                                      "w-10 h-12 rounded-md flex items-center justify-center border-2",
                                      darkMode ? "bg-gray-700 border-amber-600" : "bg-amber-200 border-amber-500"
                                    )}>
                                      <UserCircle className={cn("w-8 h-8", darkMode ? "text-amber-400" : "text-amber-700")} />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 text-left min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <AlertTriangle className={cn("h-4 w-4 flex-shrink-0", darkMode ? "text-amber-400" : "text-amber-600")} />
                                    <span className={cn("text-xs font-semibold uppercase tracking-wide", darkMode ? "text-amber-400" : "text-amber-700")}>
                                      CPF já cadastrado
                                    </span>
                                  </div>
                                  <p className={cn("text-sm font-medium truncate", darkMode ? "text-amber-200" : "text-amber-900")}>
                                    {cpfExistingPerson.name}
                                  </p>
                                  <p className={cn("text-xs mt-0.5", darkMode ? "text-amber-400" : "text-amber-600")}>
                                    Clique para ver detalhes completos →
                                  </p>
                                </div>
                                <ArrowRight className={cn("w-5 h-5 flex-shrink-0", darkMode ? "text-amber-400" : "text-amber-600")} />
                              </button>
                            )}

                            {/* CPF notes banner */}
                            {!compactMode && cpfCheckStatus === 'taken' && cpfExistingPerson && existingPersonNotes.length > 0 && showNotesBanner && (
                              <div className={cn(
                                "mt-3 p-3 rounded-lg border-l-4",
                                darkMode ? "bg-blue-900/20 border-blue-600" : "bg-blue-50 border-blue-500"
                              )}>
                                <div className="flex items-start gap-2">
                                  <MessageSquare className={cn("w-4 h-4 flex-shrink-0 mt-0.5", darkMode ? "text-blue-400" : "text-blue-600")} />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className={cn("text-xs font-semibold uppercase tracking-wide", darkMode ? "text-blue-400" : "text-blue-700")}>
                                        {existingPersonNotes.length === 1
                                          ? 'Nota sobre esta pessoa'
                                          : `${existingPersonNotes.length} notas sobre esta pessoa`
                                        }
                                      </span>
                                      <button
                                        onClick={() => setShowNotesBanner(false)}
                                        className={cn("p-1 rounded hover:bg-opacity-10 transition-colors", darkMode ? "hover:bg-blue-500 text-blue-400" : "hover:bg-blue-500 text-blue-600")}
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                    <p className={cn("text-sm line-clamp-2", darkMode ? "text-blue-200" : "text-blue-900")}>
                                      {existingPersonNotes[0].content}
                                    </p>
                                    {existingPersonNotes.length > 1 && (
                                      <p className={cn("text-xs mt-2", darkMode ? "text-blue-400" : "text-blue-600")}>
                                        Clique no perfil acima para ver todas as notas →
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}

                  {/* Identity fields for Cadastrar Acesso mode — editable, like the new visitor form.
                      A misspelled name or a missing CPF used to be fixable only by leaving this flow
                      and opening the person's record, so in practice it got carried forward. Blank
                      CPF here means "not filled in": it never erases the stored one. */}
                  {existingPerson && (
                    <div className="grid grid-cols-2 gap-3">
                      {/* Nome takes the whole row when the condomínio has CPF turned off, so it
                          matches the new-visitor form instead of leaving a half-width field. */}
                      <div className={cpfEnabled ? undefined : 'col-span-2'}>
                        <label
                          htmlFor="cadastrar-acesso-nome"
                          className={`block text-[11px] font-semibold tracking-widest uppercase mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                        >
                          Nome Completo *
                        </label>
                        <input
                          id="cadastrar-acesso-nome"
                          value={fullName}
                          onChange={(e) => {
                            const name = sanitizePersonName(e.target.value);
                            setFullName(name);
                            const parts = name.trim().split(/\s+/);
                            setValue('first_name', parts[0] || '', { shouldValidate: false });
                            setValue('last_name', parts.slice(1).join(' ') || '', { shouldValidate: false });
                          }}
                          type="text"
                          placeholder="Nome e sobrenome"
                          className={`block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                            darkMode
                              ? 'bg-gray-800 border border-gray-600 text-white placeholder-gray-500'
                              : 'border border-gray-300'
                          }`}
                        />
                        {fullName.trim() && fullName.trim().split(/\s+/).length < 2 && (
                          <p className="mt-1 text-xs text-amber-500">Digite nome e sobrenome</p>
                        )}
                      </div>
                      {/* Same config switch the new-visitor form honours: a customer that turned CPF
                          off does not get asked for one here either. */}
                      {cpfEnabled && (
                      <div>
                        <label
                          htmlFor="cadastrar-acesso-cpf"
                          className={`block text-[11px] font-semibold tracking-widest uppercase mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                        >
                          CPF
                        </label>
                        <input
                          id="cadastrar-acesso-cpf"
                          {...register('cpf', {
                            validate: (value) => {
                              const clean = (value || '').replace(/\D/g, '');
                              if (clean && clean.length !== 11) return 'CPF deve ter 11 dígitos';
                              return true;
                            },
                          })}
                          // Controlled on purpose: several code paths setValue('cpf') with the raw
                          // digits the API returns, at least one of them asynchronously after this
                          // mounts, so masking only on prefill left "36063683801" on screen.
                          value={formatCPF(cpf || '')}
                          onChange={(e) => setValue('cpf', formatCPF(e.target.value), { shouldValidate: true })}
                          type="text"
                          placeholder="000.000.000-00"
                          className={`block w-full rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                            darkMode
                              ? 'bg-gray-800 border border-gray-600 text-white placeholder-gray-500'
                              : 'border border-gray-300'
                          }`}
                        />
                        {errors.cpf && (
                          <p className="mt-1 text-xs text-red-500">{String(errors.cpf.message || '')}</p>
                        )}
                      </div>
                      )}
                      {/* The rest of the condomínio's configuration — Telefone, RG, Data de
                          Nascimento and every custom field, from the SAME renderer and the same
                          config the new-visitor form uses, inside the same grid as Nome/CPF so the
                          two forms lay out identically. This screen used to draw its own Telefone
                          and stop there: the duplicate consumed a cell (shifting every later field)
                          and the configured fields never appeared at all. */}
                      {configurableFieldsFor(effectiveConfig, { exclude: EXISTING_PERSON_HARDCODED_FIELDS })
                        .map(item => renderConfigurableVisitorField(item.key, item.isCustom, item.col_span))}
                    </div>
                  )}

                  {/* Existing Notes - Highly Highlighted Alert at Top */}
                  {/* One note, a count, and the rest behind a button. This used to print every
                      note in full inside a gradient card that pushed the actual form off-screen. */}
                  {existingPerson && (
                    <PersonNotesAlert
                      notes={existingPersonNotes as any}
                      onConclude={handleConcludeNote}
                      darkMode={darkMode}
                    />
                  )}

                  {/* Vehicles — merged registered + manual */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className={`text-[11px] font-semibold tracking-widest uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Veículos
                      </label>
                      {!showVehicleForm && (
                        <button
                          type="button"
                          onClick={() => setShowVehicleForm(true)}
                          className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors ${
                            darkMode
                              ? 'border-primary-500 text-primary-400 hover:bg-primary-900/30'
                              : 'border-primary-600 text-primary-700 hover:bg-primary-50'
                          }`}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Adicionar Veículo
                        </button>
                      )}
                    </div>

                    {/* Inline add form */}
                    {showVehicleForm && (
                      <div className={`rounded-xl border p-3 mb-2 ${darkMode ? 'bg-gray-700/50 border-gray-500' : 'bg-white border-gray-300 shadow-sm'}`}>
                        <div className="flex gap-3 items-end">
                          <div className="flex-1 min-w-0">
                            <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Placa *</label>
                            <input
                              type="text"
                              value={newVehiclePlate}
                              onChange={(e) => setNewVehiclePlate(e.target.value.toUpperCase())}
                              placeholder="ABC1D23"
                              maxLength={8}
                              autoFocus
                              className={`block w-full rounded-lg py-2 px-3 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                darkMode ? 'bg-gray-600 border border-gray-500 text-white placeholder-gray-400' : 'bg-gray-50 border border-gray-300 text-gray-900'
                              }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Modelo</label>
                            <input
                              type="text"
                              value={newVehicleModel}
                              onChange={(e) => setNewVehicleModel(e.target.value)}
                              placeholder="Ex: Civic"
                              className={`block w-full rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                darkMode ? 'bg-gray-600 border border-gray-500 text-white placeholder-gray-400' : 'bg-gray-50 border border-gray-300 text-gray-900'
                              }`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <label className={`block text-[10px] font-semibold tracking-widest uppercase mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cor</label>
                            <input
                              type="text"
                              value={newVehicleColor}
                              onChange={(e) => setNewVehicleColor(e.target.value)}
                              placeholder="Ex: Preto"
                              className={`block w-full rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                darkMode ? 'bg-gray-600 border border-gray-500 text-white placeholder-gray-400' : 'bg-gray-50 border border-gray-300 text-gray-900'
                              }`}
                            />
                          </div>
                          <div className="flex gap-1.5 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => { setShowVehicleForm(false); setNewVehiclePlate(''); setNewVehicleModel(''); setNewVehicleColor(''); }}
                              className={`px-3 py-2 text-sm rounded-lg border transition-colors ${darkMode ? 'border-gray-500 text-gray-300 hover:bg-gray-600' : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50'}`}
                            >
                              <X className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const plate = newVehiclePlate.trim();
                                if (!plate) return;
                                setVehicleList([{ plate, model: newVehicleModel.trim(), color: newVehicleColor.trim() }]);
                                setShowVehicleForm(false);
                                setNewVehiclePlate(''); setNewVehicleModel(''); setNewVehicleColor('');
                              }}
                              disabled={!newVehiclePlate.trim()}
                              className="px-3 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 transition-colors"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* "Entrando a pé" — a first-class choice, not the absence of one.
                        A plate was attached to the visit automatically (from the person's record,
                        or carried across by "Repetir Visita") and the only way out was to notice
                        the row was a toggle. Even then nothing recorded that the person walked in,
                        so the gate log showed a car that was never on the property.
                        Rendered above every vehicle list rather than inside one, because the plate
                        can come from any of them — and shown only while the visitor is still
                        arriving: once inside, the arrival has happened and there is nothing left
                        to declare about it. */}
                    {showOnFootChoice && (
                      <div className={`rounded-xl border overflow-hidden mb-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <button
                          type="button"
                          onClick={() => setVehicleList([])}
                          className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left ${
                            arrivedOnFoot
                              ? darkMode ? 'bg-blue-900/30 border-l-2 border-l-blue-500' : 'bg-blue-50 border-l-2 border-l-blue-500'
                              : darkMode ? 'bg-gray-800/30 hover:bg-gray-700/40' : 'bg-white hover:bg-gray-50'
                          }`}
                        >
                          <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                            arrivedOnFoot ? darkMode ? 'bg-blue-800/50' : 'bg-blue-100' : darkMode ? 'bg-gray-700' : 'bg-gray-100'
                          }`}>
                            <Footprints className={`w-4 h-4 transition-colors ${arrivedOnFoot ? 'text-blue-400' : darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>Entrando a pé</p>
                            <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Sem veículo nesta visita</p>
                          </div>
                          {arrivedOnFoot && (
                            <span className={`flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-600'}`}>
                              Nesta visita
                            </span>
                          )}
                        </button>
                      </div>
                    )}

                    {/* One list, built from utils/visitArrivalMode. The selection is a FLAG on
                        these rows, never the list itself — that conflation is what made "Entrando
                        a pé" delete a plate the visit had carried over, leaving the operator with
                        "Nenhum veículo adicionado" and no way back to the car. */}
                    {visitVehicles.length > 0 && (
                      <div className={`rounded-xl border overflow-hidden divide-y ${darkMode ? 'border-gray-700 divide-gray-700' : 'border-gray-200 divide-gray-100'}`}>
                        {visitVehicles.map((v, i) => {
                          const subtitle = [v.brand, v.model, v.color, v.year].filter(Boolean).join(' · ');
                          return (
                            <div
                              key={`${v.plate}-${i}`}
                              className={`w-full flex items-center gap-3 transition-colors ${
                                v.isSelected
                                  ? darkMode ? 'bg-blue-900/30 border-l-2 border-l-blue-500' : 'bg-blue-50 border-l-2 border-l-blue-500'
                                  : darkMode ? 'bg-gray-800/30' : 'bg-white'
                              }`}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  if (v.isSelected) {
                                    setVehicleList([]);
                                  } else {
                                    setVehicleList([{ plate: v.plate, model: v.model || '', color: v.color || '' }]);
                                  }
                                }}
                                className={`flex-1 min-w-0 flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                                  v.isSelected ? '' : darkMode ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'
                                }`}
                              >
                                <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                                  v.isSelected ? darkMode ? 'bg-blue-800/50' : 'bg-blue-100' : darkMode ? 'bg-gray-700' : 'bg-gray-100'
                                }`}>
                                  <Car className={`w-4 h-4 transition-colors ${v.isSelected ? 'text-blue-400' : darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <LicensePlate plate={v.plate} size="sm" />
                                  {subtitle && (
                                    <p className={`text-xs mt-0.5 truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{subtitle}</p>
                                  )}
                                </div>
                              </button>
                              {v.isSelected ? (
                                <span className={`flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-600'}`}>
                                  Nesta visita
                                </span>
                              ) : v.source === 'registered' ? (
                                <span className={`flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${
                                  v.isActive
                                    ? darkMode ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-50 text-emerald-600'
                                    : darkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-100 text-gray-400'
                                }`}>
                                  {v.isActive ? 'Ativo' : 'Inativo'}
                                </span>
                              ) : null}
                              {/* Removing a one-off plate is a different act from not using it on
                                  this visit: this takes it off the list entirely. A vehicle on the
                                  person's record is never removable from here. */}
                              {v.source === 'visit' ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOfferedPlates(prev => prev.filter(p => p.plate.replace(/\s+/g, '').toUpperCase() !== v.plate.replace(/\s+/g, '').toUpperCase()));
                                    setVehicleList(prev => prev.filter(vl => vl.plate.replace(/\s+/g, '').toUpperCase() !== v.plate.replace(/\s+/g, '').toUpperCase()));
                                  }}
                                  className={`p-1 mr-3 rounded transition-colors flex-shrink-0 ${darkMode ? 'text-gray-500 hover:text-red-400 hover:bg-gray-700' : 'text-gray-400 hover:text-red-500 hover:bg-gray-200'}`}
                                  title="Remover veículo da lista"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              ) : (
                                <span className="w-3 flex-shrink-0" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {visitVehicles.length === 0 && !showVehicleForm && (
                      <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Nenhum veículo adicionado</p>
                    )}
                  </div>

                  {/* Block/Unit/Department Selection - For filtering residents */}
                  {(selectedCustomer?.type !== 'empresa' && selectedCustomer?.type !== 'fabrica') ? (
                    // Condo: Show Block + Unit (or Quadra + Lote for casa type)
                    (() => {
                      const isCondoOfHouses = selectedCustomer?.condoType === 'casa' || selectedCustomer?.condoType === 'casas' || (selectedCustomer as any)?.condo_data?.condo_type === 'casa' || (selectedCustomer as any)?.condo_data?.condo_type === 'casas' || streets.length > 0;
                      const blockLabel = isCondoOfHouses ? 'Quadra' : 'Bloco';
                      const unitLabel = isCondoOfHouses ? 'Lote' : 'Unidade';

                      // Sort blocks alphabetically
                      const sortedBlocks = [...blocks].sort((a, b) =>
                        a.name.localeCompare(b.name, 'pt-BR', { numeric: true, sensitivity: 'base' })
                      );

                      // Filter units by selected street / street number
                      const streetFilteredUnits = selectedStreet
                        ? (allCustomerUnits.length > 0 ? allCustomerUnits : units).filter((u: any) => u.street?.id === selectedStreet.value)
                        : units;
                      const numberFilteredUnits = streetNumberSearch.trim()
                        ? streetFilteredUnits.filter((u: any) => (u.street_number || '') === streetNumberSearch)
                        : streetFilteredUnits;
                      // Available house numbers from the street-filtered units
                      const availableNumbers = Array.from(new Set<string>(
                        streetFilteredUnits.map((u: any) => u.street_number || '').filter(Boolean)
                      )).sort((a: string, b: string) => a.localeCompare(b, 'pt-BR', { numeric: true }))
                        .map((n: string) => ({ value: n, label: n }));

                      // Sort units alphabetically
                      const sortedUnits = [...numberFilteredUnits].sort((a, b) => {
                        const aLabel = `${a.block?.name || ''} ${a.number || a.name}`;
                        const bLabel = `${b.block?.name || ''} ${b.number || b.name}`;
                        return aLabel.localeCompare(bLabel, 'pt-BR', { numeric: true, sensitivity: 'base' });
                      });

                      return (
                        <div className="flex flex-col gap-4">
                          {/* Rua + Número filters — only for casa condos with streets */}
                          {isCondoOfHouses && streets.length > 0 && (
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className={`block text-[11px] font-semibold tracking-widest uppercase mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Rua <span className="opacity-60">(Filtro)</span>
                                </label>
                                <Select
                                  options={streets.map(s => ({ value: s.id, label: s.name }))}
                                  value={selectedStreet}
                                  onChange={(option: SingleValue<{ value: string; label: string }>) => {
                                    setSelectedStreet(option);
                                    setSelectedUnit(null);
                                    setStreetNumberSearch('');
                                  }}
                                  placeholder="Selecione uma rua..."
                                  noOptionsMessage={() => "Nenhuma rua encontrada"}
                                  isClearable
                                  isSearchable
                                  classNames={{
                                    control: (state) => `!shadow-sm !rounded-md ${darkMode ? `!bg-gray-800 !border-gray-600 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}` : `!border-gray-300 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`}`,
                                    input: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
                                    option: (state) => `!text-sm ${darkMode ? state.isSelected ? '!bg-primary-600 !text-white' : state.isFocused ? '!bg-gray-700 !text-white' : '!bg-gray-800 !text-gray-200' : state.isSelected ? '!bg-primary-600 !text-white' : state.isFocused ? '!bg-gray-100 !text-gray-900' : '!text-gray-900'}`,
                                    placeholder: () => `!text-sm ${darkMode ? '!text-gray-500' : '!text-gray-400'}`,
                                    singleValue: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
                                    menu: () => darkMode ? '!bg-gray-800 !border-gray-600' : '!bg-white',
                                    menuList: () => darkMode ? '!bg-gray-800' : '!bg-white',
                                  }}
                                  styles={{
                                    control: (base) => ({ ...base, minHeight: '38px', backgroundColor: darkMode ? '#1f2937' : base.backgroundColor }),
                                    menu: (base) => ({ ...base, zIndex: 9999, backgroundColor: darkMode ? '#1f2937' : base.backgroundColor }),
                                    option: (base, state) => ({ ...base, backgroundColor: darkMode ? state.isSelected ? '#7c3aed' : state.isFocused ? '#374151' : '#1f2937' : state.isSelected ? '#7c3aed' : state.isFocused ? '#f3f4f6' : '#ffffff', color: darkMode ? '#e5e7eb' : state.isSelected ? '#ffffff' : '#111827' }),
                                  }}
                                />
                              </div>
                              <div>
                                <label className={`block text-[11px] font-semibold tracking-widest uppercase mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Número <span className="opacity-60">(Filtro)</span>
                                </label>
                                <Select
                                  options={availableNumbers}
                                  value={streetNumberSearch ? { value: streetNumberSearch, label: streetNumberSearch } : null}
                                  onChange={(option: SingleValue<{ value: string; label: string }>) => {
                                    const num = option?.value || '';
                                    setStreetNumberSearch(num);
                                    setSelectedUnit(null);
                                    setSelectedBlock(null);
                                    setSelectedResponsible(null);
                                    if (num) {
                                      const sourceUnits = allCustomerUnits.length > 0 ? allCustomerUnits : units;
                                      const match = sourceUnits.find((u: any) =>
                                        u.street?.id === selectedStreet?.value && (u.street_number || '') === num
                                      );
                                      if (match) {
                                        setSelectedUnit({ value: match.id, label: `${match.block?.name ? `${match.block.name} - ` : ''}${match.number || match.name}` });
                                        if (match.block_id) {
                                          const blk = blocks.find((b: any) => b.id === match.block_id);
                                          if (blk) setSelectedBlock({ value: blk.id, label: blk.name });
                                        }
                                      }
                                    }
                                  }}
                                  placeholder="Selecione um número..."
                                  noOptionsMessage={() => selectedStreet ? "Nenhum número encontrado" : "Selecione uma rua primeiro"}
                                  isClearable
                                  isSearchable
                                  classNames={{
                                    control: (state) => `!shadow-sm !rounded-md ${darkMode ? `!bg-gray-800 !border-gray-600 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}` : `!border-gray-300 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`}`,
                                    input: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
                                    option: (state) => `!text-sm ${darkMode ? state.isSelected ? '!bg-primary-600 !text-white' : state.isFocused ? '!bg-gray-700 !text-white' : '!bg-gray-800 !text-gray-200' : state.isSelected ? '!bg-primary-600 !text-white' : state.isFocused ? '!bg-gray-100 !text-gray-900' : '!text-gray-900'}`,
                                    placeholder: () => `!text-sm ${darkMode ? '!text-gray-500' : '!text-gray-400'}`,
                                    singleValue: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
                                    menu: () => darkMode ? '!bg-gray-800 !border-gray-600' : '!bg-white',
                                    menuList: () => darkMode ? '!bg-gray-800' : '!bg-white',
                                  }}
                                  styles={{
                                    control: (base) => ({ ...base, minHeight: '38px', backgroundColor: darkMode ? '#1f2937' : base.backgroundColor }),
                                    menu: (base) => ({ ...base, zIndex: 9999, backgroundColor: darkMode ? '#1f2937' : base.backgroundColor }),
                                    option: (base, state) => ({ ...base, backgroundColor: darkMode ? state.isSelected ? '#7c3aed' : state.isFocused ? '#374151' : '#1f2937' : state.isSelected ? '#7c3aed' : state.isFocused ? '#f3f4f6' : '#ffffff', color: darkMode ? '#e5e7eb' : state.isSelected ? '#ffffff' : '#111827' }),
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-2 gap-4">
                          {/* Block Selection */}
                          <div className="block-select">
                            <label className={`block text-[11px] font-semibold tracking-widest uppercase mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {blockLabel} <span className="opacity-60">(Filtro)</span>
                            </label>
                            <Select
                              options={sortedBlocks.map((block) => ({
                                value: block.id,
                                label: block.name
                              }))}
                          value={selectedBlock}
                              onChange={(option: SingleValue<{ value: string; label: string }>) => {
                                setSelectedBlock(option);
                                setSelectedUnit(null);
                                setSelectedResponsible(null);
                              }}
                              placeholder={`Selecione ${isCondoOfHouses ? 'uma quadra' : 'um bloco'}...`}
                              noOptionsMessage={() => `Nenhum${isCondoOfHouses ? 'a quadra encontrada' : ' bloco encontrado'}`}
                              isClearable
                              isSearchable
                              classNames={{
                                control: (state) =>
                                  `!shadow-sm !rounded-md ${
                                    darkMode
                                      ? `!bg-gray-800 !border-gray-600 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`
                                      : `!border-gray-300 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`
                                  }`,
                                input: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
                                option: (state) => `!text-sm ${
                                  darkMode
                                    ? state.isSelected ? '!bg-primary-600 !text-white' : state.isFocused ? '!bg-gray-700 !text-white' : '!bg-gray-800 !text-gray-200'
                                    : state.isSelected ? '!bg-primary-600 !text-white' : state.isFocused ? '!bg-gray-100 !text-gray-900' : '!text-gray-900'
                                }`,
                                placeholder: () => `!text-sm ${darkMode ? '!text-gray-500' : '!text-gray-400'}`,
                                singleValue: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
                                menu: () => darkMode ? '!bg-gray-800 !border-gray-600' : '!bg-white',
                                menuList: () => darkMode ? '!bg-gray-800' : '!bg-white',
                              }}
                              styles={{
                                control: (base) => ({
                                  ...base,
                                  minHeight: '38px',
                                  backgroundColor: darkMode ? '#1f2937' : base.backgroundColor,
                                }),
                                menu: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                  backgroundColor: darkMode ? '#1f2937' : base.backgroundColor,
                                }),
                                option: (base, state) => ({
                                  ...base,
                                  backgroundColor: darkMode
                                    ? state.isSelected ? '#7c3aed' : state.isFocused ? '#374151' : '#1f2937'
                                    : state.isSelected ? '#7c3aed' : state.isFocused ? '#f3f4f6' : '#ffffff',
                                  color: darkMode ? '#e5e7eb' : state.isSelected ? '#ffffff' : '#111827',
                                }),
                              }}
                            />
                          </div>

                          {/* Unit Selection */}
                          <div className="unit-select">
                            <label className={`block text-[11px] font-semibold tracking-widest uppercase mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {unitLabel} <span className="opacity-60">(Filtro)</span>
                            </label>
                            <Select
                              options={sortedUnits.map((unit) => {
                                const p: string[] = [];
                                if (unit.block?.name) p.push(unit.block.name);
                                if (unit.street?.name) p.push(unit.street_number ? `${unit.street.name}, ${unit.street_number}` : unit.street.name);
                                p.push(unit.number || unit.name);
                                return {
                                  value: unit.id,
                                  label: p.join(' – '),
                                  blockId: unit.block_id,
                                  blockName: unit.block?.name
                                };
                              })}
                              value={selectedUnit}
                              onChange={(option: SingleValue<{ value: string; label: string; blockId?: string; blockName?: string }>) => {
                                logger.info('visitor.modal', 'Unit selected', {
                                  unitId: option?.value,
                                  unitLabel: option?.label,
                                  blockId: option?.blockId
                                });

                                setSelectedUnit(option);
                                setSelectedResponsible(null);

                                // Auto-select block if unit is selected
                                if (option && option.blockId) {
                                  const block = blocks.find(b => b.id === option.blockId);
                                  if (block) {
                                    setSelectedBlock({ value: block.id, label: block.name });
                                  }
                                }

                                // Auto-populate Rua + Número if the unit has a street
                                if (option) {
                                  const sourceUnits = allCustomerUnits.length > 0 ? allCustomerUnits : units;
                                  const fullUnit = sourceUnits.find((u: any) => u.id === option.value);
                                  if (fullUnit?.street?.id) {
                                    setSelectedStreet({ value: fullUnit.street.id, label: fullUnit.street.name });
                                    setStreetNumberSearch(fullUnit.street_number || '');
                                  } else {
                                    setSelectedStreet(null);
                                    setStreetNumberSearch('');
                                  }
                                } else {
                                  setSelectedStreet(null);
                                  setStreetNumberSearch('');
                                }
                              }}
                              placeholder={`Buscar ${isCondoOfHouses ? 'lote' : 'unidade'}...`}
                              noOptionsMessage={() => `Nenhum${isCondoOfHouses ? ' lote encontrado' : 'a unidade encontrada'}`}
                              isClearable
                              isSearchable
                              classNames={{
                                control: (state) =>
                                  `!shadow-sm !rounded-md ${
                                    darkMode
                                      ? `!bg-gray-800 !border-gray-600 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`
                                      : `!border-gray-300 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`
                                  }`,
                                input: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
                                option: (state) => `!text-sm ${
                                  darkMode
                                    ? state.isSelected ? '!bg-primary-600 !text-white' : state.isFocused ? '!bg-gray-700 !text-white' : '!bg-gray-800 !text-gray-200'
                                    : state.isSelected ? '!bg-primary-600 !text-white' : state.isFocused ? '!bg-gray-100 !text-gray-900' : '!text-gray-900'
                                }`,
                                placeholder: () => `!text-sm ${darkMode ? '!text-gray-500' : '!text-gray-400'}`,
                                singleValue: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
                                menu: () => darkMode ? '!bg-gray-800 !border-gray-600' : '!bg-white',
                                menuList: () => darkMode ? '!bg-gray-800' : '!bg-white',
                              }}
                              styles={{
                                control: (base) => ({
                                  ...base,
                                  minHeight: '38px',
                                  backgroundColor: darkMode ? '#1f2937' : base.backgroundColor,
                                }),
                                menu: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                  backgroundColor: darkMode ? '#1f2937' : base.backgroundColor,
                                }),
                                option: (base, state) => ({
                                  ...base,
                                  backgroundColor: darkMode
                                    ? state.isSelected ? '#7c3aed' : state.isFocused ? '#374151' : '#1f2937'
                                    : state.isSelected ? '#7c3aed' : state.isFocused ? '#f3f4f6' : '#ffffff',
                                  color: darkMode ? '#e5e7eb' : state.isSelected ? '#ffffff' : '#111827',
                                }),
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      );
                    })()
                  ) : (
                    // Empresa: Show Department only
                    <div>
                      <label className={`block text-[11px] font-semibold tracking-widest uppercase mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Departamento <span className="opacity-60">(Filtro)</span>
                      </label>
                      <Select
                        options={departments.map((dept) => ({
                          value: dept.id,
                          label: dept.name
                        }))}
                        value={selectedUnit}
                        onChange={(option: SingleValue<{ value: string; label: string }>) => {
                          setSelectedUnit(option);
                          setSelectedResponsible(null);
                        }}
                        placeholder="Selecione um departamento..."
                        noOptionsMessage={() => "Nenhum departamento encontrado"}
                        isClearable
                        isSearchable
                        classNames={{
                          control: (state) =>
                            `!shadow-sm !rounded-md ${
                              darkMode
                                ? `!bg-gray-800 !border-gray-600 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`
                                : `!border-gray-300 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`
                            }`,
                          input: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
                          option: (state) => `!text-sm ${
                            darkMode
                              ? state.isSelected ? '!bg-primary-600 !text-white' : state.isFocused ? '!bg-gray-700 !text-white' : '!bg-gray-800 !text-gray-200'
                              : state.isSelected ? '!bg-primary-600 !text-white' : state.isFocused ? '!bg-gray-100 !text-gray-900' : '!text-gray-900'
                          }`,
                          placeholder: () => `!text-sm ${darkMode ? '!text-gray-500' : '!text-gray-400'}`,
                          singleValue: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
                          menu: () => darkMode ? '!bg-gray-800 !border-gray-600' : '!bg-white',
                          menuList: () => darkMode ? '!bg-gray-800' : '!bg-white',
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '38px',
                            backgroundColor: darkMode ? '#1f2937' : base.backgroundColor,
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 9999,
                            backgroundColor: darkMode ? '#1f2937' : base.backgroundColor,
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: darkMode
                              ? state.isSelected ? '#7c3aed' : state.isFocused ? '#374151' : '#1f2937'
                              : state.isSelected ? '#7c3aed' : state.isFocused ? '#f3f4f6' : '#ffffff',
                            color: darkMode ? '#e5e7eb' : state.isSelected ? '#ffffff' : '#111827',
                          }),
                        }}
                      />
                    </div>
                  )}

                  {/* Resident/Employee Authorization */}
                  <div className="resident-select">
                    <label className={`block text-[11px] font-semibold tracking-widest uppercase mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {selectedCustomer?.type === 'empresa' || selectedCustomer?.type === 'fabrica'
                        ? (roleType === 'visitor' ? 'Pessoa Responsável *' : 'Autorizado por (Pessoa) *')
                        : (roleType === 'visitor' ? 'Morador Responsável *' : 'Autorizado por (Morador) *')
                      }
                    </label>
                    <Select
                      options={filteredResidents.map((resident) => {
                        let locationSuffix = '';
                        if (selectedCustomer?.type === 'empresa' || selectedCustomer?.type === 'fabrica') {
                          // Show only PRIMARY dept in label (most recently created role first)
                          const staffRoles: any[] = [...(resident.roles || [])].filter((r: any) =>
                            r.role_type === 'staff' || r.role_type === 'employee'
                          ).reverse(); // reverse so most recent role is checked first
                          let primaryDeptName = '';
                          for (const role of staffRoles) {
                            if (primaryDeptName) break;
                            const meta = role.role_metadata || {};
                            if (Array.isArray(meta.departments)) {
                              const real = meta.departments.find((d: any) => d.name !== 'Sem Departamento');
                              if (real?.name) { primaryDeptName = real.name; break; }
                            }
                            const idsToCheck: string[] = [
                              ...(Array.isArray(meta.department_ids) ? meta.department_ids : []),
                              ...(meta.department_id ? [meta.department_id] : []),
                            ];
                            for (const id of idsToCheck) {
                              const dept = departments.find(d => d.id === id && d.name !== 'Sem Departamento');
                              if (dept) { primaryDeptName = dept.name; break; }
                            }
                          }
                          if (primaryDeptName) locationSuffix = ` - ${primaryDeptName}`;
                        } else {
                          // For condo: show block/unit + relation type
                          const residentRole = resident.roles?.find((r: any) => r.role_type === 'resident');
                          const unitInfo = residentRole?.role_metadata?.unit_info;
                          const firstAssignment = (residentRole?.role_metadata?.unit_assignments as any[])?.[0];
                          const relType = (resident as any).relation_type ||
                            residentRole?.role_metadata?.relation_type ||
                            firstAssignment?.relation_type;
                          const RELATION_SHORT: Record<string, string> = {
                            proprietario: 'Prop.', inquilino: 'Inq.',
                            dependente: 'Dep.', familiar: 'Fam.', conjuge: 'Cônjuge',
                          };
                          // Name the unit this pick is FOR. Labelling every row with the
                          // resident's primary unit meant filtering to 148 and reading
                          // "UNIDADES 102" next to the only name on the list.
                          const heldUnits = collectResidentUnits(resident.roles, lookupLoadedUnit);
                          const shownUnit = resolveUnitAfterResidentSelection(selectedUnit?.value, heldUnits);
                          if (shownUnit) {
                            locationSuffix = ` - ${shownUnit.label.replace(' - ', ' ')}`;
                            if (heldUnits.length > 1) locationSuffix += ` +${heldUnits.length - 1}`;
                          } else if (unitInfo && unitInfo.block) {
                            locationSuffix = ` - ${unitInfo.block.name} ${unitInfo.number || unitInfo.name}`;
                          } else if (firstAssignment) {
                            const blockPart = firstAssignment.block_name ? `${firstAssignment.block_name} ` : '';
                            const unitPart = firstAssignment.unit_number || firstAssignment.number || '';
                            locationSuffix = blockPart || unitPart ? ` - ${blockPart}${unitPart}` : '';
                          }
                          if (relType) locationSuffix += ` (${RELATION_SHORT[relType] ?? relType})`;
                        }

                        return {
                          value: resident.id,
                          label: `${resident.first_name} ${resident.last_name}${locationSuffix}`
                        };
                      })}
                      value={selectedResponsible}
                      onChange={(option: SingleValue<{ value: string; label: string }>) => {
                        setSelectedResponsible(option);
                        setValue('authorized_by_resident_id', option?.value || '');

                        if (!option) {
                          setPersonAvailableUnits([]);
                        } else {
                          // Auto-select block/unit or department when person is selected
                          const resident = filteredResidents.find(r => r.id === option.value);
                          if (resident) {
                            if (selectedCustomer?.type === 'empresa' || selectedCustomer?.type === 'fabrica') {
                              // Collect ALL real depts from all roles (most recent first for primary)
                              const staffRolesAll: any[] = [...(resident.roles || [])].filter((r: any) =>
                                r.role_type === 'staff' || r.role_type === 'employee'
                              );
                              const allDeptsMap = new Map<string, { value: string; label: string }>();
                              // Reversed = most recent first (primary inserted first into Map)
                              for (const role of [...staffRolesAll].reverse()) {
                                const meta = role.role_metadata || {};
                                if (Array.isArray(meta.departments)) {
                                  for (const d of meta.departments) {
                                    if (d.id && d.name && d.name !== 'Sem Departamento') {
                                      allDeptsMap.set(d.id, { value: d.id, label: d.name });
                                    }
                                  }
                                }
                                const idsToCheck: string[] = [
                                  ...(Array.isArray(meta.department_ids) ? meta.department_ids : []),
                                  ...(meta.department_id ? [meta.department_id] : []),
                                ];
                                for (const id of idsToCheck) {
                                  const dept = departments.find(d => d.id === id && d.name !== 'Sem Departamento');
                                  if (dept) allDeptsMap.set(dept.id, { value: dept.id, label: dept.name });
                                }
                              }
                              const availableDepts = Array.from(allDeptsMap.values());
                              setPersonAvailableUnits(availableDepts);
                              // Same rule as units: a person in several departments must not have
                              // the operator's choice replaced by their first one.
                              const primaryDept = resolveUnitAfterResidentSelection(selectedUnit?.value, availableDepts);
                              if (primaryDept) {
                                setSelectedUnit({ value: primaryDept.value, label: primaryDept.label });
                              }
                            } else {
                              // Every unit the resident holds — unit_info is only their PRIMARY
                              // one, the rest live in unit_assignments (see collectResidentUnits).
                              // Reading just unit_info is what made a multi-unit morador look
                              // like a one-unit morador and snapped the field back to 102.
                              const availableUnits = collectResidentUnits(resident.roles, lookupLoadedUnit);
                              setPersonAvailableUnits(availableUnits);
                              // Keep the unit the operator already chose when this resident holds
                              // it — see resolveUnitAfterResidentSelection. Overwriting it with
                              // their first unit is how a visit got registered against unit 102
                              // when the operator had deliberately picked 148.
                              const primary = resolveUnitAfterResidentSelection(selectedUnit?.value, availableUnits);
                              if (primary) {
                                setSelectedUnit(primary);
                                setSelectedBlock({ value: primary.blockId || '', label: primary.blockName || '' });

                                // Auto-populate Rua + Número if the unit has a street
                                const sourceUnits = allCustomerUnits.length > 0 ? allCustomerUnits : units;
                                const fullUnit = sourceUnits.find((u: any) => u.id === primary.value);
                                if (fullUnit?.street?.id) {
                                  setSelectedStreet({ value: fullUnit.street.id, label: fullUnit.street.name });
                                  setStreetNumberSearch(fullUnit.street_number || '');
                                } else {
                                  setSelectedStreet(null);
                                  setStreetNumberSearch('');
                                }
                              }
                            }
                          }
                        }
                      }}

                      onInputChange={(inputValue, actionMeta) => {
                        // Update search term for all input actions
                        setResidentSearchTerm(inputValue);
                      }}
                      defaultMenuIsOpen={false}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      maxMenuHeight={200}
                      placeholder={
                        selectedUnit
                          ? (selectedCustomer?.type === 'empresa' || selectedCustomer?.type === 'fabrica' ? 'Selecione a pessoa responsável...' : 'Selecione o morador responsável...')
                          : (selectedCustomer?.type === 'empresa' || selectedCustomer?.type === 'fabrica'
                              ? 'Selecione departamento ou digite para buscar'
                              : 'Selecione bloco/unidade primeiro ou digite para buscar')
                      }
                      noOptionsMessage={() => isLoadingResidents ? "Carregando..." : (residentSearchTerm || selectedUnit) ? "Nenhum resultado encontrado" : (selectedCustomer?.type === 'empresa' || selectedCustomer?.type === 'fabrica' ? "Selecione departamento ou digite para buscar" : "Selecione unidade ou digite para buscar")}
                      isClearable
                      isSearchable
                      isLoading={isLoadingResidents}
                      filterOption={() => true} // Disable client-side filtering - backend handles all search
                      classNames={{
                        control: (state) =>
                          `!shadow-sm !rounded-md ${
                            darkMode
                              ? `!bg-gray-800 !border-gray-600 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`
                              : `!border-gray-300 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`
                          }`,
                        input: () => `!text-sm ${darkMode ? '!text-white' : ''}`,
                        option: (state) => `!text-sm ${
                          darkMode
                            ? state.isSelected ? '!bg-primary-600' : state.isFocused ? '!bg-gray-700' : '!bg-gray-800'
                            : ''
                        }`,
                        placeholder: () => `!text-sm ${darkMode ? '!text-gray-500' : '!text-gray-400'}`,
                        singleValue: () => `!text-sm ${darkMode ? '!text-white' : ''}`,
                        menu: () => darkMode ? '!bg-gray-800 !border-gray-600' : '',
                        menuList: () => darkMode ? '!bg-gray-800' : '',
                      }}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '38px',
                          backgroundColor: darkMode ? '#1f2937' : base.backgroundColor,
                          borderColor: darkMode ? '#4b5563' : base.borderColor,
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: darkMode ? '#1f2937' : base.backgroundColor,
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: darkMode
                            ? state.isSelected ? '#7c3aed' : state.isFocused ? '#374151' : '#1f2937'
                            : state.isSelected ? '#7c3aed' : state.isFocused ? '#f3f4f6' : base.backgroundColor,
                          color: darkMode || state.isSelected ? '#fff' : base.color,
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: darkMode ? '#fff' : base.color,
                        }),
                        input: (base) => ({
                          ...base,
                          color: darkMode ? '#fff' : base.color,
                        }),
                      }}
                    />
                    <input
                      type="hidden"
                      {...register('authorized_by_resident_id', { required: 'Campo obrigatório' })}
                    />
                    {errors.authorized_by_resident_id && !selectedResponsible && (
                      <p className="mt-1 text-sm text-red-500">{String(errors.authorized_by_resident_id.message || "")}</p>
                    )}

                    {selectedCustomer?.mobileAppEnabled && selectedResponsible && (
                      <label className="mt-2 flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={waitForAppApproval}
                          onChange={(e) => setWaitForAppApproval(e.target.checked)}
                          className="mt-0.5"
                        />
                        <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Aguardar aprovação do responsável via app — em vez de liberar o acesso
                          agora, {selectedResponsible.label} receberá uma notificação para
                          autorizar ou negar antes da entrada.
                        </span>
                      </label>
                    )}

                    {/* Multi-unit/dept sub-selector */}
                    {selectedResponsible && personAvailableUnits.length > 1 && (
                      <div className="mt-2">
                        <p className={`text-[10px] font-semibold tracking-widest uppercase mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {selectedCustomer?.type === 'empresa' || selectedCustomer?.type === 'fabrica' ? 'Departamento da visita' : 'Unidade da visita'}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {personAvailableUnits.map(unit => (
                            <button
                              key={unit.value}
                              type="button"
                              onClick={() => {
                                setSelectedUnit(unit);
                                if (unit.blockId) setSelectedBlock({ value: unit.blockId, label: unit.blockName || '' });
                              }}
                              className={`px-2.5 py-0.5 text-xs rounded-full border transition-all ${
                                selectedUnit?.value === unit.value
                                  ? 'bg-violet-600 border-violet-600 text-white font-semibold'
                                  : darkMode
                                    ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-violet-400'
                                    : 'bg-white border-gray-300 text-gray-600 hover:border-violet-400 hover:text-violet-600'
                              }`}
                            >
                              {unit.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Visita a múltiplos destinos — the other unidades/departamentos of THIS
                      visit. Absent unless the customer turned it on. Sits under the destination
                      and the responsible person because that is the order it is filled in:
                      where the visitor is going first, then "and also…". */}
                  {multiDestinationEnabled && (
                    <div className="mt-3">
                      <VisitDestinationList
                        enabled
                        destinations={visitDestinations}
                        onChange={handleDestinationsChange}
                        onPick={handlePickDestination}
                      />
                      {pickingDestination && (
                        <div className="mt-2">
                          <Select
                            aria-label={isEmpresaCustomer ? 'Adicionar outro destino (departamento)' : 'Adicionar outro destino (unidade)'}
                            options={availableDestinationOptions}
                            value={null}
                            onChange={(option: SingleValue<{ value: string; label: string; kind: 'unit' | 'department' }>) => {
                              setPickingDestination(false);
                              if (!option) return;
                              handleDestinationsChange(
                                addDestination(visitDestinations, {
                                  id: option.value,
                                  label: option.label,
                                  kind: option.kind,
                                })
                              );
                            }}
                            placeholder={isEmpresaCustomer ? 'Buscar departamento...' : 'Buscar unidade...'}
                            noOptionsMessage={() => isEmpresaCustomer ? 'Nenhum departamento encontrado' : 'Nenhuma unidade encontrada'}
                            isClearable
                            isSearchable
                            menuPortalTarget={document.body}
                            classNames={{
                              control: (state) =>
                                `!shadow-sm !rounded-md ${
                                  darkMode
                                    ? `!bg-gray-800 !border-gray-600 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`
                                    : `!border-gray-300 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`
                                }`,
                              input: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
                              placeholder: () => `!text-sm ${darkMode ? '!text-gray-500' : '!text-gray-400'}`,
                              menu: () => darkMode ? '!bg-gray-800 !border-gray-600' : '!bg-white',
                              menuList: () => darkMode ? '!bg-gray-800' : '!bg-white',
                            }}
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: '38px',
                                backgroundColor: darkMode ? '#1f2937' : base.backgroundColor,
                              }),
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              menu: (base) => ({
                                ...base,
                                backgroundColor: darkMode ? '#1f2937' : base.backgroundColor,
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: darkMode
                                  ? state.isFocused ? '#374151' : '#1f2937'
                                  : state.isFocused ? '#f3f4f6' : '#ffffff',
                                color: darkMode ? '#e5e7eb' : '#111827',
                              }),
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Selected Morador Info Display */}
                  {selectedResponsible && (() => {
                    const selectedPerson = residents.find(r => r.id === selectedResponsible.value);
                    if (!selectedPerson) return null;

                    // Debug: Log available fields to understand why photo isn't showing
                    logger.debug('visitor.modal', 'Selected person data', {
                      id: selectedPerson.id,
                      name: `${selectedPerson.first_name} ${selectedPerson.last_name}`,
                      profile_picture_url: selectedPerson.profile_picture_url,
                      identity_photo_url: selectedPerson.identity_photo_url,
                      identity_photo: selectedPerson.identity_photo,
                      allFields: Object.keys(selectedPerson)
                    });

                    // Get photo URL with proper absolute path resolution
                    // Check all possible photo field locations
                    const rawPhotoUrl = selectedPerson.profile_picture_url ||
                                       selectedPerson.identity_photo_url ||
                                       selectedPerson.identity_photo?.photo_url ||
                                       (selectedPerson as any).photo_url ||
                                       (selectedPerson as any).identity_photos?.[0]?.photo_url;
                    // Only use photo if it's a non-empty string
                    const photoUrl = (rawPhotoUrl && rawPhotoUrl.trim()) ? getAbsoluteImageUrl(rawPhotoUrl) : null;

                    const fullName = `${selectedPerson.first_name} ${selectedPerson.last_name}`;
                    const unitInfo = selectedPerson.unit_residents?.[0] ||
                                    selectedPerson.employee_departments?.[0] ||
                                    selectedPerson.roles?.find((r: any) => r.role_type === 'resident' || r.role_type === 'employee');

                    return (
                      <div
                        className={cn(
                          "p-4 rounded-lg border mt-4",
                          darkMode ? "bg-gray-800/50 border-gray-700" : "bg-gray-50 border-gray-200"
                        )}
                      >
                        <div className="flex items-center gap-4">
                          {/* Photo */}
                          <div className="flex-shrink-0">
                            {photoUrl ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setZoomPhotoUrl(photoUrl);
                                  setZoomPhotoTitle(fullName);
                                  setZoomPhotoActions(null);
                                }}
                                className="block cursor-zoom-in transition-opacity hover:opacity-80"
                                title="Ampliar foto"
                              >
                                <img
                                  src={photoUrl}
                                  alt={fullName}
                                  className="w-[51px] h-16 rounded-md object-cover border-2 border-gray-300"
                                />
                              </button>
                            ) : (
                              <div className={cn(
                                "w-[51px] h-16 rounded-md flex items-center justify-center text-xl font-bold border-2",
                                darkMode ? "bg-gray-700 border-gray-600 text-gray-300" : "bg-gray-200 border-gray-300 text-gray-600"
                              )}>
                                {selectedPerson.first_name?.[0]}{selectedPerson.last_name?.[0]}
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className={cn("text-xs mb-1 flex items-center gap-2 flex-wrap", darkMode ? "text-gray-400" : "text-gray-500")}>
                              <span><span className="font-medium">Nome: </span>
                              <span className={cn("font-semibold", darkMode ? "text-white" : "text-gray-900")}>{fullName}</span></span>
                              {(() => {
                                const RELATION_LABELS: Record<string, string> = {
                                  proprietario: 'Proprietário', inquilino: 'Inquilino',
                                  dependente: 'Dependente', familiar: 'Familiar', conjuge: 'Cônjuge',
                                };
                                const resRole = selectedPerson.roles?.find((r: any) => r.role_type === 'resident');
                                const rel = resRole?.role_metadata?.relation_type ||
                                  (resRole?.role_metadata?.unit_assignments as any[])?.[0]?.relation_type ||
                                  (selectedPerson as any).relation_type;
                                if (!rel) return null;
                                return (
                                  <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase tracking-wide",
                                    darkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-500"
                                  )}>
                                    {RELATION_LABELS[rel] ?? rel}
                                  </span>
                                );
                              })()}
                            </div>
                            {selectedPerson.cpf && (
                              <p className={cn("text-xs mb-1", darkMode ? "text-gray-400" : "text-gray-600")}>
                                <span className="font-medium">CPF: </span>{maskCPF(selectedPerson.cpf, [])}
                              </p>
                            )}
                            <div className={cn("text-xs mb-1 flex items-center gap-2 flex-wrap", darkMode ? "text-gray-400" : "text-gray-600")}>
                              {selectedPerson.phone ? (
                                <span><span className="font-medium">Telefone: </span>{selectedPerson.phone}</span>
                              ) : addingResidentPhone ? (
                                <div className="flex items-center gap-1.5 w-full">
                                  <input
                                    type="tel"
                                    value={residentPhoneInput}
                                    onChange={e => setResidentPhoneInput(e.target.value)}
                                    placeholder="(00) 00000-0000"
                                    autoFocus
                                    className={cn(
                                      "flex-1 rounded-md px-2.5 py-1.5 text-xs border focus:outline-none focus:ring-1 focus:ring-blue-500",
                                      darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                                    )}
                                    onKeyDown={e => {
                                      if (e.key === 'Escape') { setAddingResidentPhone(false); setResidentPhoneInput(''); }
                                    }}
                                  />
                                  <button
                                    type="button"
                                    disabled={savingResidentPhone || residentPhoneInput.replace(/\D/g, '').length < 10}
                                    onClick={async () => {
                                      if (!selectedPerson?.id || !customerId) return;
                                      setSavingResidentPhone(true);
                                      try {
                                        await condoApi.updatePerson(customerId, selectedPerson.id, { phone: residentPhoneInput });
                                        selectedPerson.phone = residentPhoneInput;
                                        setAddingResidentPhone(false);
                                        setResidentPhoneInput('');
                                        toast.success('Telefone salvo');
                                      } catch { toast.error('Erro ao salvar telefone'); }
                                      finally { setSavingResidentPhone(false); }
                                    }}
                                    className={cn(
                                      "flex-shrink-0 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-all",
                                      residentPhoneInput.replace(/\D/g, '').length >= 10
                                        ? "bg-blue-600 hover:bg-blue-500 text-white"
                                        : darkMode ? "bg-gray-700 text-gray-500 cursor-not-allowed" : "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    )}
                                  >
                                    {savingResidentPhone ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Salvar'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => { setAddingResidentPhone(false); setResidentPhoneInput(''); }}
                                    className={cn("flex-shrink-0 p-1 rounded transition-colors", darkMode ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600")}
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setAddingResidentPhone(true)}
                                  className={cn(
                                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border transition-all",
                                    darkMode
                                      ? "border-dashed border-gray-600 text-gray-400 hover:border-blue-500 hover:text-blue-400 hover:bg-blue-500/10"
                                      : "border-dashed border-gray-300 text-gray-400 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50"
                                  )}
                                >
                                  <Phone className="h-3 w-3" />
                                  Inserir número de telefone
                                </button>
                              )}
                              {selectedPerson.phone && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();

                                    const visitorName = [watch('first_name'), watch('last_name')].filter(Boolean).join(' ') || 'Visitante';

                                    // Get destination from selected person's unit or department
                                    let destination = '';
                                    const personUnitInfo = selectedPerson.unit_residents?.[0] ||
                                                          selectedPerson.employee_departments?.[0] ||
                                                          selectedPerson.roles?.find((r: any) => r.role_type === 'resident' || r.role_type === 'employee');

                                    if (personUnitInfo?.unit?.block?.name && personUnitInfo?.unit?.number) {
                                      destination = `${personUnitInfo.unit.block.name} ${personUnitInfo.unit.number}`;
                                    } else if (personUnitInfo?.department?.name) {
                                      destination = personUnitInfo.department.name;
                                    }

                                    const message = createWhatsAppMessage(
                                      fullName,
                                      visitorName,
                                      roleType,
                                      destination,
                                      selectedCustomer?.name || 'Condomínio',
                                      whatsappTemplate
                                    );

                                    openWhatsApp(selectedPerson.phone, message);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-all hover:shadow-md"
                                  style={{ backgroundColor: '#25D366' }}
                                  title="Enviar mensagem via WhatsApp"
                                >
                                  {/* WhatsApp Icon SVG */}
                                  <svg
                                    viewBox="0 0 24 24"
                                    className="h-4 w-4"
                                    fill="white"
                                  >
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                  </svg>
                                  <span className="text-xs font-semibold text-white">
                                    Avisar
                                  </span>
                                </button>
                              )}
                            </div>
                            <p className={cn(
                              "text-xs mt-1",
                              darkMode ? "text-blue-400" : "text-blue-600"
                            )}>
                              {/* Show block + unit for residents */}
                              {unitInfo?.unit?.block?.name && unitInfo?.unit?.number
                                ? `${unitInfo.unit.block.name} ${unitInfo.unit.number} · Morador`
                                : unitInfo?.department?.name
                                ? `${unitInfo.department.name} · Funcionário`
                                : unitInfo?.unit?.number || unitInfo?.unit?.name ||
                                  (selectedCustomer?.type === 'empresa' ? 'Funcionário' : 'Morador')
                              }
                            </p>
                            {unitInfo?.unit?.street?.name && (
                              <p className={cn(
                                "text-xs",
                                darkMode ? "text-gray-400" : "text-gray-500"
                              )}>
                                {unitInfo.unit.street_number
                                  ? `${unitInfo.unit.street.name}, ${unitInfo.unit.street_number}`
                                  : unitInfo.unit.street.name}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Role Type - Compact cards */}
                  <div>
                    <label className={`block text-[11px] font-semibold tracking-widest uppercase mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tipo de Pessoa *</label>
                    <div className="grid grid-cols-2 gap-3 mb-2">
                      {/* Visitor Card */}
                      <label className={`relative px-3 py-2 border-2 rounded-lg cursor-pointer transition-all ${
                        roleType === 'visitor'
                          ? darkMode
                            ? 'border-blue-500 bg-blue-500/20 shadow-lg ring-2 ring-blue-400/50'
                            : 'border-blue-500 bg-blue-50 shadow-lg ring-2 ring-blue-200'
                          : darkMode
                            ? 'border-gray-600 bg-gray-800 hover:border-gray-500 hover:shadow-md'
                            : 'border-gray-300 bg-white hover:border-gray-400 hover:shadow-md'
                      }`}>
                        <input type="radio" {...register('role_type', { required: 'Selecione o tipo de pessoa' })} value="visitor" className="absolute top-1.5 right-2" />
                        <div className="flex items-center justify-center text-center">
                          <h3 className={`text-sm font-bold uppercase tracking-widest transition-colors ${
                            roleType === 'visitor'
                              ? darkMode ? 'text-blue-300' : 'text-blue-800'
                              : darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Visitante
                          </h3>
                        </div>
                      </label>

                      {/* Service Provider Card */}
                      <label className={`relative px-3 py-2 border-2 rounded-lg cursor-pointer transition-all ${
                        roleType === 'service_provider'
                          ? darkMode
                            ? 'border-blue-500 bg-blue-500/20 shadow-lg ring-2 ring-blue-400/50'
                            : 'border-blue-500 bg-blue-50 shadow-lg ring-2 ring-blue-200'
                          : darkMode
                            ? 'border-gray-600 bg-gray-800 hover:border-gray-500 hover:shadow-md'
                            : 'border-gray-300 bg-white hover:border-gray-400 hover:shadow-md'
                      }`}>
                        <input
                          type="radio"
                          {...register('role_type', { required: 'Selecione o tipo de pessoa' })}
                          value="service_provider"
                          className="absolute top-1.5 right-2"
                          onChange={(e) => { register('role_type').onChange(e); setProviderSubType('single_visit'); }}
                        />
                        <div className="flex items-center justify-center text-center">
                          <h3 className={`text-sm font-bold uppercase tracking-widest transition-colors ${
                            roleType === 'service_provider'
                              ? darkMode ? 'text-blue-300' : 'text-blue-800'
                              : darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Prestador de Serviço
                          </h3>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Type-Specific Configuration — Prestador de Serviço */}
                  {roleType === 'service_provider' && (
                    <div className={`rounded-lg p-4 border ${
                      darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <h3 className={`text-[11px] font-semibold tracking-widest uppercase mb-3 ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Agenda de Acesso *
                      </h3>

                      {/* Sub-type selector */}
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {([
                          { value: 'single_visit', label: 'Visita Única' },
                        ] as const).map(({ value, label }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setProviderSubType(value)}
                            className={`px-2 py-1.5 rounded-md text-xs font-medium border transition-all ${
                              providerSubType === value
                                ? darkMode
                                  ? 'bg-blue-600 border-blue-500 text-white'
                                  : 'bg-blue-600 border-blue-600 text-white'
                                : darkMode
                                  ? 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                                  : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>

                      {/* Custom schedule only shown for Horário Personalizado */}
                      {providerSubType === 'custom' && (
                        <ScheduleInput
                          value={{ ...schedule, type: 'custom' }}
                          onChange={setSchedule}
                          darkMode={darkMode}
                          hideTypeSelector
                        />
                      )}
                    </div>
                  )}

                  {/* "Visitante Sempre Habilitado" and "Período de Acesso Personalizado" used to
                      live here, as two toggle cards plus their date/grade bodies. Removed at the
                      user's request — this form registers a visit, and both modes are set from the
                      morador app (liberações) or from the person's own record. `isAlwaysEnabled` and
                      `useCustomPeriodAccess` therefore stay false for everything submitted here. */}

                  {/* Company — only for service providers */}
                  {roleType === 'service_provider' && (() => {
                    const filtered = contractors.filter(c =>
                      c.name.toLowerCase().includes(contractorSearch.toLowerCase())
                    );
                    const selectedName = contractors.find(c => c.id === contractorId)?.name ?? '';
                    const noExactMatch = contractorSearch.trim() !== '' &&
                      !contractors.some(c => c.name.toLowerCase() === contractorSearch.trim().toLowerCase());
                    // Porteiro can only create a new contractor if the customer setting allows it —
                    // other roles (admin/supervisor/sindico) are always allowed, mirroring the backend.
                    const canCreateContractor = !roles?.includes('porteiro') || porteiroCanManageContractors;

                    const doCreate = async (name: string) => {
                      if (!name.trim() || creatingContractor) return;
                      setCreatingContractor(true);
                      try {
                        const res = await condoApi.createContractor(customerId, {
                          name: name.trim(), responsible: '', phone: '', email: '', notes: '',
                        });
                        const created = res.data?.data ?? res.data;
                        const refreshed = await condoApi.listContractors(customerId);
                        const list = refreshed.data?.data ?? [];
                        setContractors(list);
                        const match = list.find((c: { id: string; name: string }) => c.name === name.trim());
                        if (match || created?.id) setContractorId(match?.id ?? created?.id ?? '');
                        setContractorSearch('');
                        setContractorDropdownOpen(false);
                      } catch { /* silent */ } finally {
                        setCreatingContractor(false);
                      }
                    };

                    return (
                      <div className="grid grid-cols-2 gap-4">
                        {/* Empresa */}
                        <div>
                        <label className={`block text-[11px] font-semibold tracking-widest uppercase mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Empresa
                        </label>
                        <div ref={contractorComboRef} className="relative">

                          {/* Selected state — confirmed chip */}
                          {contractorId && !contractorDropdownOpen ? (
                            <button
                              type="button"
                              onClick={() => { setContractorSearch(''); openContractorDropdown(); }}
                              className={`w-full flex items-center gap-2.5 rounded-lg py-2 pl-3 pr-2.5 text-sm border-2 transition-all group ${
                                darkMode
                                  ? 'bg-emerald-950/50 border-emerald-700/70 hover:border-emerald-500'
                                  : 'bg-emerald-50 border-emerald-300 hover:border-emerald-400'
                              }`}
                            >
                              <CheckCircle className={`h-4 w-4 flex-shrink-0 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                              <span className={`flex-1 text-left font-semibold truncate ${darkMode ? 'text-emerald-200' : 'text-emerald-800'}`}>
                                {selectedName}
                              </span>
                              <span
                                role="button"
                                onClick={e => { e.stopPropagation(); setContractorId(''); setContractorSearch(''); }}
                                className={`p-0.5 rounded transition-colors ${darkMode ? 'text-emerald-600 hover:text-emerald-300 hover:bg-emerald-900/50' : 'text-emerald-500 hover:text-emerald-700 hover:bg-emerald-100'}`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </span>
                            </button>
                          ) : (
                            /* Search / open input */
                            <div className="relative">
                              <input
                                type="text"
                                value={contractorSearch}
                                placeholder={canCreateContractor ? "Buscar ou criar empresa..." : "Buscar empresa..."}
                                autoFocus={contractorDropdownOpen}
                                onChange={e => {
                                  setContractorSearch(e.target.value);
                                  setContractorId('');
                                  setContractorDropdownOpen(true);
                                }}
                                onFocus={() => openContractorDropdown()}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (filtered.length > 0) {
                                      setContractorId(filtered[0].id);
                                      setContractorSearch('');
                                      setContractorDropdownOpen(false);
                                    } else if (contractorSearch.trim() && canCreateContractor) {
                                      doCreate(contractorSearch.trim());
                                    }
                                  } else if (e.key === 'Escape') {
                                    setContractorDropdownOpen(false);
                                    setContractorSearch('');
                                  }
                                }}
                                className={`w-full rounded-lg py-2 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                  darkMode
                                    ? 'bg-gray-800 border border-gray-600 text-white placeholder-gray-500'
                                    : 'border border-gray-300 text-gray-900 placeholder-gray-400 bg-white'
                                }`}
                              />
                              <span className={`absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                              </span>
                            </div>
                          )}

                          {/* Dropdown — portal + position:fixed so the modal's overflow-y-auto
                              body can never clip it (field sits right above the footer) */}
                          {contractorDropdownOpen && contractorDropdownPos && ReactDOM.createPortal(
                            <div
                              ref={contractorDropdownRef}
                              className={cn(
                                "fixed z-[200] rounded-lg border shadow-xl overflow-hidden",
                                darkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200"
                              )}
                              style={{
                                left: contractorDropdownPos.left,
                                width: contractorDropdownPos.width,
                                top: contractorDropdownPos.top,
                                bottom: contractorDropdownPos.bottom,
                              }}
                            >
                              <div className="max-h-52 overflow-y-auto">
                                {/* None option */}
                                <button
                                  type="button"
                                  onClick={() => { setContractorId(''); setContractorSearch(''); setContractorDropdownOpen(false); }}
                                  className={`w-full text-left px-3 py-2 text-sm transition-colors italic ${
                                    darkMode ? 'text-gray-500 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-50'
                                  }`}
                                >
                                  — Nenhuma —
                                </button>

                                {/* Company list — always show filtered (all when search empty) */}
                                {filtered.map((c, i) => (
                                  <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => { setContractorId(c.id); setContractorSearch(''); setContractorDropdownOpen(false); }}
                                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                                      c.id === contractorId
                                        ? (darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700')
                                        : contractorSearch.trim() && i === 0
                                          ? (darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900')
                                          : (darkMode ? 'text-white hover:bg-gray-700' : 'text-gray-900 hover:bg-gray-50')
                                    }`}
                                  >
                                    {c.id === contractorId && <CheckCircle className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" />}
                                    {c.name}
                                  </button>
                                ))}

                                {/* No results message */}
                                {contractorSearch.trim() && filtered.length === 0 && (
                                  <p className={`px-3 py-2 text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    Nenhuma empresa encontrada
                                  </p>
                                )}
                              </div>

                              {/* Create new option */}
                              {noExactMatch && canCreateContractor && (
                                <button
                                  type="button"
                                  disabled={creatingContractor}
                                  onClick={() => doCreate(contractorSearch.trim())}
                                  className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 border-t font-medium transition-colors disabled:opacity-50 ${
                                    darkMode
                                      ? 'border-gray-700 text-blue-400 hover:bg-gray-700'
                                      : 'border-gray-100 text-blue-600 hover:bg-blue-50'
                                  }`}
                                >
                                  {creatingContractor
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
                                    : <Plus className="h-3.5 w-3.5 flex-shrink-0" />
                                  }
                                  Criar &ldquo;{contractorSearch.trim()}&rdquo; como nova empresa
                                </button>
                              )}
                            </div>,
                            document.getElementById('modal-root') ?? document.body
                          )}
                        </div>
                        </div>{/* /Empresa col */}

                        {/* Tipo de Serviço */}
                        <div>
                          <label className={`block text-[11px] font-semibold tracking-widest uppercase mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Tipo de Serviço
                          </label>
                          <input
                            {...register('purpose')}
                            type="text"
                            placeholder="Ex: Manutenção..."
                            className={`block w-full rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              darkMode
                                ? 'bg-gray-800 border border-gray-600 text-white placeholder-gray-500'
                                : 'border border-gray-300 text-gray-900 placeholder-gray-400'
                            }`}
                          />
                        </div>
                      </div>
                    );
                  })()}

                  {/* Motivo da Visita — visitor only (service_provider has it in the empresa row) */}
                  {roleType === 'visitor' && (
                    <div>
                      <label className={`block text-[11px] font-semibold tracking-widest uppercase mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Motivo da Visita
                      </label>
                      <input
                        {...register('purpose')}
                        type="text"
                        placeholder="Ex: Visita social..."
                        className={`block w-full rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          darkMode
                            ? 'bg-gray-800 border border-gray-600 text-white placeholder-gray-500'
                            : 'border border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  )}

                {/* Custom fields are now rendered inside the configurable fields grid above */}

                </div>
              </div>
            </form>
          )}
          </div>

          {/* Footer - Fixed at bottom */}
          {currentStep === 'form' && (
            <div className={`flex-shrink-0 border-t ${
              darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
            }`}>
            <div className="flex justify-end space-x-3 p-4">
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${
                  darkMode
                    ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                Cancelar
              </button>
              {/* On screen, not in a tooltip: a disabled button with no visible reason reads as
                  a broken system, and nobody hovers a control that looks dead. */}
              {onPreRegistrationCreated && !canPreRegSubmit && (
                <p
                  data-testid="pre-cadastro-missing"
                  role="status"
                  className={`text-xs font-medium mr-auto max-w-[22rem] ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}
                >
                  {formatMissingRequirements(preRegMissing)}
                </p>
              )}
              {onPreRegistrationCreated && (
                <button
                  type="button"
                  disabled={!canPreRegSubmit || isActuallySubmitting || isSubmitting}
                  onClick={handlePreCadastroSubmit}
                  // A disabled button with no explanation is the operator staring at a form
                  // wondering which field it wants. Names every outstanding requirement at once.
                  title={
                    canPreRegSubmit
                      ? undefined
                      : `Faltam: ${preRegMissing.join(', ')}`
                  }
                  className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium transition-all flex items-center gap-2 ${
                    canPreRegSubmit && !isActuallySubmitting && !isSubmitting
                      ? 'text-white bg-blue-600 hover:bg-blue-700 cursor-pointer'
                      : darkMode
                      ? 'text-gray-500 bg-gray-700 cursor-not-allowed'
                      : 'text-gray-400 bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  <span>
                    {isActuallySubmitting ? 'Cadastrando...' : 'Pré-Cadastro'}
                  </span>
                </button>
              )}
              <button
                type="submit"
                form="visitor-form"
                disabled={!canSubmit || isSubmitting || isActuallySubmitting}
                className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium transition-all flex items-center gap-2 ${
                  canSubmit && !isSubmitting && !isActuallySubmitting
                    ? 'text-white bg-green-600 hover:bg-green-700 cursor-pointer'
                    : darkMode
                    ? 'text-gray-500 bg-gray-700 cursor-not-allowed'
                    : 'text-gray-400 bg-gray-300 cursor-not-allowed'
                }`}
              >
                <span>Liberar Acesso</span>
                {canSubmit && !isSubmitting && !isActuallySubmitting && (
                  <kbd className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold bg-white/20 text-white rounded border border-white/30">
                    ENTER
                  </kbd>
                )}
              </button>
            </div>
          </div>
          )}
        </div>
  );

  return (
    <>
      {/* Inject scrollbar styles */}
      <style>{getScrollbarStyles(darkMode)}</style>

      {embedded ? (
        /* Embedded mode: render content directly without overlay */
        modalContent
      ) : (
        /* Modal mode: render with overlay */
        <div className={`fixed inset-0 ${darkMode ? 'bg-black bg-opacity-80' : 'bg-gray-500 bg-opacity-75'} flex items-center justify-center p-4 z-[95]`}>
          {modalContent}
        </div>
      )}

      {/* All-photos editor */}
      <AllPhotosEditModal
        open={showAllPhotosModal}
        onClose={() => setShowAllPhotosModal(false)}
        onSave={(photos) => {
          if (photos.identity)       setCapturedPhoto(photos.identity);
          if (photos.document_front) setDocumentFrontPhoto(photos.document_front);
          if (photos.document_back)  setDocumentBackPhoto(photos.document_back);
          if (photos.vehicle)        setVehiclePhoto(photos.vehicle);
        }}
        currentPhotos={{
          identity:       capturedPhoto,
          document_front: documentFrontPhoto,
          document_back:  documentBackPhoto,
          vehicle:        vehiclePhoto,
        }}
        customerId={customerId}
        darkMode={darkMode}
      />

      {/* Photo Modals for ID Photo */}
      <PhotoUploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onConfirm={handlePhotoCapture}
        title="Upload de Foto"
        cropShape="rect"
        aspectRatio={4 / 5}
        darkMode={darkMode}
      />
      <PhotoWebcamModal
        open={showWebcamModal}
        onClose={() => setShowWebcamModal(false)}
        onConfirm={handlePhotoCapture}
        title="Captura via Webcam"
        cropShape="rect"
        aspectRatio={4 / 5}
        darkMode={darkMode}
      />
      <PhotoIPCameraModal
        open={showIDPhotoModal}
        onClose={() => setShowIDPhotoModal(false)}
        onConfirm={handlePhotoCapture}
        title="Captura via Câmera IP"
        customerId={customerId}
        filterCategory="captura_facial"
        cropShape="rect"
        aspectRatio={4 / 5}
        darkMode={darkMode}
      />

      {/* Document Capture Modals - Front */}
      <PhotoUploadModal
        open={showDocFrontUploadModal}
        onClose={() => setShowDocFrontUploadModal(false)}
        onConfirm={handleDocumentFrontCapture}
        title="Upload - Frente do Documento"
        cropShape="rect"
        aspectRatio={3 / 2}
        darkMode={darkMode}
      />
      <PhotoWebcamModal
        open={showDocFrontWebcamModal}
        onClose={() => setShowDocFrontWebcamModal(false)}
        onConfirm={handleDocumentFrontCapture}
        title="Webcam - Frente do Documento"
        cropShape="rect"
        aspectRatio={3 / 2}
        darkMode={darkMode}
      />
      <PhotoIPCameraModal
        open={showDocumentFrontModal}
        onClose={() => setShowDocumentFrontModal(false)}
        onConfirm={handleDocumentFrontCapture}
        title="Documento - Frente"
        customerId={customerId}
        filterCategory="documento"
        cropShape="rect"
        aspectRatio={3 / 2}
        darkMode={darkMode}
      />

      {/* Document Capture Modals - Back */}
      <PhotoUploadModal
        open={showDocBackUploadModal}
        onClose={() => setShowDocBackUploadModal(false)}
        onConfirm={handleDocumentBackCapture}
        title="Upload - Verso do Documento"
        cropShape="rect"
        aspectRatio={3 / 2}
        darkMode={darkMode}
      />
      <PhotoWebcamModal
        open={showDocBackWebcamModal}
        onClose={() => setShowDocBackWebcamModal(false)}
        onConfirm={handleDocumentBackCapture}
        title="Webcam - Verso do Documento"
        cropShape="rect"
        aspectRatio={3 / 2}
        darkMode={darkMode}
      />
      <PhotoIPCameraModal
        open={showDocumentBackModal}
        onClose={() => setShowDocumentBackModal(false)}
        onConfirm={handleDocumentBackCapture}
        title="Documento - Verso"
        customerId={customerId}
        filterCategory="documento"
        cropShape="rect"
        aspectRatio={3 / 2}
        darkMode={darkMode}
      />

      {/* Vehicle Photo Capture Modals */}
      <PhotoUploadModal
        open={showVehicleUploadModal}
        onClose={() => setShowVehicleUploadModal(false)}
        onConfirm={handleVehiclePhotoCapture}
        title="Upload - Foto do Veículo"
        cropShape="rect"
        aspectRatio={VEHICLE_PHOTO_ASPECT}
        disableFaceDetection
        darkMode={darkMode}
      />
      <PhotoWebcamModal
        open={showVehicleWebcamModal}
        onClose={() => setShowVehicleWebcamModal(false)}
        onConfirm={handleVehiclePhotoCapture}
        title="Webcam - Foto do Veículo"
        cropShape="rect"
        aspectRatio={VEHICLE_PHOTO_ASPECT}
        darkMode={darkMode}
      />
      <PhotoIPCameraModal
        open={showVehiclePhotoModal}
        onClose={() => setShowVehiclePhotoModal(false)}
        onConfirm={handleVehiclePhotoCapture}
        title="Foto do Veículo"
        customerId={customerId}
        filterCategory="veiculo"
        cropShape="rect"
        // Was 16/9 while the slot below was 3/2, so a photo framed here did not match the hole
        // it was dropped into. One ratio, from utils/vehiclePhoto.
        aspectRatio={VEHICLE_PHOTO_ASPECT}
        darkMode={darkMode}
      />

      {/* Photo Zoom Modal — portaled to #modal-root (absolute, not fixed) to avoid the
       * SwiftShader black-paint bug when fixed positioning sits inside overflow-y-auto */}
      {zoomPhotoUrl && ReactDOM.createPortal(
        <div
          className="absolute inset-0 bg-black/80 z-[9999] flex items-center justify-center p-6"
          onClick={() => { setZoomPhotoUrl(null); setZoomPhotoActions(null); }}
        >
          <div
            className="relative max-w-[90vw] flex flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Title above the image */}
            {zoomPhotoTitle && (
              <div className="text-white font-semibold text-lg text-center">
                {zoomPhotoTitle}
              </div>
            )}

            {/* Image, with close button anchored to its corner */}
            <div className="relative">
              <img
                src={zoomPhotoUrl}
                alt={zoomPhotoTitle}
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              />
              <button
                onClick={() => { setZoomPhotoUrl(null); setZoomPhotoActions(null); }}
                className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white text-gray-700 shadow-lg hover:bg-gray-100 transition-colors"
                title="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Action buttons */}
            {zoomPhotoActions && (
              <div className="flex gap-3 mt-1">
                <button
                  onClick={() => { setZoomPhotoUrl(null); setZoomPhotoActions(null); zoomPhotoActions.onReplace(); }}
                  className="px-5 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-medium transition-colors"
                >
                  Trocar foto
                </button>
                <button
                  onClick={() => { setZoomPhotoUrl(null); setZoomPhotoActions(null); zoomPhotoActions.onRemove(); }}
                  className="px-5 py-2 rounded-lg bg-red-500/70 hover:bg-red-500/90 text-white text-sm font-medium transition-colors"
                >
                  Remover
                </button>
              </div>
            )}
          </div>
        </div>,
        document.getElementById('modal-root') ?? document.body
      )}

      {/* Re-entry Confirmation Modal */}
      <ReentryConfirmationModal
        isOpen={showReentryConfirmation}
        onClose={handleReentryCancel}
        onConfirm={handleReentryConfirm}
        onViewProfile={activeVisitInfo ? () => {
          setShowReentryConfirmation(false);
          onClose();
          setTimeout(() => openPersonDetail(activeVisitInfo.personId, false, darkMode), 150);
        } : undefined}
        variant={reentryVariant}
        visitorInfo={activeVisitInfo ? {
          personName: activeVisitInfo.personName,
          unit: activeVisitInfo.unit,
          entryTime: activeVisitInfo.entryTime
        } : {
          personName: '',
          unit: '',
          entryTime: ''
        }}
      />

      {/* Registration Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full flex items-center justify-center"
          style={{ zIndex: 9999 }}
          onClick={handleCancelConfirm}
        >
          <div
            className={`relative rounded-xl shadow-2xl w-full max-w-md mx-4 border ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isActuallySubmitting) {
                e.preventDefault();
                e.stopPropagation();
                if (!disclaimerAcknowledged) {
                  setDisclaimerAcknowledged(true);
                } else {
                  handleConfirmSubmit();
                }
              }
            }}
            tabIndex={0}
            ref={(el) => el?.focus()}
          >
            {/* Loading overlay for confirmation modal */}
            <FormLoadingOverlay
              isLoading={isActuallySubmitting}
              message="Cadastrando visitante..."
              darkMode={darkMode}
            />

            {/* Header */}
            <div className={`px-6 py-4 border-b ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            } flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-400" />
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Confirmar Cadastro de Visitante
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              <p className={`text-base leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Você está prestes a cadastrar um novo visitante no sistema.
              </p>
            </div>

            {/* Disclaimer Section */}
            <div className="px-6 pb-6">
              <div className={`border rounded-lg p-4 ${
                darkMode
                  ? 'bg-amber-900/20 border-amber-600/50'
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-start gap-2 mb-3">
                  <AlertTriangle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                    darkMode ? 'text-amber-300' : 'text-amber-600'
                  }`} />
                  <h4 className={`text-sm font-bold uppercase tracking-wide ${
                    darkMode ? 'text-amber-300' : 'text-amber-700'
                  }`}>
                    AVISO DE RESPONSABILIDADE
                  </h4>
                </div>
                <div className={`text-xs ${
                  darkMode ? 'text-amber-200' : 'text-amber-900'
                } leading-relaxed space-y-2`}>
                  <p>Ao cadastrar este visitante, você declara que:</p>
                  <ul className="space-y-1 ml-3">
                    <li>• Verificou a identidade e autorização da pessoa</li>
                    <li>• Confirma que as informações fornecidas são verdadeiras</li>
                    <li>• Está ciente que este cadastro será registrado em seu nome</li>
                    <li>• Assume responsabilidade pela veracidade dos dados</li>
                  </ul>
                  <p className="mt-3">
                    Este cadastro será registrado permanentemente vinculado ao seu usuário{' '}
                    <span className={`font-semibold ${
                      darkMode ? 'text-cyan-300' : 'text-cyan-700'
                    }`}>({operatorName})</span>.
                  </p>
                  <p className={`text-xs mt-2 ${
                    darkMode ? 'text-amber-300' : 'text-amber-700'
                  }`}>
                    Registro será criado em: {new Date().toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </p>
                </div>
                <div className={`mt-4 pt-3 border-t ${
                  darkMode ? 'border-gray-600' : 'border-amber-200'
                }`}>
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={disclaimerAcknowledged}
                      onChange={(e) => setDisclaimerAcknowledged(e.target.checked)}
                      className={`mt-1 h-4 w-4 rounded border cursor-pointer ${
                        darkMode
                          ? 'border-gray-500 bg-gray-700 text-blue-600'
                          : 'border-gray-300 bg-white text-blue-600'
                      } focus:ring-2 focus:ring-blue-500 focus:ring-offset-0`}
                    />
                    <span className="flex-1 flex items-center justify-between gap-2">
                      <span className={`text-xs transition-colors ${
                        darkMode
                          ? 'text-gray-300 group-hover:text-white'
                          : 'text-gray-700 group-hover:text-gray-900'
                      }`}>
                        Li e concordo com os termos de responsabilidade acima
                      </span>
                      {!disclaimerAcknowledged && (
                        <kbd className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold bg-blue-600 text-white rounded border border-blue-500 shadow-sm">
                          ENTER
                        </kbd>
                      )}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className={`px-6 py-4 rounded-b-xl flex justify-end gap-3 ${
              darkMode ? 'bg-gray-900' : 'bg-gray-50'
            }`}>
              <button
                onClick={handleCancelConfirm}
                disabled={isActuallySubmitting}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  darkMode
                    ? 'border border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600'
                    : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                }`}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmSubmit}
                disabled={isActuallySubmitting || !disclaimerAcknowledged}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                  disclaimerAcknowledged && !isActuallySubmitting
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                <span>{isActuallySubmitting ? 'Liberando...' : 'Confirmar Acesso'}</span>
                {!isActuallySubmitting && disclaimerAcknowledged && (
                  <kbd className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold bg-blue-500 text-white rounded border border-blue-400 shadow-sm">
                    ENTER
                  </kbd>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-registration entrance type picker */}
      {entranceTypePickerData && ReactDOM.createPortal(
        <div className="absolute inset-0 z-[9998] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 px-6 pt-6 pb-5 text-white text-center">
              <p className="text-indigo-200 text-xs font-medium uppercase tracking-wide mb-1">Visitante registrado</p>
              <h2 className="text-xl font-bold">{entranceTypePickerData.personName}</h2>
              {entranceTypePickerData.unitLabel && (
                <p className="text-indigo-200 text-sm mt-1">{entranceTypePickerData.unitLabel}</p>
              )}
            </div>
            <div className="px-5 py-5">
              <p className="text-center text-sm font-semibold text-gray-700 mb-3">Como está entrando?</p>
              {/* Countdown bar */}
              <div className="flex items-center gap-2 mb-4">
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 rounded-full transition-all duration-1000 ease-linear"
                    style={{ width: `${(pickerCountdown / PICKER_COUNTDOWN_SECONDS) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0 tabular-nums">{pickerCountdown}s</span>
              </div>
              <div className="flex flex-col gap-2">
                {visitorEntranceTypes.map((et, idx) => {
                  const IC = ENTRANCE_ICON_MAP_MODAL[et.icon] ?? _IconDoorOpen;
                  return (
                    <button
                      key={et.id}
                      type="button"
                      onClick={async () => {
                        const { personId, roleId } = entranceTypePickerData;
                        setEntranceTypePickerData(null);
                        if (et.action === 'auto') {
                          try {
                            await condoApi.manualCheckIn(customerId, personId, roleId);
                            toast.success('Entrada registrada!');
                          } catch {
                            toast.error('Erro ao registrar entrada');
                          }
                          pendingSyncRef.current?.(true);
                        } else {
                          // Manual: visitor stays in awaiting_entry — sync all devices (including entrada)
                          pendingSyncRef.current?.(false);
                        }
                        pendingSyncRef.current = null;
                        onSuccess?.();
                        onClose();
                      }}
                      className="group flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 transition-all text-left"
                    >
                      <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <IC size={18} className="text-indigo-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-800">{et.label}</p>
                        <p className="text-xs text-gray-400">{et.action === 'auto' ? 'Entrada automática' : 'Entrada manual'}</p>
                      </div>
                      <kbd className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 text-sm font-bold font-mono bg-white border-2 border-gray-200 rounded-lg text-gray-400 shadow-[0_2px_0_0_#d1d5db] group-hover:border-indigo-300 group-hover:text-indigo-400 select-none">
                        {idx + 1}
                      </kbd>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>,
        document.getElementById('modal-root') ?? document.body
      )}

    </>
  );
};

export default VisitorRegistrationModal;
